import React, { useState, useEffect } from "react";
import axios from "axios";
import "./App.css";
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { createConfigWithOptions } from './chatbot/config';
import ChatbotToggler from './chatbot/ChatbotToggler';

// Fallback configuration for unstable preview URLs
// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Set up axios defaults with retry logic
axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.timeout = 30000;

// Add retry interceptor for unstable connections
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Retry on network errors or 5xx errors
    if (
      (!error.response || error.response.status >= 500) &&
      !originalRequest._retry &&
      originalRequest.method !== 'get'
    ) {
      originalRequest._retry = true;
      
      // Wait 2 seconds before retry
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      try {
        return await axios(originalRequest);
      } catch (retryError) {
        console.error('Retry failed:', retryError);
        return Promise.reject(retryError);
      }
    }
    
    return Promise.reject(error);
  }
);

// IUT Fotso Victor - Departments, Cursus and Parcours Structure
const IUT_STRUCTURE = {
  "GENIE MECANIQUE ET PRODUCTIQUE": {
    
    "DUT": ["Maintenance Industrielle et Productique (MIP)", "Mecatronik Automobile (MKA)"],
    "LICENCE": [
      "Maintenance Industrielle et Productique (MIP)",
      "Mecatronik Automobile Recyclage et Construction (MKA-REC)",
      "Mecatronik Automobile Service Après Vente en Diagnostic Electronique Automobile (MKA-SAV)",
      "Soudure Industrielle (SIN)"
    ]
  },
  "GENIE INFORMATIQUE": {
    // "BTS": ["Informatique de Gestion (IG)", "Maintenance des Systèmes Informatiques (MSI)"],
    "DUT": ["Genie Logiciel (GL)", "Informatique et Services Réseaux (ISR)"],
    "LICENCE": [
      "Concepteur-Developpeur Réseaux et Internet (CDRI)",
      "Qualité, Sécurité Internet et Réseaux (QSIR)"
    ]
  },
  "GENIE DES TELECOMMUNICATIONS ET RESEAUX": {
    "BTS": ["Réseaux et Sécurité (RSE)"],
    "DUT": ["Genie Telecommunication et Reseaux (GTR)"],
    "LICENCE": ["Ingenierie des Reseaux et Telecommunication (IRT)"]
  },
  "GENIE ELECTRIQUE": {
    "BTS": ["Maintenance des Systèmes Electroniques (MSE)", "Electrotechnique (ELT)"],
    "DUT": ["Automatique Informatique Industriel (AII)", "Electrotechnique (ELT)"],
    "LICENCE": ["Ingénierie du Génie Electrique (IGE)"]
  },
  "ENERGIE RENOUVELABLE": {
    
    "DUT": [
      "Biomasse (BIO)",
      "Energie Eolienne (EEO)",
      "Energie HydroElectrique (EHY)",
      "Energie Solaire (ESO)",
      // "Ingénierie Biomédicale (IBM)",
      "Ingénierie des Technologies de l'Eau (ITE)"
    ],
    // "LICENCE": [
    //   "Ingénierie Biomédicale (IBM)",
    //   // "Ingénierie des Technologies de l'Eau (ITE)"
    // ],
    "MASTER": ["Innovation en Energies Renouvelables (IER)"]
  },
  "GENIE BIOMÉDICAL": {
    "DUT": ["Ingénierie Biomédicale (IBM)"],
    "LICENCE": ["Ingénierie Biomédicale (IBM)"]
  },
  "GENIE THERMIQUE ENERGIE ET ENVIRONNEMENT": {
    "BTS": ["Froid et Climatisation (FRC)"],
    "DUT": ["Genie Thermique, Energie et Environnement (GTEE)"],
    "LICENCE": ["Gestion et Maintenance des Installations Energetiques (GMIE)"]
  },
  "GENIE CIVIL": {
    "BTS": ["Bâtiment (BAT)", "Travaux Publics (TPU)"],
    "DUT": ["Genie Civil (GC)"],
    "LICENCE": ["Bâtiment (BAT)", "Travaux Publics (TPU)", "Géodésie Photogrammétrie SIG Topométrie (GEO)"],
    "MASTER": [
      "Constructions, Maintenance, Renovations des Ouvrages d'Arts et Maritimes (CMROAM)",
      "Constructions, Maintenance et Renovations des Batiments (CMRB)"
    ]
  },
  "TECHNIQUES DE COMMERCIALISATION, DE GESTION COMPTABLE ET FINANCIERE": {
    "BTS": [
      "Banque et Finance (BQF)",
      // "Collectivités Territoriales (CT)",
      "Comptabilite et Gestion des Entreprises (CGE)",
      "Gestion et Management Hôtelier (GMH)",
      "Marketing-Commerce-Vente (MCV)"
    ],
    "DUT": [
      "Gestion des Entreprises et des Administrations (GEA: GCF & GRH)"
    ],
    "LICENCE": [
      // "Marketing et Manager Opérationnel (MMO)",
      "Banque, Gestion des Actifs (BGA)",
      "Gestion Comptable et Financiere (GCF)",
      "Gestion et Management des Organisations (GMO)",
      "Marketing Numérique (MAN)"
    ],
    "MASTER": ["Ingénierie Technico Commercial (ITC)"]
  }
};

// ========================================================================
// REMPLACEZ VOTRE COMPOSANT App EXISTANT PAR CELUI-CI
// ========================================================================

const App = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
      }
    }
    setLoading(false);
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    // La redirection se fera automatiquement via le routeur
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-xl text-gray-600">Chargement...</div>
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <Routes>
          <Route path="/login" element={!user ? <AuthSection onLogin={setUser} /> : <Navigate to="/" />} />
          <Route path="/forgot-password" element={!user ? <ForgotPasswordPage /> : <Navigate to="/" />} />
          <Route path="/reset-password" element={!user ? <ResetPasswordPage /> : <Navigate to="/" />} />
          <Route path="/*" element={user ? <Dashboard user={user} logout={logout} /> : <Navigate to="/login" />} />
        </Routes>
      </div>
    </Router>
  );
};

// ========================================================================
// COPIEZ ET REMPLACEZ VOTRE FONCTION AuthSection EXISTANTE PAR CELLE-CI
// ========================================================================

// ========================================================================
// REMPLACEZ VOTRE COMPOSANT AuthSection PAR CETTE NOUVELLE VERSION
// ========================================================================
import ReCAPTCHA from "react-google-recaptcha"; // <-- 1. Importer le composant

const AuthSection = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [recaptchaToken, setRecaptchaToken] = useState(null); // <-- 2. Ajouter un état pour le token reCAPTCHA
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Vérifier que le CAPTCHA a été coché
    if (!recaptchaToken) {
      setError("Veuillez cocher la case 'Je ne suis pas un robot'.");
      setLoading(false);
      return;
    }

    try {
      // <-- 3. Ajouter le token reCAPTCHA aux données envoyées
      const loginData = { ...formData, recaptcha_token: recaptchaToken };
      
      const response = await axios.post(`${API}/auth/login`, loginData);
      
      localStorage.setItem('token', response.data.access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      onLogin(response.data.user);
      window.location.reload();
    } catch (error) {
      setError(error.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
        {/* --- AJOUTEZ CE BLOC COMPLET --- */}
  <div className="flex justify-center items-center space-x-4 mb-6">
    {/* Logo 1 */}
    <img 
    
     src="/logo2.png"
      alt="Logo 2" 
     
      className="h-16" // Ajustez la hauteur (h-12, h-16, h-20) selon la taille de votre logo
    />
    {/* Logo 2 (si vous en avez un deuxième) */}
    <img 
      src="/logo1.png"  
      alt="Logo 1" 
      className="h-16" // Gardez la même hauteur pour un alignement parfait
    />
  </div>
  {/* --- FIN DE L'AJOUT --- */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            IUT Fotso Victor
          </h1>
          <p className="text-gray-600">Plateforme de Gestion des Stages</p>
        </div>
        
        <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-gray-800">Connexion</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* --- 2. MODIFICATION DU BLOC MOT DE PASSE --- */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Mot de passe</label>
            <div className="relative mt-1">
              <input
                // Le type est maintenant dynamique
                type={isPasswordVisible ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                // Ajout d'un padding à droite pour ne pas que le texte aille sous l'icône
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 pr-10"
                required
              />
              {/* Le bouton pour afficher/masquer */}
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5">
                <button
                  type="button" // Important pour ne pas soumettre le formulaire
                  onClick={() => setIsPasswordVisible(!isPasswordVisible)} // Inverse l'état
                  className="text-gray-500 hover:text-gray-700"
                >
                  {/* On affiche une icône différente selon l'état */}
                  {isPasswordVisible ? (
                    // Icône "œil barré"
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.243 4.243L6.228 6.228" />
                    </svg>
                  ) : (
                    // Icône "œil"
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639l4.418-5.523A2.5 2.5 0 018.82 5.5h6.36a2.5 2.5 0 011.802 1.16l4.418 5.523a1.012 1.012 0 010 .639l-4.418 5.523a2.5 2.5 0 01-1.802 1.16H8.82a2.5 2.5 0 01-1.802-1.16L2.036 12.322z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>

          

          {/* <-- 4. Ajouter le composant ReCAPTCHA dans le formulaire --> */}
          <div className="flex justify-center">
            <ReCAPTCHA
              sitekey="6Lf4jt8rAAAAAJO9FBmG5OGkgqbdBLA8uSdfjgFA"
              onChange={(token) => setRecaptchaToken(token)}
            />
          </div>

          {error && (
            <div className="text-red-600 text-sm">{error}</div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loading ? 'Chargement...' : 'Se connecter'}
          </button>
        </form>
         {/* --- AJOUTEZ CE BLOC --- */}
        <div className="text-center mt-4">
          <a href="/forgot-password" className="text-sm text-blue-600 hover:underline">
            Mot de passe oublié ?
          </a>
        </div>
        {/* --- FIN DE L'AJOUT --- */}
      </div>
    </div>
  );
};

const Dashboard = ({ user, logout }) => {
 // Logique pour définir l'onglet initial
  const getInitialTab = () => {
    const items = getNavItems(user.role);
    // On prend le premier élément de la liste des onglets disponibles
    return items.length > 0 ? items[0].key : 'default'; 
  };

  const [currentTab, setCurrentTab] = useState(getInitialTab);
  const [academicYear, setAcademicYear] = useState(null);
  const [academicYearLoading, setAcademicYearLoading] = useState(true);
 

  useEffect(() => {
    fetchCurrentAcademicYear();
  }, []);

  const fetchCurrentAcademicYear = async () => {
    try {
      const response = await axios.get(`${API}/academic-year/current`);
      setAcademicYear(response.data);
    } catch (error) {
      console.error('Error fetching academic year:', error);
      setAcademicYear({ is_open: false });
    } finally {
      setAcademicYearLoading(false);
    }
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      student: 'Étudiant',
      professional_supervisor: 'Encadreur professionnel',
      employer: 'Employeur', // <-- NOUVEAU
      academic_supervisor: 'Encadreur académique',
      teacher_mission: 'Enseignant en mission',
      admin: 'Administrateur'
    };
    return roleNames[role] || role;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">IUT Fotso Victor</h1>
              <p className="text-sm text-gray-600">Plateforme de Gestion des Stages</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user.name}</p>
                <p className="text-xs text-gray-500">{getRoleDisplayName(user.role)}</p>
                {user.company_name && (
                  <p className="text-xs text-gray-500">{user.company_name}</p>
                )}
                {(user.role === 'academic_supervisor' || user.role === 'teacher_mission') && user.department && (
                  <p className="text-xs text-blue-600 font-medium">Dép. {user.department}</p>
                )}
                {user.role === 'student' && user.student_department && (
                  <div className="text-xs text-blue-600">
                    <p>{user.student_department}</p>
                    {user.student_cursus && <p>{user.student_cursus}</p>}
                    {user.student_parcours && <p className="font-medium">{user.student_parcours}</p>}
                  </div>
                )}
              </div>
              <button
                onClick={logout}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Academic Year Status Banner */}
      {!academicYearLoading && (
        <div className={`${
          academicYear.is_open 
            ? 'bg-green-50 border-green-200 text-green-800' 
            : 'bg-red-50 border-red-200 text-red-800'
        } border rounded-lg p-3 mx-4 mb-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <span className="font-medium">
                {academicYear.is_open ? '📅 Année Académique Ouverte:' : '⚠️ Aucune Année Académique Ouverte'}
              </span>
              {academicYear.is_open && (
                <span className="ml-2">
                  {academicYear.name} ({new Date(academicYear.start_date).toLocaleDateString()} - {new Date(academicYear.end_date).toLocaleDateString()})
                </span>
              )}
            </div>
            {!academicYear.is_open && user.role === 'super_admin' && (
              <button
                onClick={() => setCurrentTab('super-admin-academic-year')}
                className="text-sm bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded"
              >
                Ouvrir une année académique
              </button>
            )}
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="bg-blue-600 shadow-lg">
          <div className="flex flex-wrap gap-x-1 sm:gap-x-2 lg:gap-x-3 gap-y-1 py-1">
            {getNavItems(user).map((item) =>{
                const cleanLabel = item.label.replace(/^[🔍📋📤📄➕🏢📨👥🎓📝✅👤🚗📊📥🔒📅]\s*/, '');
                const words = cleanLabel.split(' ');
                const line1 = words.slice(0, Math.ceil(words.length / 2)).join(' ');
                const line2 = words.slice(Math.ceil(words.length / 2)).join(' ');
                
                return (
                  <button
                    key={item.key}
                    onClick={() => setCurrentTab(item.key)}
                    className={`
                      nav-button flex flex-col items-center justify-center px-2 py-2 rounded-lg font-medium text-xs
                      transition-all duration-200 ease-in-out transform hover:scale-105
                      min-w-fit nav-label h-16 w-20 sm:w-24
                      ${
                        currentTab === item.key
                          ? 'bg-white text-blue-600 shadow-md border-2 border-blue-200 ring-2 ring-blue-300'
                          : 'text-blue-100 hover:text-white hover:bg-blue-500 hover:shadow-md focus:ring-2 focus:ring-white focus:ring-opacity-50'
                      }
                    `}
                    aria-label={item.label}
                    title={item.label}
                    role="tab"
                    aria-selected={currentTab === item.key}
                  >
                    <span className="text-lg mb-1" aria-hidden="true">{item.icon}</span>
                    <div className="text-center leading-tight text-xs">
                      <div className="block">{line1}</div>
                      {line2 && <div className="block">{line2}</div>}
                    </div>
                  </button>
                );
              })}
            
          </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="transition-all duration-300 ease-in-out">
            {renderTabContent(currentTab, user)}
          </div>
        </div>
      </main>
      {/* Chatbot */}
<div className="fixed bottom-8 right-8 z-50">
<ChatbotToggler user={user} />
</div>
    </div>
  );
};

// ===============================================
// REMPLACEZ VOTRE FONCTION getNavItems PAR CELLE-CI
// ===============================================

const getNavItems = (user) => {
  const commonItems = [];
  const role = user.role;

  // On ajoute la "Vue d'Ensemble" pour tous les rôles, SAUF pour l'employeur
  if (role !== 'professional_supervisor' && role !== 'student' && role !== 'employer'&& role !== 'teacher_mission') {
    commonItems.push({ key: 'overview', label: '🏠 Vue d\'Ensemble', icon: '🏠' });
  }

  const roleSpecificItems = {
    student: [
      { key: 'search-internships', label: '🔍 Rechercher des Stages', icon: '🔍' },
      { key: 'my-applications', label: '📋 Mes Candidatures', icon: '📋' },
      { key: 'external-internship', label: '📤 Soumettre un Stage Externe', icon: '📤' },
      { key: 'my-documents', label: '📄 Mes Documents', icon: '📄' },
      { key: 'student-messaging', label: '💬 Messagerie', icon: '💬' }
    ],
    professional_supervisor: [
      { key: 'post-internship', label: '➕ Publier un Stage', icon: '➕' },
      { key: 'my-internships', label: '🏢 Mes Offres de Stage', icon: '🏢' },
      { key: 'applications', label: '📨 Candidatures Reçues', icon: '📨' },
      { key: 'supervisor-messaging', label: '💬 Messagerie', icon: '💬' },
      { key: 'evaluate-students', label: '📝 Évaluation Stagiaires', icon: '📝' } // <-- LIGNE AJOUTÉE
    ],
    employer: [ // <-- NOUVEAU
       { key: 'post-internship', label: '➕ Publier un Stage', icon: '➕' },
       { key: 'my-internships', label: '🏢 Mes Offres de Stage', icon: '🏢' },
       { key: 'applications', label: '📨 Candidatures Reçues', icon: '📨' },
       { key: 'supervisor-messaging', label: '💬 Messagerie', icon: '💬' }
    ],
    academic_supervisor: [
      { key: 'department-students', label: '👥 Étudiants de Mon Département', icon: '👥' },
      { key: 'department-internships', label: '🎓 Stages du Département', icon: '🎓' },
      { key: 'department-applications', label: '📝 Candidatures du Département', icon: '📝' },
      { key: 'external-internships-review', label: '✅ Stages Externes à Valider', icon: '✅' },
      { key: 'create-student', label: '👤 Créer un Utilisateur', icon: '👤' },
      { key: 'placement-validation', label: '🎯 Validation des Placements', icon: '🎯' },
      { key: 'spontaneous-application', label: '✨ Candidatures Spontanées', icon: '✨' },
      { key: 'jury-list', label: '📋 Attribution des Jurys', icon: '📋' }, 
      { key: 'supervisor-messaging', label: '💬 Messagerie', icon: '💬' },
      { key: 'admin-documents', label: '📁 Documents Étudiants', icon: '📁' },
      { key: 'academic-evaluation', label: '📝 Évaluation Stagiaires', icon: '📝' }
    ],
    teacher_mission: [
      { key: 'department-students', label: '🎓 Étudiants', icon: '🎓' },
      // { key: 'department-internships', label: '🎓 Stages du Département', icon: '🎓' },
      { key: 'prospecting', label: '🔍 Prospection Entreprises', icon: '🔍' },
      { key: 'visits', label: '🚗 Visites Terrain', icon: '🚗' },
      { key: 'supervisor-messaging', label: '💬 Messagerie', icon: '💬' },
      // { key: 'admin-documents', label: '📁 Documents Étudiants', icon: '📁' }
    ],
    admin: [
      { key: 'admin-users', label: '👥 Gestion des Utilisateurs', icon: '👥' },
      { key: 'admin-internships', label: '🎓 Offres de stages', icon: '🎓' },
      { key: 'admin-external-internships', label: '📤 Stages Externes', icon: '📤' },
      { key: 'admin-applications', label: '📋 Toutes les Candidatures', icon: '📋' },
      { key: 'admin-reports', label: '📊 Rapports & Statistiques', icon: '📊' },
      { key: 'admin-export', label: '📥 Export des Données', icon: '📥' },
      { key: 'admin-create-users', label: '➕ Créer des Utilisateurs', icon: '➕' },
      { key: 'admin-messaging', label: '💬 Messagerie', icon: '💬' },
      { key: 'prospecting', label: '🔍 Prospection', icon: '🔍' },
      { key: 'admin-placements', label: '🎯 Mise en Stage', icon: '🎯' },
      { key: 'admin-assignments', label: '👨‍🏫 Affectation Encadreurs Académiques', icon: '👨‍🏫' },
      { key: 'admin-pro-assignment', label: '🧑‍💼 Affectation Encadreur Pro', icon: '🧑‍💼' },
      { key: 'teacher-list', label: '👨‍⚖️ Gestion des Enseignants', icon: '👨‍⚖️' },
      { key: 'jury-list', label: '📋 Attribution des Jurys', icon: '📋' },       // <-- AJOUTEZ CETTE LIGNE
      { key: 'supervision-management', label: '🧑‍🏫 Encadrements', icon: '🧑‍🏫' },
      { key: 'super-admin-academic-year', label: '📅 Gestion Année Académique', icon: '📅' },
      { key: 'admin-documents', label: '📁 Documents Étudiants', icon: '📁' }
    ],
    super_admin: [
      { key: 'admin-users', label: '👥 Gestion des Utilisateurs', icon: '👥' },
      { key: 'admin-internships', label: '🎓 Gestion des Stages', icon: '🎓' },
      { key: 'rights-management', label: '🔒 Gestion des Droits', icon: '🔒' }, 
      { key: 'admin-external-internships', label: '📤 Stages Externes', icon: '📤' },
      { key: 'admin-applications', label: '📋 Toutes les Candidatures', icon: '📋' },
      { key: 'admin-reports', label: '📊 Rapports & Statistiques', icon: '📊' },
      { key: 'admin-export', label: '📥 Export des Données', icon: '📥' },
      { key: 'admin-create-users', label: '➕ Créer des Utilisateurs', icon: '➕' },
      { key: 'super-admin-roles', label: '🔒 Gérer Visibilité des Rôles', icon: '🔒' },
      { key: 'super-admin-academic-year', label: '📅 Gestion Année Académique', icon: '📅' },
      { key: 'admin-messaging', label: '💬 Messagerie', icon: '💬' },
      { key: 'admin-placements', label: '🎯 Mise en Stage', icon: '🎯' },
      { key: 'admin-assignments', label: '👨‍🏫 Affectation Encadreurs Académiques', icon: '👨‍🏫' },
      { key: 'admin-pro-assignment', label: '🧑‍💼 Affectation Encadreur Pro', icon: '🧑‍💼' },
      { key: 'teacher-list', label: '👨‍⚖️ Gestion des Enseignants', icon: '👨‍⚖️' }, // <-- AJOUTEZ CETTE LIGNE
      { key: 'jury-list', label: '📋 Attribution des Jurys', icon: '📋' },     // <-- AJOUTEZ CETTE LIGNE
      { key: 'supervision-management', label: '🧑‍🏫 Encadrements', icon: '🧑‍🏫' },
      { key: 'admin-documents', label: '📁 Documents Étudiants', icon: '📁' }
    ]
  };

  let items = [...commonItems, ...(roleSpecificItems[role] || [])];

  // 3. Filtrage par permissions (si définies)
  if (user.permissions) {
    items = items.filter(item => {
        // Si la clé est dans permissions et vaut 'false', on masque
        return user.permissions[item.key] !== false;
    });
  }

  return items;
};

const renderTabContent = (tab, user) => {
  switch (tab) {
    case 'overview':
      return <OverviewTab user={user} />;
    case 'search-internships':
      return <SearchInternshipsTab />;
    case 'my-applications':
      return <MyApplicationsTab />;
    case 'spontaneous-application':
     return <SpontaneousApplicationTab user={user} />;
    case 'post-internship':
      return <PostInternshipTab  />;  
    case 'supervision-management':
      return <SupervisionTab />;
    case 'evaluate-students': // <-- AJOUTER CE CASE
     return <ProfessionalSupervisorEvaluationTab />; // <-- AJOUTER CETTE LIGNE
    case 'my-internships':
      return <MyInternshipsTab />;
    // case 'jury-management':
    //   return <JuryManagementTab />;
    case 'teacher-list':
      return <TeacherListTab />;
    case 'jury-list':
      return <JuryListTab />;
    case 'applications':
      return <ApplicationsTab />;
    case 'admin-users':
      return <AdminUsersTab />;
    case 'rights-management':
        return <RightsManagementTab />;
    case 'admin-pro-assignment':
      return <AdminProAssignmentTab />;
    case 'admin-internships':
      return <AdminInternshipsTab />;
    case 'admin-applications':
      return <AdminApplicationsTab />;
    case 'admin-reports':
      return <AdminReportsTab />;
    case 'prospecting':
      return <ProspectionTab />;
    case 'admin-export':
      return <AdminExportTab />;
    case 'admin-create-users':
      return <AdminCreateUsersTab />;
    case 'department-students':
      return <DepartmentStudentsTab user={user} />;
    case 'department-internships':
      return <DepartmentInternshipsTab user={user} />;
    case 'department-applications':
      return <DepartmentApplicationsTab user={user} />;
    case 'external-internship':
      return <ExternalInternshipTab />;
    case 'my-documents':
      return <MyDocumentsTab />;
    case 'external-internships-review':
      return <ExternalInternshipsReviewTab />;
    case 'create-student':
      return <CreateStudentTab user={user} />;
    case 'super-admin-create-users':
      return <SuperAdminCreateUsersTab />;
    case 'super-admin-roles':
      return <SuperAdminRolesTab />;
    case 'super-admin-academic-year':
      return <SuperAdminAcademicYearTab />;
    case 'admin-messaging':
      return <AdminMessagingTab />;
    case 'student-messaging':
      return <StudentMessagingTab />;
    case 'supervisor-messaging':
      return <SupervisorMessagingTab />;
    case 'admin-placements':
      return <AdminPlacementsTab />;
    case 'placement-validation':
      return <PlacementValidationTab />;
    case 'admin-assignments':
      return <AdminAssignmentsTab />;
    case 'academic-evaluation':
      return <AcademicSupervisorEvaluationTab />;  
    case 'admin-external-internships': 
      return <AdminExternalInternshipsTab />;
    case 'admin-documents':
      return <AdminDocumentsTab />;
    default:
      return (
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900">
            Fonctionnalité en cours de développement
          </h3>
          <p className="text-gray-600 mt-2">
            Cette section sera bientôt disponible.
          </p>
        </div>
      );
  }
};

// Ajoutez cette liste en haut de App.js, après vos imports
const ALL_AVAILABLE_TABS = [
    { key: 'overview', label: '🏠 Vue d\'Ensemble' },
    { key: 'search-internships', label: '🔍 Rechercher des Stages' },
    { key: 'my-applications', label: '📋 Mes Candidatures' },
    { key: 'external-internship', label: '📤 Soumettre un Stage Externe' },
    { key: 'my-documents', label: '📄 Mes Documents' },
    { key: 'post-internship', label: '➕ Publier un Stage' },
    { key: 'my-internships', label: '🏢 Mes Offres de Stage' },
    { key: 'applications', label: '📨 Candidatures Reçues' },
    { key: 'evaluate-students', label: '📝 Évaluation Stagiaires' },
    { key: 'department-students', label: '👥 Étudiants Département' },
    { key: 'department-internships', label: '🎓 Stages Département' },
    { key: 'department-applications', label: '📝 Candidatures Département' },
    { key: 'external-internships-review', label: '✅ Stages Externes à Valider' },
    { key: 'create-student', label: '👤 Créer un Utilisateur' },
    { key: 'placement-validation', label: '🎯 Validation des Placements' },
    { key: 'spontaneous-application', label: '✨ Candidatures Spontanées' },
    { key: 'academic-evaluation', label: '📝 Évaluation Stagiaires' },
    { key: 'admin-users', label: '👥 Gestion des Utilisateurs' },
    { key: 'admin-internships', label: '🎓 Offres de stages' },
    { key: 'admin-external-internships', label: '📤 Stages Externes' },
    { key: 'admin-applications', label: '📋 Toutes les Candidatures' },
    { key: 'admin-reports', label: '📊 Rapports & Statistiques' },
    { key: 'admin-export', label: '📥 Export des Données' },
    { key: 'admin-create-users', label: '➕ Créer des Utilisateurs' },
    { key: 'admin-messaging', label: '💬 Messagerie' },
    { key: 'prospecting', label: '🔍 Prospection' },
    { key: 'admin-placements', label: '🎯 Mise en Stage' },
    { key: 'admin-assignments', label: '👨‍🏫 Affectation Encadreurs Académiques' },
    { key: 'admin-pro-assignment', label: '🧑‍💼 Affectation Encadreur Pro' },
    { key: 'teacher-list', label: '👨‍⚖️ Gestion des Enseignants' },
    { key: 'jury-list', label: '📋 Attribution des Jurys' },
    { key: 'supervision-management', label: '🧑‍🏫 Encadrements' },
    { key: 'super-admin-academic-year', label: '📅 Gestion Année Académique' },
    { key: 'admin-documents', label: '📁 Documents Étudiants' },
    { key: 'super-admin-roles', label: '🔒 Gérer Visibilité des Rôles' }
];
const RightsManagementTab = () => {
    const [users, setUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [filters, setFilters] = useState({ role: '', search: '' });

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const res = await axios.get(`${API}/admin/users/all`);
                setUsers(res.data);
            } catch (err) { console.error(err); }
        };
        fetchUsers();
    }, []);

    const filteredUsers = users.filter(u => 
        (filters.role === '' || u.role === filters.role) &&
        (u.name.toLowerCase().includes(filters.search.toLowerCase()) || 
         u.email.toLowerCase().includes(filters.search.toLowerCase()))
    );

    const handleSelectAll = (e) => {
        setSelectedUsers(e.target.checked ? filteredUsers.map(u => u.id) : []);
    };

    const handleToggleUser = (id) => {
        setSelectedUsers(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };

    const savePermissions = async (permissions) => {
    try {
        // Forcez l'URL complète pour être certain
        await axios.put(`${API}/admin/users/permissions`, { 
            user_ids: selectedUsers, 
            permissions: permissions 
        });
        
        alert("Droits mis à jour !");
        setIsModalOpen(false);
        setSelectedUsers([]);
        window.location.reload(); 
    } catch (e) { 
        alert("Erreur : " + (e.response?.data?.detail || "Erreur de sauvegarde.")); 
    }
};

    return (
        <div className="p-6">
            <h2 className="text-2xl font-bold mb-6">Gestion des Droits d'Accès</h2>
            
            <div className="flex flex-wrap gap-4 mb-6 bg-white p-4 rounded shadow">
                <input type="text" placeholder="Rechercher par nom..." className="p-2 border rounded" onChange={e => setFilters({...filters, search: e.target.value})} />
               <select onChange={e => setFilters({...filters, role: e.target.value})} className="p-2 border rounded">
    <option value="">Tous les rôles</option>
    <option value="student">Étudiant</option>
    <option value="academic_supervisor">Encadreur Académique</option>
    <option value="professional_supervisor">Encadreur Professionnel</option>
    <option value="employer">Employeur</option>
    <option value="teacher_mission">Enseignant en Mission</option>
    <option value="admin">Administrateur</option>
    <option value="super_admin">Super Administrateur</option>
</select>
                {selectedUsers.length > 0 && (
                    <button onClick={() => setIsModalOpen(true)} className="bg-purple-600 text-white px-4 py-2 rounded font-medium">
                        Gérer droits ({selectedUsers.length})
                    </button>
                )}
            </div>

            <table className="min-w-full bg-white shadow rounded overflow-hidden">
                <thead className="bg-gray-100">
                    <tr>
                        <th className="p-3"><input type="checkbox" onChange={handleSelectAll} checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0} /></th>
                        <th className="p-3 text-left">Nom</th>
                        <th className="p-3 text-left">Rôle</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredUsers.map(u => (
                        <tr key={u.id} className="border-t hover:bg-gray-50">
                            <td className="p-3"><input type="checkbox" checked={selectedUsers.includes(u.id)} onChange={() => handleToggleUser(u.id)} /></td>
                            <td className="p-3">{u.name}</td>
                            <td className="p-3">{u.role}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {isModalOpen && (
                <RightsModal 
                    onClose={() => setIsModalOpen(false)} 
                    onSave={savePermissions} 
                    allTabs={ALL_AVAILABLE_TABS} 
                    initialPermissions={users.find(u => u.id === selectedUsers[0])?.permissions || {}} 
                />
            )}
        </div>
    );
};
const RightsModal = ({ onClose, onSave, allTabs, initialPermissions }) => {
    // Initialiser avec les permissions existantes OU true par défaut
    const [permissions, setPermissions] = useState(() => {
        const initialState = {};
        allTabs.forEach(tab => {
            initialState[tab.key] = initialPermissions[tab.key] !== false; // true par défaut
        });
        return initialState;
    });

    const togglePermission = (key) => {
        setPermissions(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const selectAll = () => {
        const allTrue = allTabs.reduce((acc, tab) => ({ ...acc, [tab.key]: true }), {});
        setPermissions(allTrue);
    };

    const deselectAll = () => {
        const allFalse = allTabs.reduce((acc, tab) => ({ ...acc, [tab.key]: false }), {});
        setPermissions(allFalse);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-lg">
                <h3 className="font-bold text-lg mb-4">Configurer les accès</h3>
                <div className="flex gap-2 mb-4">
                    <button type="button" onClick={selectAll} className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded hover:bg-blue-200">
                        Tout cocher
                    </button>
                    <button type="button" onClick={deselectAll} className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded hover:bg-gray-200">
                        Tout décocher
                    </button>
                </div>
                <div className="max-h-96 overflow-y-auto space-y-2 mb-6 border-t pt-2">
                    {allTabs.map(tab => (
                        <label key={tab.key} className="flex items-center space-x-3 p-2 hover:bg-gray-100 rounded cursor-pointer">
                            <input 
                                type="checkbox" 
                                checked={permissions[tab.key] !== false} 
                                onChange={() => togglePermission(tab.key)}
                                className="h-4 w-4 text-blue-600 rounded"
                            />
                            <span className="text-sm">{tab.label}</span>
                        </label>
                    ))}
                </div>
                <div className="flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded">Annuler</button>
                    <button onClick={() => onSave(permissions)} className="px-4 py-2 bg-blue-600 text-white rounded">Enregistrer</button>
                </div>
            </div>
        </div>
    );
};
const ProspectionTab = () => {
  const [prospects, setProspects] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    company_name: '',
    contact_email: '',
    contact_phone: '',
    domains: []
  });

  const [editingId, setEditingId] = useState(null); // Pour savoir quel prospect on modifie

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [prospectsRes, domainsRes] = await Promise.all([
        axios.get(`${API}/prospects`),
        axios.get(`${API}/structure/domains`)
      ]);
      setProspects(prospectsRes.data || []);
      setDomains(domainsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDomainChange = (domain) => {
    setFormData(prev => {
      const newDomains = prev.domains.includes(domain)
        ? prev.domains.filter(d => d !== domain)
        : [...prev.domains, domain];
      return { ...prev, domains: newDomains };
    });
  };
  const handleSubmit = async (e) => {
  e.preventDefault();
  if (formData.domains.length === 0) {
    alert("Veuillez sélectionner au moins un domaine d'activité.");
    return;
  }

  const url = editingId 
    ? `${API}/prospects/${editingId}` // URL pour la mise à jour
    : `${API}/prospects`;            // URL pour la création

  const method = editingId ? 'put' : 'post';

  try {
    await axios[method](url, formData);
    alert(editingId ? "Prospection modifiée avec succès !" : "Prospection enregistrée avec succès !");
    cancelEdit(); // Réinitialise le formulaire et le mode édition
    fetchData(); // Rafraîchit la liste
  } catch (error) {
    alert(error.response?.data?.detail || "Erreur lors de l'enregistrement.");
  }
};

  

  const handleEdit = (prospect) => {
  // On remplit le formulaire avec les données du prospect à modifier
  setFormData({
    company_name: prospect.company_name,
    contact_email: prospect.contact_email || '',
    contact_phone: prospect.contact_phone || '',
    domains: prospect.domains || []
  });
  // On stocke l'ID du prospect en cours de modification
  setEditingId(prospect.id);
  // On fait défiler la page vers le haut pour voir le formulaire
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const handleDelete = async (prospectId) => {
  if (window.confirm("Êtes-vous sûr de vouloir supprimer cette prospection ?")) {
    try {
      await axios.delete(`${API}/prospects/${prospectId}`);
      alert("Prospection supprimée avec succès !");
      fetchData(); // On rafraîchit la liste
    } catch (error) {
      alert("Erreur lors de la suppression.");
    }
  }
};

const cancelEdit = () => {
  // On vide le formulaire et on annule le mode édition
  setFormData({ company_name: '', contact_email: '', contact_phone: '', domains: [] });
  setEditingId(null);
};

  const filteredProspects = prospects.filter(p => {
    const search = searchTerm.toLowerCase();
    if (!search) return true;
    return (
      p.company_name.toLowerCase().includes(search) ||
      (p.contact_email && p.contact_email.toLowerCase().includes(search)) ||
      (p.contact_phone && p.contact_phone.includes(search))
    );
  });

  if(loading) { return <div>Chargement...</div>; }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Prospection Entreprises</h2>

      {/* Formulaire de création */}
      <div className="bg-white p-6 rounded-lg shadow mb-8">
        <h3 className="text-lg font-semibold mb-4">
          {editingId ? '✏️ Modifier la prospection' : '➕ Enregistrer une nouvelle entreprise'}
        </h3>
 <form onSubmit={handleSubmit} className="space-y-6">
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Nom de l'entreprise *</label>
      <input type="text" name="company_name" value={formData.company_name} onChange={handleFormChange} required className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"/>
    </div>
  </div>
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Email contact (Optionnel)</label>
      <input type="email" name="contact_email" value={formData.contact_email} onChange={handleFormChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"/>
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone contact *</label>
      <input type="tel" name="contact_phone" value={formData.contact_phone} onChange={handleFormChange} required className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"/>
    </div>
  </div>
  <div>
    <label className="block text-sm font-medium text-gray-700">Domaines d'Activité *</label>
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-2 p-4 border rounded-md bg-gray-50">
      {domains.map(domain => (
        <label key={domain} className="flex items-center space-x-3 p-2 rounded-md hover:bg-gray-100 cursor-pointer">
          <input 
            type="checkbox"
            checked={formData.domains.includes(domain)}
            onChange={() => handleDomainChange(domain)}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">{domain}</span>
        </label>
      ))}
    </div>
  </div>
 <div className="flex justify-end pt-4 space-x-4">
  {editingId && (
    <button type="button" onClick={cancelEdit} className="py-2 px-6 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
      Annuler
    </button>
  )}
  <button type="submit" className="py-2 px-6 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700">
    {editingId ? 'Sauvegarder les modifications' : 'Enregistrer'}
  </button>
</div>
</form>
      </div>

      {/* Liste des prospections */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">📋 Prospections Enregistrées ({filteredProspects.length})</h3>
        <input 
          type="text"
          placeholder="Rechercher par nom d'entreprise, email, téléphone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full ... mb-4"
        />
<table className="min-w-full divide-y divide-gray-200">
  <thead className="bg-gray-50">
    <tr>
      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entreprise</th>
      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Domaines</th>
      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ajouté par / le</th>
      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
    </tr>
  </thead>
  <tbody className="bg-white divide-y divide-gray-200">
    {filteredProspects.map(p => (
      <tr key={p.id} className="hover:bg-gray-50">
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="text-sm font-medium text-gray-900">{p.company_name}</div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="text-sm text-gray-900">{p.contact_email || '-'}</div>
          <div className="text-sm text-gray-500">{p.contact_phone || '-'}</div>
        </td>
        <td className="px-6 py-4">
          <div className="flex flex-wrap gap-1">
            {p.domains.map(domain => (
              <span key={domain} className="px-2 py-1 text-xs leading-4 font-semibold rounded-full bg-blue-100 text-blue-800">
                {domain}
              </span>
            ))}
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="text-sm text-gray-900">{p.created_by_name}</div>
          <div className="text-sm text-gray-500">{new Date(p.created_at).toLocaleDateString()}</div>
        </td>
               
        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
  <button onClick={() => handleEdit(p)} className="text-blue-600 hover:text-blue-900 mr-4">
    Modifier
  </button>
  <button onClick={() => handleDelete(p.id)} className="text-red-600 hover:text-red-900">
    Supprimer
  </button>
  </td>

      </tr>
    ))}
  </tbody>
</table>
      </div>
    </div>
  );
};
const OverviewTab = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [dynamicStats, setDynamicStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    department: '',
    cursus: '',
    parcours: '',
    period: 'all'
  });

  // États pour les listes de filtres
  const [departments, setDepartments] = useState([]);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRY = 3;
  const [cursus, setCursus] = useState([]);
  const [parcours, setParcours] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchDepartments();
    fetchAllCursus();
    fetchAllParcours();
  }, []);

  useEffect(() => {
    fetchFilteredStats();
  }, [filters.department, filters.cursus, filters.parcours]);

  // Fetch filtered stats when filters change
  const fetchFilteredStats = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.department) params.append('department', filters.department);
      if (filters.cursus) params.append('cursus', filters.cursus);
      if (filters.parcours) params.append('parcours', filters.parcours);
      
    
      const response = await axios.get(`${API}/dashboard/filtered-stats?${params}`);
      
      setDynamicStats(response.data);
    } catch (error) {
      console.error('Error fetching filtered stats:', error);
    }
  };

  // Fetch departments on component mount
  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/structure/departments`);
      setDepartments(response.data.departments);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  // Fetch all cursus on component mount
  const fetchAllCursus = async () => {
    try {
      const response = await axios.get(`${API}/structure/cursus`);
      setCursus(response.data.cursus);
    } catch (error) {
      console.error('Error fetching all cursus:', error);
    }
  };

  // Fetch all parcours on component mount
  const fetchAllParcours = async () => {
    try {
      const response = await axios.get(`${API}/structure/parcours`);
      setParcours(response.data.parcours);
    } catch (error) {
      console.error('Error fetching all parcours:', error);
    }
  };

  // Fetch cursus when department changes
  const fetchCursus = async (department) => {
    try {
      const response = await axios.get(`${API}/structure/cursus`, {
        params: { department }
      });
      setCursus(response.data.cursus);
    } catch (error) {
      console.error('Error fetching cursus:', error);
    }
  };

  // Fetch parcours when department or cursus changes
  const fetchParcours = async (department, cursus) => {
    try {
      const response = await axios.get(`${API}/structure/parcours`, {
        params: { department, cursus }
      });
      setParcours(response.data.parcours);
    } catch (error) {
      console.error('Error fetching parcours:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      
      // Logique de cascade pour les filtres
      if (key === 'department') {
        newFilters.cursus = '';
        newFilters.parcours = '';
        setCursus([]);
        setParcours([]);
        
        if (value) {
          fetchCursus(value);
        } else {
          // Si pas de département sélectionné, charger tous les cursus
          fetchAllCursus();
        }
      } else if (key === 'cursus') {
        newFilters.parcours = '';
        setParcours([]);
        
        if (value && newFilters.department) {
          fetchParcours(newFilters.department, value);
        } else if (newFilters.department) {
          // Si cursus vide mais département sélectionné, charger tous les parcours du département
          fetchParcours(newFilters.department, '');
        } else {
          // Si pas de département, charger tous les parcours
          fetchAllParcours();
        }
      }
      
      return newFilters;
    });
  };

  const clearFilters = () => {
    setFilters({
      department: '',
      cursus: '',
      parcours: '',
      period: 'all'
    });
  };

  // Fonction pour obtenir les statistiques filtrées
  const getFilteredStats = () => {
    if (!stats) return null;

    

    // Utiliser les statistiques dynamiques si disponibles, sinon utiliser les données de base
    const displayStats = {
      ...stats,
      // Utiliser les valeurs dynamiques pour les cartes principales
     filtered_total_students: dynamicStats?.filtered_total_students ?? stats.total_students ?? 0,
     filtered_placed_students: dynamicStats?.filtered_placed_students ?? stats.placed_students_total ?? 0,
     filtered_pending_students: dynamicStats?.filtered_pending_students ?? stats.pending_students_total ?? 0,
      // Utiliser les valeurs dynamiques pour les statistiques d'offres
  total_internships: dynamicStats?.total_internships ?? stats.total_internships ?? 0,
  active_internships: dynamicStats?.active_internships ?? stats.available_internships ?? 0, // Note: active_internships
  external_internships: dynamicStats?.external_internships ?? stats.external_internships ?? 0,
  external_approved: dynamicStats?.external_approved ?? stats.external_approved ?? 0
    };

    
    return displayStats;
  };

  if (loading) {
    return <div className="text-center py-8">Chargement des statistiques...</div>;
  }

  const filteredStats = getFilteredStats();

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Vue d'ensemble</h2>
      
      {/* Filtres avec structure officielle IUT */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">🔍 Filtres</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Département</label>
            <select
              value={filters.department}
              onChange={(e) => updateFilter('department', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Tous les départements</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cursus</label>
            <select
              value={filters.cursus}
              onChange={(e) => updateFilter('cursus', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              disabled={!filters.department}
            >
              <option value="">Tous les cursus</option>
              {cursus.map(cursusItem => (
                <option key={cursusItem} value={cursusItem}>{cursusItem}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Parcours</label>
            <select
              value={filters.parcours}
              onChange={(e) => updateFilter('parcours', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              disabled={!filters.cursus}
            >
              <option value="">Tous les parcours</option>
              {parcours.map(parcoursItem => (
                <option key={parcoursItem} value={parcoursItem}>{parcoursItem}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Période</label>
            <select
              value={filters.period}
              onChange={(e) => updateFilter('period', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Toutes les périodes</option>
              <option value="current">Année courante</option>
              <option value="month">Ce mois</option>
              <option value="week">Cette semaine</option>
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <button
            onClick={clearFilters}
            className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-md text-sm"
          >
            Effacer les filtres
          </button>
        </div>
      </div>

      {/* Cartes principales - 3 cartes alignées sur une seule ligne */}
      <div className="grid grid-cols-3 gap-6 mb-8">
        {/* Étudiants inscrits */}
        <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-blue-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl font-bold">👥</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Étudiants inscrits</p>
              <p className="text-2xl font-semibold text-gray-900">
                {filteredStats?.filtered_total_students || 0}
              </p>
            </div>
          </div>
        </div>

        {/* En attente */}
        <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-yellow-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl font-bold">⏳</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">En attente</p>
              <p className="text-2xl font-semibold text-gray-900">
                {filteredStats?.filtered_pending_students || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Étudiants placés */}
        <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-green-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl font-bold">✅</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Étudiants placés</p>
              <p className="text-2xl font-semibold text-gray-900">
                {filteredStats?.filtered_placed_students || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Statistiques des Offres avec cartes colorées */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">📈 Statistiques des Offres</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Offres totales */}
          <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <span className="text-white text-sm font-bold">📊</span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-blue-600">Offres Totales</p>
                <p className="text-xl font-bold text-blue-900">{filteredStats?.total_internships || 0}</p>
              </div>
            </div>
          </div>

          {/* Offres actives */}
          <div className="bg-green-50 rounded-lg p-4 border-l-4 border-green-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                  <span className="text-white text-sm font-bold">✅</span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-600">Offres Actives</p>
                <p className="text-xl font-bold text-green-900">{filteredStats?.active_internships || 0}</p>
              </div>
            </div>
          </div>

          {/* Stages externes */}
          <div className="bg-purple-50 rounded-lg p-4 border-l-4 border-purple-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                  <span className="text-white text-sm font-bold">🎯</span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-purple-600">Stages Externes</p>
                <p className="text-xl font-bold text-purple-900">{filteredStats?.external_internships || 0}</p>
              </div>
            </div>
          </div>

          {/* Externes approuvés */}
          <div className="bg-indigo-50 rounded-lg p-4 border-l-4 border-indigo-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center">
                  <span className="text-white text-sm font-bold">🏆</span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-indigo-600">Externes Approuvés</p>
                <p className="text-xl font-bold text-indigo-900">{filteredStats?.external_approved || 0}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Message de bienvenue */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Bienvenue, {user.name}!
        </h3>
        <p className="text-gray-600">
          Vue d'ensemble optimisée de la plateforme IUT Fotso Victor. Utilisez les filtres pour analyser les données selon vos besoins.
        </p>
      </div>
    </div>
  );
};

const renderStatsCards = (stats, role) => {
  const getStatsConfig = () => {
    switch (role) {
      case 'student':
        return [
          { label: 'Mes candidatures', value: stats.total_applications, color: 'bg-blue-500' },
          { label: 'En attente', value: stats.pending_applications, color: 'bg-yellow-500' },
          { label: 'Acceptées', value: stats.accepted_applications, color: 'bg-green-500' },
          { label: 'Stages disponibles', value: stats.available_internships, color: 'bg-purple-500' }
        ];
      case 'professional_supervisor':
        return [
          { label: 'Mes offres', value: stats.total_internships, color: 'bg-blue-500' },
          { label: 'Candidatures reçues', value: stats.total_applications, color: 'bg-green-500' },
          { label: 'Offres actives', value: stats.active_internships, color: 'bg-purple-500' }
        ];
      case 'academic_supervisor':
      case 'teacher_mission':
        return [
          { label: 'Étudiants département', value: stats.total_students, color: 'bg-blue-500' },
          { label: 'Candidatures département', value: stats.total_applications, color: 'bg-green-500' },
          { label: 'En attente', value: stats.pending_applications, color: 'bg-yellow-500' },
          { label: 'Étudiants placés', value: stats.students_with_internships, color: 'bg-purple-500' }
        ];
      case 'admin':
        return [
          { label: 'Utilisateurs', value: stats.total_users, color: 'bg-blue-500' },
          { label: 'Étudiants', value: stats.total_students, color: 'bg-green-500' },
          { label: 'Entreprises', value: stats.total_companies, color: 'bg-purple-500' },
          { label: 'Stages', value: stats.total_internships, color: 'bg-yellow-500' }
        ];
      default:
        return [];
    }
  };

  return getStatsConfig().map((stat, index) => (
    <div key={index} className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center">
        <div className={`${stat.color} rounded-md p-3`}>
          <div className="text-white text-2xl font-bold">{stat.value}</div>
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-500">{stat.label}</p>
        </div>
      </div>
    </div>
  ));
};

const getWelcomeMessage = (role) => {
  const messages = {
    student: 'Utilisez cette plateforme pour rechercher et postuler aux stages qui correspondent à votre profil. Suivez vos candidatures et gérez votre parcours de stage.',
    professional_supervisor: 'Publiez vos offres de stage et trouvez les meilleurs candidats. Gérez les candidatures et accompagnez vos stagiaires.',
    academic_supervisor: 'Supervisez vos étudiants, validez leurs choix de stage et assurez le suivi pédagogique de leur parcours dans votre département.',
    teacher_mission: 'Prospectez de nouvelles entreprises partenaires pour votre département et effectuez le suivi terrain des stagiaires.',
    admin: 'Administrez la plateforme, gérez les utilisateurs et suivez les statistiques globales du dispositif de stage.'
  };
  return messages[role] || 'Bienvenue sur la plateforme de gestion des stages.';
};

const SearchInternshipsTab = () => {
  const [internships, setInternships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('');

  useEffect(() => {
    fetchInternships();
  }, [searchQuery, locationFilter]);

  const fetchInternships = async () => {
    try {
      const params = new URLSearchParams();
      if (locationFilter) params.append('location', locationFilter);
      if (searchQuery) params.append('company', searchQuery);
      
      const response = await axios.get(`${API}/internships?${params.toString()}`);
      setInternships(response.data);
    } catch (error) {
      console.error('Error fetching internships:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyToInternship = async (internshipId) => {
    const coverLetter = prompt('Lettre de motivation (optionnelle):');
    if (coverLetter === null) return; // User cancelled

    try {
      await axios.post(`${API}/applications`, {
        internship_id: internshipId,
        cover_letter: coverLetter || ''
      });
      alert('Candidature envoyée avec succès!');
      fetchInternships(); // Refresh to update application counts
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la candidature');
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Rechercher des stages</h2>

      {/* Search filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recherche par entreprise
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Nom de l'entreprise..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Localisation
            </label>
            <input
              type="text"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              placeholder="Ville..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Internships list */}
      {loading ? (
        <div className="text-center py-8">Chargement des stages...</div>
      ) : (
        <div className="space-y-6">
          {internships.map((internship) => (
            <div key={internship.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {internship.title}
                  </h3>
                  <p className="text-lg text-blue-600 mb-2">{internship.company_name}</p>
                  <div className="flex items-center text-sm text-gray-600 mb-4">
                    <span className="mr-4">📍 {internship.location}</span>
                    <span className="mr-4">⏱️ {internship.duration_months} mois</span>
                    <span>📅 {new Date(internship.start_date).toLocaleDateString()}</span>
                  </div>
                  <p className="text-gray-700 mb-4">{internship.description}</p>
                  
                  {internship.requirements.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-medium text-gray-900 mb-2">Exigences:</h4>
                      <ul className="list-disc list-inside text-sm text-gray-600">
                        {internship.requirements.map((req, idx) => (
                          <li key={idx}>{req}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {internship.skills_required.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-medium text-gray-900 mb-2">Compétences requises:</h4>
                      <div className="flex flex-wrap gap-2">
                        {internship.skills_required.map((skill, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <p className="text-sm text-gray-500">
                    {internship.applications_count} candidature(s)
                  </p>
                </div>
                
                <button
                  onClick={() => applyToInternship(internship.id)}
                  className="ml-4 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md font-medium"
                >
                  Postuler
                </button>
              </div>
            </div>
          ))}
          
          {internships.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600">Aucun stage trouvé selon vos critères.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MyApplicationsTab = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${API}/my-applications`);
      setApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const cancelApplication = async (applicationId) => {
    if (!confirm('Êtes-vous sûr de vouloir annuler cette candidature ?')) return;
    
    try {
      await axios.delete(`${API}/applications/${applicationId}`);
      alert('Candidature annulée avec succès!');
      fetchApplications(); // Refresh list
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

// Dans MyApplicationsTab (vue étudiant)
const getStatusText = (status) => {
  // Pour l'étudiant, si le statut est en attente d'approbation,
  // on lui affiche "En attente" pour qu'il ne pense pas que c'est final.
  if (status === 'pending_academic_approval') {
    return 'En attente';
  }
  switch (status) {
    case 'pending': return 'En attente';
    case 'accepted': return 'Acceptée';
    case 'rejected': return 'Refusée';
    case 'cancelled': return 'Annulée';
    default: return status;
  }
};

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Mes candidatures</h2>

      {loading ? (
        <div className="text-center py-8">Chargement des candidatures...</div>
      ) : (
        <div className="space-y-6">
          {applications.map((app) => (
            <div key={app.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {app.internship.title}
                  </h3>
                  <p className="text-lg text-blue-600 mb-2">{app.internship.company_name}</p>
                  <div className="flex items-center text-sm text-gray-600 mb-4">
                    <span className="mr-4">📍 {app.internship.location}</span>
                    <span className="mr-4">📅 Candidature envoyée le {new Date(app.applied_at).toLocaleDateString()}</span>
                  </div>
                  
                  {app.cover_letter && (
                    <div className="mb-4">
                      <h4 className="font-medium text-gray-900 mb-2">Lettre de motivation:</h4>
                      <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                        {app.cover_letter}
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="ml-4 flex flex-col items-end space-y-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(app.status)}`}>
                    {getStatusText(app.status)}
                  </span>
                  
                  {app.status === 'pending' && (
                    <button
                      onClick={() => cancelApplication(app.id)}
                      className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                    >
                      Annuler candidature
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
          
          {applications.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600">Vous n'avez encore postulé à aucun stage.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SpontaneousApplicationTab = ({ user }) => {
  
  const initialFormState = {
    title: '',
    description: '',
    company_name: (user && user.role !== 'academic_supervisor') ? user.company_name : '',
    location: '',
    duration_months: 3,
    start_date: '',
    requirements: '',
    skills_required: ''
  };

  const [formData, setFormData] = useState(initialFormState);
  const [loading, setLoading] = useState(false);
  const [myInternships, setMyInternships] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchMyInternships();
  }, []);

  const fetchMyInternships = async () => {
    setListLoading(true);
    try {
      const response = await axios.get(`${API}/my-internships`);
      setMyInternships(response.data);
      setError('');
    } catch (error) {
      console.error("Erreur lors de la récupération des stages:", error);
      setError('Impossible de charger la liste de vos stages.');
    } finally {
      setListLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const internshipData = {
        ...formData,
        start_date: new Date(formData.start_date).toISOString(),
        requirements: formData.requirements.split('\n').filter(r => r.trim()),
        skills_required: formData.skills_required.split(',').map(s => s.trim()).filter(s => s)
      };
      await axios.post(`${API}/internships`, internshipData);
      alert('Offre de stage publiée avec succès!');
      setFormData(initialFormState);
      fetchMyInternships();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la publication');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const toggleInternshipStatus = async (internshipId, currentStatus) => {
    const newStatus = currentStatus === 'open' ? 'closed' : 'open';
    const action = newStatus === 'closed' ? 'fermer' : 'rouvrir';
    if (!window.confirm(`Voulez-vous vraiment ${action} cette offre ?`)) return;

    try {
      await axios.put(`${API}/internships/${internshipId}/status`, null, { params: { new_status: newStatus } });
      fetchMyInternships();
    } catch (error) {
      alert('Erreur lors de la mise à jour du statut.');
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        {user.role === 'academic_supervisor' ? 'Proposer un Stage Spontané' : 'Publier une offre de stage'}
      </h2>
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Titre du stage *</label>
              <input type="text" name="title" value={formData.title} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" required />
            </div>
            {user.role === 'academic_supervisor' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nom de l'entreprise *</label>
                <input type="text" name="company_name" value={formData.company_name} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" required />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nom de l'entreprise</label>
                <input type="text" name="company_name" value={formData.company_name} className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100" readOnly />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Localisation *</label>
              <input type="text" name="location" value={formData.location} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Durée (mois) *</label>
              <input type="number" name="duration_months" value={formData.duration_months} onChange={handleInputChange} min="1" max="12" className="w-full px-3 py-2 border border-gray-300 rounded-md" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date de début *</label>
              <input type="date" name="start_date" value={formData.start_date} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" required />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description du stage *</label>
            <textarea name="description" value={formData.description} onChange={handleInputChange} rows="4" className="w-full px-3 py-2 border border-gray-300 rounded-md" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Exigences (une par ligne)</label>
            <textarea name="requirements" value={formData.requirements} onChange={handleInputChange} rows="3" placeholder="Ex: Niveau Bac+2..." className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Compétences requises (séparées par des virgules)</label>
            <input type="text" name="skills_required" value={formData.skills_required} onChange={handleInputChange} placeholder="Java, Python..." className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>
          <button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md disabled:opacity-50">
            {loading ? 'Publication en cours...' : 'Publier l\'offre de stage'}
          </button>
        </form>
      </div>
      
    
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Vos Offres Publiées</h2>
      <div className="bg-white rounded-lg shadow">
        {listLoading ? (
          <p className="p-6 text-center text-gray-500">Chargement de vos offres...</p>
        ) : error ? (
          <p className="p-6 text-center text-red-500">{error}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Titre du Stage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entreprise</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {myInternships.map((internship) => (
                  <tr key={internship.id}>
                    <td className="px-6 py-4 whitespace-nowrap">{internship.title}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{internship.company_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${internship.status === 'open' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {internship.status === 'open' ? 'Ouvert' : 'Fermé'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => toggleInternshipStatus(internship.id, internship.status)}
                        className={`px-3 py-1 text-sm rounded ${internship.status === 'open' ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-green-500 hover:bg-green-600 text-white'}`}
                      >
                        {internship.status === 'open' ? 'Fermer' : 'Rouvrir'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {myInternships.length === 0 && !listLoading && !error && (
          <p className="p-6 text-center text-gray-500">Vous n'avez publié aucune offre pour le moment.</p>
        )}
      </div>
    </div>
  );
};

// ======================================================
// REMPLACEZ VOTRE COMPOSANT PostInternshipTab PAR CE CODE COMPLET
// ======================================================
const PostInternshipTab = () => {
  
   const [formData, setFormData] = useState({
    title: '',
    description: '',
    company_name: '',
    location: '',
    duration_months: 3,
    start_date: '',
    requirements: '',
    skills_required: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const internshipData = {
        ...formData,
        start_date: new Date(formData.start_date).toISOString(),
        requirements: formData.requirements.split('\n').filter(r => r.trim()),
        skills_required: formData.skills_required.split(',').map(s => s.trim()).filter(s => s)
      };

      await axios.post(`${API}/internships`, internshipData);
      alert('Offre de stage publiée avec succès!');
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        company_name: '',
        location: '',
        duration_months: 3,
        start_date: '',
        requirements: '',
        skills_required: ''
      });
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la publication');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Publier une offre de stage</h2>

      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Titre du stage *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom de l'entreprise *
              </label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Localisation *
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Durée (mois) *
              </label>
              <input
                type="number"
                name="duration_months"
                value={formData.duration_months}
                onChange={handleInputChange}
                min="1"
                max="12"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date de début *
              </label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description du stage *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="4"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Exigences (une par ligne)
            </label>
            <textarea
              name="requirements"
              value={formData.requirements}
              onChange={handleInputChange}
              rows="3"
              placeholder="Exemple:&#10;Niveau Bac+2 minimum&#10;Connaissance en informatique&#10;Motivation et sérieux"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Compétences requises (séparées par des virgules)
            </label>
            <input
              type="text"
              name="skills_required"
              value={formData.skills_required}
              onChange={handleInputChange}
              placeholder="Java, Python, Communication, Travail en équipe"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md disabled:opacity-50"
          >
            {loading ? 'Publication en cours...' : 'Publier l\'offre de stage'}
          </button>
        </form>
      </div>
    </div>
  );
};
// ===============================================
// REMPLACEZ VOTRE COMPOSANT MyInternshipsTab PAR CETTE NOUVELLE VERSION
// ===============================================
const MyInternshipsTab = () => {
  const [internships, setInternships] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyInternships();
  }, []);

  const fetchMyInternships = async () => {
    try {
      const response = await axios.get(`${API}/my-internships`);
      setInternships(response.data);
    } catch (error) {
      console.error('Error fetching internships:', error);
    } finally {
      setLoading(false);
    }
  };

  // --- FONCTION MISE À JOUR ---
  const toggleInternshipStatus = async (internshipId, currentStatus) => {
    const newStatus = currentStatus === 'open' ? 'closed' : 'open';
    const action = newStatus === 'closed' ? 'fermer' : 'rouvrir';

    if (!window.confirm(`Voulez-vous vraiment ${action} cette offre de stage ?`)) {
      return;
    }

    try {
      // On appelle la nouvelle route API
      await axios.put(`${API}/internships/${internshipId}/status`, null, {
        params: { new_status: newStatus }
      });
      alert(`Offre de stage ${newStatus === 'closed' ? 'fermée' : 'rouverte'} avec succès!`);
      fetchMyInternships(); // Recharger la liste
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  // --- FONCTIONS MISES À JOUR ---
  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'open': return 'Ouverte';
      case 'closed': return 'Fermée';
      default: return status;
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Mes offres de stage</h2>

      {loading ? (
        <div className="text-center py-8">Chargement des offres...</div>
      ) : (
        <div className="space-y-6">
          {internships.map((internship) => (
            <div key={internship.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {internship.title}
                  </h3>
                  {/* --- J'ai remis l'affichage du nom de l'entreprise qui avait disparu --- */}
                  <p className="text-lg text-blue-600 mb-2">{internship.company_name}</p> 
                  <div className="flex items-center text-sm text-gray-600 mb-4">
                    <span className="mr-4">📍 {internship.location}</span>
                    <span className="mr-4">⏱️ {internship.duration_months} mois</span>
                    <span className="mr-4">📅 {new Date(internship.start_date).toLocaleDateString()}</span>
                    <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(internship.status)}`}>
                      {getStatusText(internship.status)}
                    </span>
                  </div>
                  <p className="text-gray-700 mb-4">{internship.description}</p>
                  <p className="text-sm text-blue-600 font-medium">
                    {internship.applications_count || 0} candidature(s) reçue(s)
                  </p>
                </div>
                
                {/* --- LOGIQUE DU BOUTON MISE À JOUR --- */}
                <button
                  onClick={() => toggleInternshipStatus(internship.id, internship.status)}
                  className={`ml-4 flex-shrink-0 px-4 py-2 rounded-md text-sm text-white ${
                    internship.status === 'open' 
                      ? 'bg-red-600 hover:bg-red-700' 
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {internship.status === 'open' ? 'Fermer l\'offre' : 'Rouvrir l\'offre'}
                </button>
              </div>
            </div>
          ))}
          
          {internships.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600">Vous n'avez publié aucune offre de stage.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ApplicationsTab = () => {
  const [myInternships, setMyInternships] = useState([]);
  const [selectedInternship, setSelectedInternship] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyInternships();
  }, []);

  const fetchMyInternships = async () => {
    try {
      const response = await axios.get(`${API}/my-internships`);
      setMyInternships(response.data);
      if (response.data.length > 0) {
        setSelectedInternship(response.data[0]);
        fetchApplications(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching internships:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async (internshipId) => {
    try {
      const response = await axios.get(`${API}/internship-applications/${internshipId}`);
      setApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
    }
  };

  const updateApplicationStatus = async (applicationId, status) => {
    try {
      await axios.put(`${API}/applications/${applicationId}/status?status=${status}`);
      // Refresh applications
      if (selectedInternship) {
        fetchApplications(selectedInternship.id);
      }
      alert('Statut mis à jour avec succès!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };
// Dans ApplicationsTab (vue employeur)
const getStatusColor = (status) => {
  // On associe la couleur "acceptée" au nouveau statut
  if (status === 'pending_academic_approval') {
    return 'bg-green-100 text-green-800';
  }
  switch (status) {
    case 'pending': return 'bg-yellow-100 text-yellow-800';
    case 'accepted': return 'bg-green-100 text-green-800';
    case 'rejected': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

// Dans ApplicationsTab (vue employeur)
const getStatusText = (status) => {
  // Pour l'employeur, si le statut est en attente d'approbation académique,
  // on lui montre quand même "Acceptée" pour que l'action soit claire.
  if (status === 'pending_academic_approval') {
    return 'Acceptée';
  }
  switch (status) {
    case 'pending': return 'En attente';
    case 'accepted': return 'Acceptée';
    case 'rejected': return 'Refusée';
    default: return status;
  }
};

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  if (myInternships.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Vous devez d'abord publier une offre de stage pour voir les candidatures.</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Candidatures reçues</h2>

      {/* Internship selector */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Sélectionner une offre de stage:
        </label>
        <select
          value={selectedInternship?.id || ''}
          onChange={(e) => {
            const internship = myInternships.find(i => i.id === e.target.value);
            setSelectedInternship(internship);
            fetchApplications(e.target.value);
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        >
          {myInternships.map((internship) => (
            <option key={internship.id} value={internship.id}>
              {internship.title} - {internship.applications_count} candidature(s)
            </option>
          ))}
        </select>
      </div>

      {/* Applications list */}
      <div className="space-y-6">
        {applications.map((app) => (
          <div key={app.id} className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {app.student_name}
                </h3>
                <p className="text-gray-600 mb-2">{app.student_email}</p>
                <p className="text-sm text-gray-500 mb-4">
                  Candidature envoyée le {new Date(app.applied_at).toLocaleDateString()}
                </p>
                
                {app.cover_letter && (
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-900 mb-2">Lettre de motivation:</h4>
                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                      {app.cover_letter}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="ml-4 flex flex-col items-end space-y-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(app.status)}`}>
                  {getStatusText(app.status)}
                </span>
                
                {app.status === 'pending' && (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => updateApplicationStatus(app.id, 'accepted')}
                      className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm"
                    >
                      Accepter
                    </button>
                    <button
                      onClick={() => updateApplicationStatus(app.id, 'rejected')}
                      className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                    >
                      Refuser
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        
        {applications.length === 0 && selectedInternship && (
          <div className="text-center py-12">
            <p className="text-gray-600">Aucune candidature reçue pour ce stage.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Admin Components
const AdminUsersTab = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    role: '',
    department: '',
    status: '',
    search: ''
  });
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRY = 3;
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchDepartments();
  }, []);


const handleSelectUser = (userId) => {
  setSelectedUsers(prev => 
    prev.includes(userId) 
      ? prev.filter(id => id !== userId) // Si déjà sélectionné, on le retire
      : [...prev, userId] // Sinon, on l'ajoute
  );
};

const handleSelectAll = () => {
  // Si tous les utilisateurs filtrés sont déjà sélectionnés, on désélectionne tout
  if (selectedUsers.length === filteredUsers.length) {
    setSelectedUsers([]);
  } else {
    // Sinon, on sélectionne tous les utilisateurs actuellement visibles/filtrés
    setSelectedUsers(filteredUsers.map(user => user.id));
  }
};

// Dans AdminUsersTab

const handleDeleteSelected = async () => {
  if (selectedUsers.length === 0) {
    alert("Aucun utilisateur sélectionné.");
    return;
  }

  if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedUsers.length} utilisateur(s) ? Cette action est irréversible.`)) {
    return;
  }

  // On peut mettre un état de chargement ici si on veut
  
  let deletedCount = 0;
  for (const userId of selectedUsers) {
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      deletedCount++;
    } catch (error) {
      console.error(`Erreur lors de la suppression de l'utilisateur ${userId}:`, error);
      alert(`Une erreur est survenue lors de la suppression de l'utilisateur ${userId}. L'opération s'est arrêtée.`);
      break; // Arrêter la boucle en cas d'erreur
    }
  }

  alert(`${deletedCount} sur ${selectedUsers.length} utilisateur(s) ont été supprimés avec succès.`);
  
  // Réinitialiser la sélection et recharger la liste des utilisateurs
  setSelectedUsers([]);
  fetchUsers(); 
};
  

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/structure/departments`);
      setDepartments(response.data.departments);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };
  // Dans AdminUsersTab

const handleOpenEditModal = (user) => {
    setEditingUser(user); // Stocker l'utilisateur qu'on modifie
    setIsEditModalOpen(true); // Ouvrir la modale
};

const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingUser(null);
};

const handleUpdateUser = async (updatedData) => {
    if (!editingUser) return;

    try {
        await axios.put(`${API}/admin/users/${editingUser.id}`, updatedData);
        alert('Utilisateur mis à jour avec succès !');
        handleCloseEditModal();
        fetchUsers(); // Recharger la liste des utilisateurs
    } catch (error) {
        alert("Erreur lors de la mise à jour : " + (error.response?.data?.detail || error.message));
    }
};

  useEffect(() => {
    applyFilters();
  }, [users, filters]);

  const fetchUsers = async () => {
    setLoading(true);
    setFilteredUsers([]); // Clear previous data
    
    try {
      const response = await axios.get(`${API}/admin/users`);
      if (response.data && Array.isArray(response.data)) {
        setUsers(response.data);
        setRetryCount(0); // Reset retry count on success
        // Le loading sera mis à false après que les filtres soient appliqués
      } else {
        console.error('Invalid response format:', response.data);
        setUsers([]);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      
      // Retry logic
      if (retryCount < MAX_RETRY) {
        console.log(`Retrying... (${retryCount + 1}/${MAX_RETRY})`);
        setRetryCount(prev => prev + 1);
        setTimeout(() => {
          fetchUsers();
        }, 1000); // Wait 1 second before retry
      } else {
        setUsers([]);
        setLoading(false);
        setRetryCount(0);
      }
    }
  };

  const applyFilters = () => {
    if (!users || users.length === 0) {
      setFilteredUsers([]);
      // Si pas de données, ne pas arrêter le loading
      return;
    }

    let filtered = [...users];

    // Filter by role
    if (filters.role) {
      filtered = filtered.filter(user => user.role === filters.role);
    }

    // Filter by department
    if (filters.department) {
      filtered = filtered.filter(user => 
        user.department === filters.department || 
        user.student_department === filters.department
      );
    }

    // Filter by status
    if (filters.status) {
      filtered = filtered.filter(user => 
        filters.status === 'active' ? user.is_active : !user.is_active
      );
    }

    // Filter by search
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(user => 
        user.name.toLowerCase().includes(searchTerm) ||
        user.email.toLowerCase().includes(searchTerm) ||
        (user.company_name && user.company_name.toLowerCase().includes(searchTerm))
      );
    }

    setFilteredUsers(filtered);
    
    // Seulement arrêter le loading si on a des données valides
    if (users.length > 0) {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      role: '',
      department: '',
      status: '',
      search: ''
    });
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/status?is_active=${!currentStatus}`);
      alert('Statut utilisateur mis à jour!');
      fetchUsers();
    } catch (error) {
      alert('Erreur lors de la mise à jour');
    }
  };

  const deleteUser = async (userId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur?')) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      alert('Utilisateur supprimé!');
      fetchUsers();
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      student: 'Étudiant',
      professional_supervisor: 'Encadreur professionnel',
      academic_supervisor: 'Encadreur académique',
      teacher_mission: 'Enseignant en mission',
      employer: 'Employeur',
      admin: 'Administrateur',
      super_admin: 'Super Administrateur'
    };
    return roleNames[role] || role;
  };

  // Fixed list of all possible roles
  const allRoles = [
    'student',
    'professional_supervisor', 
    'academic_supervisor',
    'teacher_mission',
    'employer',
    'admin',
    'super_admin'
  ];

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Gestion des Utilisateurs</h2>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">🔍 Filtres</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Recherche</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => updateFilter('search', e.target.value)}
              placeholder="Nom, email ou entreprise..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Rôle</label>
            <select
              value={filters.role}
              onChange={(e) => updateFilter('role', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Tous les rôles</option>
              {allRoles.map(role => (
                <option key={role} value={role}>{getRoleDisplayName(role)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Département</label>
            <select
              value={filters.department}
              onChange={(e) => updateFilter('department', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Tous les départements</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
            <select
              value={filters.status}
              onChange={(e) => updateFilter('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Tous les statuts</option>
              <option value="active">Actifs</option>
              <option value="inactive">Inactifs</option>
            </select>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {filteredUsers.length} utilisateur(s) affiché(s) sur {users.length} total
          </div>
          <button
            onClick={clearFilters}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            Effacer les filtres
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">Chargement des utilisateurs...</div>
      ) : (
          <>
          {/* --- NOUVEAU BLOC POUR LES ACTIONS EN MASSE --- */}
          <div className="bg-white rounded-t-lg shadow px-6 py-3 border-b flex items-center justify-between">
            <span className="text-sm text-gray-600">
              {selectedUsers.length} utilisateur(s) sélectionné(s)
            </span>
            {selectedUsers.length > 0 && (
              <button
                onClick={handleDeleteSelected}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Supprimer la sélection
              </button>
            )}
          </div>
          {/* --- FIN DU NOUVEAU BLOC --- */}

          <div className="bg-white rounded-b-lg shadow overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {/* --- NOUVELLE COLONNE CHECKBOX "TOUT SÉLECTIONNER" --- */}
                  <th className="px-6 py-3">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      onChange={handleSelectAll}
                      checked={filteredUsers.length > 0 && selectedUsers.length === filteredUsers.length}
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilisateur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rôle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Département
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date création
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className={selectedUsers.includes(user.id) ? 'bg-blue-50' : ''}>
                    {/* --- NOUVELLE COLONNE CHECKBOX PAR LIGNE --- */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => handleSelectUser(user.id)}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                        {user.company_name && (
                          <div className="text-xs text-blue-600">{user.company_name}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                        {getRoleDisplayName(user.role)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.department || user.student_department || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {user.is_active ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
        onClick={() => handleOpenEditModal(user)}
        className="text-blue-600 hover:text-blue-900 mr-3"
    >
        Modifier
    </button>
                      <button
                        onClick={() => toggleUserStatus(user.id, user.is_active)}
                        className={`mr-2 px-3 py-1 rounded text-xs ${user.is_active ? 'bg-yellow-600 hover:bg-yellow-700 text-white' : 'bg-green-600 hover:bg-green-700 text-white'}`}
                      >
                        {user.is_active ? 'Désactiver' : 'Activer'}
                      </button>
                      <button
                        onClick={() => deleteUser(user.id)}
                        className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs"
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {filteredUsers.length === 0 && !loading && (
              <div className="text-center py-12">
                <p className="text-gray-600">
                  {users.length === 0 ? "Aucun utilisateur trouvé." : "Aucun utilisateur ne correspond aux critères de filtrage."}
                </p>
              </div>
            )}
          </div>
        </>
      )}
       {isEditModalOpen && (
    <EditUserModal 
      user={editingUser} 
      onClose={handleCloseEditModal} 
      onSave={handleUpdateUser}
      allRoles={allRoles}
      departments={departments}
      iutStructure={IUT_STRUCTURE}
    />
    )}
    </div>
  );
};

// Collez ce code complet dans votre fichier App.js

const getRoleDisplayName = (role) => {
    const roleNames = {
      student: 'Étudiant',
      professional_supervisor: 'Encadreur professionnel',
      academic_supervisor: 'Encadreur académique',
      teacher_mission: 'Enseignant en mission',
      employer: 'Employeur',
      admin: 'Administrateur',
      super_admin: 'Super Administrateur'
    };
    return roleNames[role] || role;
};

const EditUserModal = ({ user, onClose, onSave, allRoles, departments, iutStructure }) => {
    const [formData, setFormData] = useState({
        name: user.name || '',
        email: user.email || '',
        role: user.role || 'student', // Initialisation sûre
        company_name: user.company_name || '',
        department: user.department || '', // Initialisation sûre
        phone: user.phone || '',
        student_department: user.student_department || '', // Initialisation sûre
        student_cursus: user.student_cursus || '', // Initialisation sûre
        student_parcours: user.student_parcours || '', // Initialisation sûre
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        const newState = { ...formData, [name]: value };

        // Gérer la logique de cascade pour les étudiants
        if (name === 'student_department') {
            newState.student_cursus = '';
            newState.student_parcours = '';
        }
        if (name === 'student_cursus') {
            newState.student_parcours = '';
        }

        setFormData(newState);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
         const dataToSave = {};

    // Comparer chaque champ avec l'utilisateur original pour n'envoyer que les changements
    if (formData.name !== user.name) dataToSave.name = formData.name;
    if (formData.email !== user.email) dataToSave.email = formData.email;
    if (formData.role !== user.role) dataToSave.role = formData.role;
    if (formData.phone !== (user.phone || '')) dataToSave.phone = formData.phone;

    // Champs spécifiques au rôle
    if (formData.role === 'student') {
        if (formData.student_department !== user.student_department) dataToSave.student_department = formData.student_department;
        if (formData.student_cursus !== user.student_cursus) dataToSave.student_cursus = formData.student_cursus;
        if (formData.student_parcours !== user.student_parcours) dataToSave.student_parcours = formData.student_parcours;
    } else if (['professional_supervisor', 'employer'].includes(formData.role)) {
        if (formData.company_name !== user.company_name) dataToSave.company_name = formData.company_name;
    } else if (['academic_supervisor', 'teacher_mission'].includes(formData.role)) {
        if (formData.department !== user.department) dataToSave.department = formData.department;
    }

    // Si aucune donnée n'a changé, on peut informer l'utilisateur et ne rien envoyer
    if (Object.keys(dataToSave).length === 0) {
        alert("Aucune modification n'a été effectuée.");
        return;
    }

    onSave(dataToSave);
    };
     const isSupervisorEditing = allRoles.length === 1 && allRoles[0] === 'student';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <h3 className="text-xl font-semibold mb-6">Modifier l'utilisateur : {user.name}</h3>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* --- Champs Communs --- */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Email </label>
                        <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="mt-1 w-full bg-gray-100 p-2 border border-gray-300 rounded-md" required/>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Nom complet *</label>
                        <input type="text" name="name" value={formData.name} onChange={handleInputChange} className="mt-1 w-full p-2 border border-gray-300 rounded-md" required />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Téléphone</label>
                        <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className="mt-1 w-full p-2 border border-gray-300 rounded-md"  />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Rôle *</label>
                        <select name="role" value={formData.role} onChange={handleInputChange} className="mt-1 w-full p-2 border border-gray-300 rounded-md" disabled={isSupervisorEditing}>
                            {allRoles.map(role => <option key={role} value={role}>{getRoleDisplayName(role)}</option>)}
                        </select>
                    </div>
                    
                    {/* Pour les rôles liés à l'entreprise */}
                    {['professional_supervisor', 'employer'].includes(formData.role) && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Nom de l'entreprise *</label>
                            <input type="text" name="company_name" value={formData.company_name} onChange={handleInputChange} className="mt-1 w-full p-2 border border-gray-300 rounded-md" required />
                        </div>
                    )}

                    {/* Pour les rôles liés à un département IUT */}
                    {['academic_supervisor', 'teacher_mission'].includes(formData.role) && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Département *</label>
                            <select name="department" value={formData.department} onChange={handleInputChange} className="mt-1 w-full p-2 border border-gray-300 rounded-md" required>
                                <option value="">Sélectionner un département</option>
                                {departments.map(dept => <option key={dept} value={dept}>{dept}</option>)}
                            </select>
                        </div>
                    )}

                    {/* Pour le rôle étudiant */}
                    {formData.role === 'student' && (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Département de l'étudiant *</label>
                                <select name="student_department" value={formData.student_department} onChange={handleInputChange} className="mt-1 w-full p-2 border border-gray-300 rounded-md" required disabled={isSupervisorEditing}>
                                  {isSupervisorEditing ? (
                                        // Si c'est un encadreur, on n'affiche que son département
                                        <option value={formData.student_department}>{formData.student_department}</option>
                                    ) : (
                                        // Sinon (admin), on affiche toutes les options
                                        <>
                                    <option value="">Sélectionner un département</option>
                                    {Object.keys(iutStructure).map(dept => (<option key={dept} value={dept}>{dept}</option>))}
                                     </>
                                    )}
                                    </select>
                            </div>
                            <div>
                               <label className="block text-sm font-medium text-gray-700">Cursus *</label>
                                <select name="student_cursus" value={formData.student_cursus} onChange={handleInputChange} className="mt-1 w-full p-2 border border-gray-300 rounded-md" required disabled={!formData.student_department}>
                                    <option value="">Sélectionner un cursus</option>
                                    {formData.student_department && iutStructure[formData.student_department] && Object.keys(iutStructure[formData.student_department]).map(cursus => (<option key={cursus} value={cursus}>{cursus}</option>))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Parcours *</label>
                                <select name="student_parcours" value={formData.student_parcours} onChange={handleInputChange} className="mt-1 w-full p-2 border border-gray-300 rounded-md" required disabled={!formData.student_cursus}>
                                    <option value="">Sélectionner un parcours</option>
                                    {formData.student_department && formData.student_cursus && iutStructure[formData.student_department][formData.student_cursus] && iutStructure[formData.student_department][formData.student_cursus].map(parcours => (<option key={parcours} value={parcours}>{parcours}</option>))}
                                </select>
                            </div>
                        </>
                    )}

                    {/* --- Boutons d'action --- */}
                    <div className="flex justify-end space-x-4 pt-6">
                        <button type="button" onClick={onClose} className="px-6 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Annuler</button>
                        <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Sauvegarder les modifications</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const AdminInternshipsTab = () => {
  const [internships, setInternships] = useState([]);
  const [filteredInternships, setFilteredInternships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    department: '',
    cursus: '',
    parcours: '',
    status: '',
    search: ''
  });

  // États pour les listes de filtres depuis la structure officielle IUT
  const [departments, setDepartments] = useState([]);
  const [cursus, setCursus] = useState([]);
  const [parcours, setParcours] = useState([]);

  useEffect(() => {
    fetchInternships();
    fetchDepartments();
    fetchAllCursus();
    fetchAllParcours();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [internships, filters]);

  const fetchInternships = async () => {
    try {
      const response = await axios.get(`${API}/admin/internships`);
      setInternships(response.data);
    } catch (error) {
      console.error('Error fetching internships:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch departments from IUT structure
  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/structure/departments`);
      setDepartments(response.data.departments);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  // Fetch all cursus on component mount
  const fetchAllCursus = async () => {
    try {
      const response = await axios.get(`${API}/structure/cursus`);
      setCursus(response.data.cursus);
    } catch (error) {
      console.error('Error fetching all cursus:', error);
    }
  };

  // Fetch all parcours on component mount
  const fetchAllParcours = async () => {
    try {
      const response = await axios.get(`${API}/structure/parcours`);
      setParcours(response.data.parcours);
    } catch (error) {
      console.error('Error fetching all parcours:', error);
    }
  };

  // Fetch cursus when department changes
  const fetchCursus = async (department) => {
    try {
      const response = await axios.get(`${API}/structure/cursus`, {
        params: { department }
      });
      setCursus(response.data.cursus);
    } catch (error) {
      console.error('Error fetching cursus:', error);
    }
  };

  // Fetch parcours when department or cursus changes
  const fetchParcours = async (department, cursus) => {
    try {
      const response = await axios.get(`${API}/structure/parcours`, {
        params: { department, cursus }
      });
      setParcours(response.data.parcours);
    } catch (error) {
      console.error('Error fetching parcours:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...internships];

    // Filter by department
    if (filters.department) {
      filtered = filtered.filter(internship => internship.required_department === filters.department);
    }

    // Filter by cursus
    if (filters.cursus) {
      filtered = filtered.filter(internship => internship.required_cursus === filters.cursus);
    }

    // Filter by parcours
    if (filters.parcours) {
      filtered = filtered.filter(internship => internship.required_parcours === filters.parcours);
    }

    // Filter by status
    if (filters.status) {
      filtered = filtered.filter(internship => internship.status === filters.status);
    }

    // Filter by search
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(internship => 
        internship.title.toLowerCase().includes(searchTerm) ||
        internship.company_name.toLowerCase().includes(searchTerm) ||
        internship.location.toLowerCase().includes(searchTerm)
      );
    }

    setFilteredInternships(filtered);
  };

  const updateFilter = (key, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      
      // Cascade logic for filters
      if (key === 'department') {
        newFilters.cursus = '';
        newFilters.parcours = '';
        setCursus([]);
        setParcours([]);
        
        if (value) {
          fetchCursus(value);
        }
      } else if (key === 'cursus') {
        newFilters.parcours = '';
        setParcours([]);
        
        if (value && newFilters.department) {
          fetchParcours(newFilters.department, value);
        }
      }
      
      return newFilters;
    });
  };

  const clearFilters = () => {
    setFilters({
      department: '',
      cursus: '',
      parcours: '',
      status: '',
      search: ''
    });
  };

  const updateInternshipStatus = async (internshipId, status) => {
    try {
      await axios.put(`${API}/admin/internships/${internshipId}/status?status=${status}`);
      alert('Statut du stage mis à jour!');
      fetchInternships();
    } catch (error) {
      alert('Erreur lors de la mise à jour');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'closed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'published': return 'Publié';
      case 'draft': return 'Brouillon';
      case 'closed': return 'Fermé';
      default: return status;
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Gestion des Stages</h2>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">🔍 Filtres</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Recherche</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => updateFilter('search', e.target.value)}
              placeholder="Titre, entreprise, lieu..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Département</label>
            <select
              value={filters.department}
              onChange={(e) => updateFilter('department', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Tous les départements</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cursus</label>
            <select
              value={filters.cursus}
              onChange={(e) => updateFilter('cursus', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              
            >
              <option value="">Tous les cursus</option>
              {cursus.map(cursusItem => (
                <option key={cursusItem} value={cursusItem}>{cursusItem}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Parcours</label>
            <select
              value={filters.parcours}
              onChange={(e) => updateFilter('parcours', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              
            >
              <option value="">Tous les parcours</option>
              {parcours.map(parcoursItem => (
                <option key={parcoursItem} value={parcoursItem}>{parcoursItem}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
            <select
              value={filters.status}
              onChange={(e) => updateFilter('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Tous les statuts</option>
              <option value="published">Publié</option>
              <option value="draft">Brouillon</option>
              <option value="closed">Fermé</option>
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <button
            onClick={clearFilters}
            className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-md text-sm"
          >
            Effacer les filtres
          </button>
          <span className="text-sm text-gray-500">
            {filteredInternships.length} stage(s) trouvé(s)
          </span>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">Chargement des stages...</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prérequis
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Entreprise
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Candidatures
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Publié par
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredInternships.map((internship) => (
                <tr key={internship.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{internship.title}</div>
                      <div className="text-sm text-gray-500">
                        📍 {internship.location} • ⏱️ {internship.duration_months} mois
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{internship.required_department}</div>
                      <div className="text-sm text-gray-500">{internship.required_cursus} • {internship.required_parcours}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {internship.company_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(internship.status)}`}>
                      {getStatusText(internship.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {internship.applications_count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{internship.poster_name}</div>
                    <div className="text-sm text-gray-500">{internship.poster_email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <select
                      value={internship.status}
                      onChange={(e) => updateInternshipStatus(internship.id, e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded text-xs"
                    >
                      <option value="published">Publié</option>
                      <option value="draft">Brouillon</option>
                      <option value="closed">Fermé</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredInternships.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600">
                {internships.length === 0 
                  ? "Aucun stage trouvé." 
                  : "Aucun stage ne correspond aux critères de filtrage."
                }
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AdminApplicationsTab = () => {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    department: '',
    cursus: '',
    parcours: '',
    status: '',
    search: ''
  });

  // États pour les listes de filtres depuis la structure officielle IUT
  const [departments, setDepartments] = useState([]);
  const [cursus, setCursus] = useState([]);
  const [parcours, setParcours] = useState([]);

  useEffect(() => {
    fetchApplications();
    fetchDepartments();
    fetchAllCursus();
    fetchAllParcours();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [applications, filters]);

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${API}/admin/applications`);
      setApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch departments from IUT structure
  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/structure/departments`);
      setDepartments(response.data.departments);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  // Fetch all cursus on component mount
  const fetchAllCursus = async () => {
    try {
      const response = await axios.get(`${API}/structure/cursus`);
      setCursus(response.data.cursus);
    } catch (error) {
      console.error('Error fetching all cursus:', error);
    }
  };

  // Fetch all parcours on component mount
  const fetchAllParcours = async () => {
    try {
      const response = await axios.get(`${API}/structure/parcours`);
      setParcours(response.data.parcours);
    } catch (error) {
      console.error('Error fetching all parcours:', error);
    }
  };

  // Fetch cursus when department changes
  const fetchCursus = async (department) => {
    try {
      const response = await axios.get(`${API}/structure/cursus`, {
        params: { department }
      });
      setCursus(response.data.cursus);
    } catch (error) {
      console.error('Error fetching cursus:', error);
    }
  };

  // Fetch parcours when department or cursus changes
  const fetchParcours = async (department, cursus) => {
    try {
      const response = await axios.get(`${API}/structure/parcours`, {
        params: { department, cursus }
      });
      setParcours(response.data.parcours);
    } catch (error) {
      console.error('Error fetching parcours:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...applications];

    // Filter by department
    if (filters.department) {
      filtered = filtered.filter(app => app.student_department === filters.department);
    }

    // Filter by cursus
    if (filters.cursus) {
      filtered = filtered.filter(app => app.student_cursus === filters.cursus);
    }

    // Filter by parcours
    if (filters.parcours) {
      filtered = filtered.filter(app => app.student_parcours === filters.parcours);
    }

    // Filter by status
    if (filters.status) {
      filtered = filtered.filter(app => app.status === filters.status);
    }

    // Filter by search
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(app => 
        app.student_name.toLowerCase().includes(searchTerm) ||
        app.student_email.toLowerCase().includes(searchTerm) ||
        app.internship_title.toLowerCase().includes(searchTerm) ||
        app.company_name.toLowerCase().includes(searchTerm)
      );
    }

    setFilteredApplications(filtered);
  };

  const updateFilter = (key, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      
      // Cascade logic for filters
      if (key === 'department') {
        newFilters.cursus = '';
        newFilters.parcours = '';
        setCursus([]);
        setParcours([]);
        
        if (value) {
          fetchCursus(value);
        }
      } else if (key === 'cursus') {
        newFilters.parcours = '';
        setParcours([]);
        
        if (value && newFilters.department) {
          fetchParcours(newFilters.department, value);
        }
      }
      
      return newFilters;
    });
  };

  const clearFilters = () => {
    setFilters({
      department: '',
      cursus: '',
      parcours: '',
      status: '',
      search: ''
    });
    setCursus([]);
    setParcours([]);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'En attente';
      case 'accepted': return 'Acceptée';
      case 'rejected': return 'Refusée';
      default: return status;
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Toutes les Candidatures</h2>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">🔍 Filtres</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Recherche</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => updateFilter('search', e.target.value)}
              placeholder="Étudiant, entreprise, stage..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Département</label>
            <select
              value={filters.department}
              onChange={(e) => updateFilter('department', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Tous les départements</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cursus</label>
            <select
              value={filters.cursus}
              onChange={(e) => updateFilter('cursus', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              
            >
              <option value="">Tous les cursus</option>
              {cursus.map(cursusItem => (
                <option key={cursusItem} value={cursusItem}>{cursusItem}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Parcours</label>
            <select
              value={filters.parcours}
              onChange={(e) => updateFilter('parcours', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              
            >
              <option value="">Tous les parcours</option>
              {parcours.map(parcoursItem => (
                <option key={parcoursItem} value={parcoursItem}>{parcoursItem}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
            <select
              value={filters.status}
              onChange={(e) => updateFilter('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="pending_academic_approval">En attente (Approbation Académique)</option> {/* <-- LIGNE AJOUTÉE */}
              <option value="accepted">Acceptée</option>
              <option value="rejected">Refusée</option>
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <button
            onClick={clearFilters}
            className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-md text-sm"
          >
            Effacer les filtres
          </button>
          <span className="text-sm text-gray-500">
            {filteredApplications.length} candidature(s) trouvée(s)
          </span>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">Chargement des candidatures...</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Étudiant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Parcours
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Entreprise
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date candidature
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
  {filteredApplications.map((app) => (
    <tr key={app.id}>
      <td className="px-6 py-4 whitespace-nowrap">
        <div>
          <div className="text-sm font-medium text-gray-900">{app.student_name}</div>
          <div className="text-sm text-gray-500">{app.student_email}</div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div>
          <div className="text-sm font-medium text-gray-900">{app.student_department}</div>
          <div className="text-sm text-gray-500">{app.student_cursus} • {app.student_parcours}</div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-medium text-gray-900">{app.internship_title}</div>
        <div className="text-sm text-gray-500">📍 {app.internship_location}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {app.company_name}
      </td>
      
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(app.status)}`}>
          {getStatusText(app.status)}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {new Date(app.applied_at).toLocaleDateString()}
      </td>
    </tr>
  ))}
</tbody>
          </table>
          
          {filteredApplications.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600">
                {applications.length === 0 
                  ? "Aucune candidature trouvée." 
                  : "Aucune candidature ne correspond aux critères de filtrage."
                }
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AdminReportsTab = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReportData();
  }, []);

  const fetchReportData = async () => {
    try {
      const response = await axios.get(`${API}/admin/reports/summary`);
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Chargement des rapports...</div>;
  }

  if (!reportData) {
    return <div className="text-center py-8 text-red-600">Erreur lors du chargement des données.</div>;
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Rapports et Statistiques</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Users by Role */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Utilisateurs par Rôle</h3>
          <div className="space-y-3">
            {reportData.users_by_role.map((item) => (
              <div key={item._id} className="flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  {item._id === 'student' ? 'Étudiants' :
                   item._id === 'professional_supervisor' ? 'Encadreurs prof.' :
                   item._id === 'academic_supervisor' ? 'Encadreurs acad.' :
                   item._id === 'teacher_mission' ? 'Enseignants mission' :
                   item._id === 'admin' ? 'Administrateurs' : item._id}
                </span>
                <span className="font-medium">{item.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Applications by Status */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Candidatures par Statut</h3>
          <div className="space-y-3">
            {reportData.applications_by_status.map((item) => (
              <div key={item._id} className="flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  {item._id === 'pending' ? 'En attente' :
                   item._id === 'accepted' ? 'Acceptées' :
                   item._id === 'rejected' ? 'Refusées' : item._id}
                </span>
                <span className="font-medium">{item.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Internships by Status */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Stages par Statut</h3>
          <div className="space-y-3">
            {reportData.internships_by_status.map((item) => (
              <div key={item._id} className="flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  {item._id === 'published' ? 'Publiés' :
                   item._id === 'draft' ? 'Brouillons' :
                   item._id === 'closed' ? 'Fermés' : item._id}
                </span>
                <span className="font-medium">{item.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Companies */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Top Entreprises (Candidatures)</h3>
          <div className="space-y-3">
            {reportData.top_companies.slice(0, 5).map((item, index) => (
              <div key={index} className="flex justify-between items-center">
                <span className="text-sm text-gray-600">{item._id}</span>
                <span className="font-medium">{item.applications}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Monthly Statistics */}
      <div className="mt-6 bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Statistiques Mensuelles (Candidatures)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {reportData.monthly_statistics.map((item, index) => (
            <div key={index} className="text-center p-3 bg-gray-50 rounded">
              <div className="text-sm text-gray-600">
                {item._id.month}/{item._id.year}
              </div>
              <div className="text-lg font-medium">{item.applications}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 text-sm text-gray-500 text-center">
        Rapport généré le {new Date(reportData.generated_at).toLocaleString()}
      </div>
    </div>
  );
};

const AdminExportTab = () => {
  const [loading, setLoading] = useState(null); // 'users' | 'internships' | null

    const [filters, setFilters] = useState({
    role: '',
    department: '',
    cursus: '',
    parcours: '',
    external_stage_status: ''
  });

  const [departments, setDepartments] = useState([]);
  const [cursusList, setCursusList] = useState([]);
  const [parcoursList, setParcoursList] = useState([]);

  // Charger les départements au montage du composant
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axios.get(`${API}/structure/departments`);
        setDepartments(response.data.departments);
      } catch (error) {
        console.error("Erreur lors du chargement des départements:", error);
      }
    };
    fetchDepartments();
  }, []);

  // Gérer le changement de filtres et le chargement en cascade
  const handleFilterChange = async (name, value) => {
    const newFilters = { ...filters, [name]: value };

    if (name === 'role') {
      // Réinitialiser les filtres dépendants si le rôle change
      newFilters.department = '';
      newFilters.cursus = '';
      newFilters.parcours = '';
      setCursusList([]);
      setParcoursList([]);
    } else if (name === 'department') {
      newFilters.cursus = '';
      newFilters.parcours = '';
      setCursusList([]);
      setParcoursList([]);
      if (value) {
        try {
          const response = await axios.get(`${API}/structure/cursus?department=${value}`);
          setCursusList(response.data.cursus);
        } catch (error) {
          console.error("Erreur lors du chargement des cursus:", error);
        }
      }
    } else if (name === 'cursus') {
      newFilters.parcours = '';
      setParcoursList([]);
      if (value && filters.department) {
        try {
          const response = await axios.get(`${API}/structure/parcours?department=${filters.department}&cursus=${value}`);
          setParcoursList(response.data.parcours);
        } catch (error) {
          console.error("Erreur lors du chargement des parcours:", error);
        }
      }
    }
    setFilters(newFilters);
  };
  
  const resetFilters = () => {
    setFilters({ role: '', department: '', cursus: '', parcours: '', external_stage_status: ''});
    setCursusList([]);
    setParcoursList([]);
  };

  // Fonction générique pour télécharger n'importe quel fichier depuis une URL
  const downloadFile = async (url, defaultFilename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob', // <-- C'est la clé ! On attend un fichier binaire.
      });

      // Créer une URL temporaire pour le fichier reçu
      const fileURL = window.URL.createObjectURL(new Blob([response.data]));
      
      // Essayer de récupérer le nom de fichier depuis les headers de la réponse
      const contentDisposition = response.headers['content-disposition'];
      let filename = defaultFilename;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch.length === 2) {
          filename = filenameMatch[1];
        }
      }

      // Créer un lien invisible et le cliquer pour démarrer le téléchargement
      const link = document.createElement('a');
      link.href = fileURL;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      // Nettoyer
      link.remove();
      window.URL.revokeObjectURL(fileURL);

    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      alert("Une erreur est survenue lors de l'exportation.");
    }
  };

  const handleExport = async (type) => {
    setLoading(type);

    if (type === 'users') {
      // Construire l'URL avec les paramètres de filtre
      const params = new URLSearchParams();
      if (filters.role) params.append('role', filters.role);
      if (filters.department) params.append('department', filters.department);
      if (filters.cursus) params.append('cursus', filters.cursus);
      if (filters.parcours) params.append('parcours', filters.parcours);
      if (filters.external_stage_status) {
        params.append('external_stage_status', filters.external_stage_status);
      }
      
      const url = `${API}/admin/export/users?${params.toString()}`;
      await downloadFile(url, 'export_utilisateurs.xlsx');

    } else if (type === 'internships') {
      await downloadFile(`${API}/admin/export/internships`, 'export_stages.xlsx');
    }

    setLoading(null);
  };

  // Remplacez votre 'return' actuel par celui-ci

return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Export des Données</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6 flex flex-col">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Export Utilisateurs</h3>
          <p className="text-gray-600 mb-4">
            Exporter les données des utilisateurs au format Excel (.xlsx).
          </p>
          
          <div className="space-y-4 border-t border-b py-4 mb-4">
            <h4 className="text-sm font-medium text-gray-800">Filtres (Optionnel)</h4>
            
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Rôle</label>
              <select value={filters.role} onChange={(e) => handleFilterChange('role', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                <option value="">Tous les rôles</option>
                <option value="student">Étudiant</option>
                <option value="academic_supervisor">Encadreur Académique</option>
                <option value="professional_supervisor">Encadreur Professionnel</option>
                <option value="employer">Employeur</option>
                <option value="teacher_mission">Enseignant en Mission</option>
                <option value="admin">Administrateur</option>
              </select>
            </div>

            {/* Affiche les filtres département/cursus/parcours si le rôle est pertinent */}
            {filters.role === 'student' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Département</label>
                  <select value={filters.department} onChange={(e) => handleFilterChange('department', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                    <option value="">Tous les départements</option>
                    {departments.map(dept => <option key={dept} value={dept}>{dept}</option>)}
                  </select>
                </div>

                {filters.department && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Cursus</label>
                    <select value={filters.cursus} onChange={(e) => handleFilterChange('cursus', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                      <option value="">Tous les cursus</option>
                      {cursusList.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                )}
                
                {filters.cursus && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Parcours</label>
                    <select value={filters.parcours} onChange={(e) => handleFilterChange('parcours', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                      <option value="">Tous les parcours</option>
                      {parcoursList.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                )}

                {/* --- DÉBUT DE L'AJOUT --- */}
                <div className="pt-4 border-t border-gray-200 mt-4">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Filtre sur Stage Externe</label>
                  <select 
                    value={filters.external_stage_status || ''} 
                    onChange={(e) => handleFilterChange('external_stage_status', e.target.value)} 
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="">(Tous les étudiants)</option>
                    <option value="all">Avec un stage externe (tous états)</option>
                    <option value="submitted">Stage externe: Soumis</option>
                    <option value="approved">Stage externe: Approuvé</option>
                    <option value="rejected">Stage externe: Rejeté</option>
                    <option value="under_review">Stage externe: En révision</option>
                  </select>
                </div>
                {/* --- FIN DE L'AJOUT --- */}
              </>
            )}

            {/* Ce filtre ne s'affiche que pour les encadreurs académiques */}
            {filters.role === 'academic_supervisor' && (
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Département</label>
                    <select value={filters.department} onChange={(e) => handleFilterChange('department', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                        <option value="">Tous les départements</option>
                        {departments.map(dept => <option key={dept} value={dept}>{dept}</option>)}
                    </select>
                </div>
            )}
            
            <button onClick={resetFilters} className="text-sm text-blue-600 hover:underline">Réinitialiser les filtres</button>
          </div>
          
          <div className="mt-auto">
            <button
              onClick={() => handleExport('users')}
              disabled={loading !== null}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md disabled:opacity-50"
            >
              {loading === 'users' ? 'Export en cours...' : 'Exporter Utilisateurs'}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Export Stages</h3>
          <p className="text-gray-600 mb-4">
            Exporter toutes les données des stages au format Excel (.xlsx).
          </p>
          <button
            onClick={() => handleExport('internships')}
            disabled={loading !== null}
            className="w-full mt-auto bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md disabled:opacity-50"
          >
            {loading === 'internships' ? 'Export en cours...' : 'Exporter Stages'}
          </button>
        </div>
      </div>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">Format d'Export</h4>
        <p className="text-blue-700 text-sm">
          Les données sont exportées au format Excel (.xlsx) pour une consultation et une analyse facilitées.
        </p>
      </div>
    </div>
  );
};
// Department-specific components for Academic Supervisors and Teacher Mission
// ======================================================
// REMPLACEZ VOTRE COMPOSANT DepartmentStudentsTab PAR CE BLOC COMPLET
// ======================================================
const DepartmentStudentsTab = ({ user }) => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [cursus, setCursus] = useState([]);
  const [parcours, setParcours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    cursus: '',
    parcours: '',
    stage_status: '', // Le nouveau filtre
    search: ''
  });

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const handleOpenEditModal = (student) => {
    setEditingUser(student);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingUser(null);
  };

  const handleUpdateStudent = async (updatedData) => {
    if (!editingUser) return;
    try {
      // On appelle la NOUVELLE route sécurisée pour les superviseurs
      await axios.put(`${API}/supervisor/students/${editingUser.id}`, updatedData);
      alert('Étudiant mis à jour avec succès !');
      handleCloseEditModal();
      fetchDepartmentStudents(); // Recharger la liste
    } catch (error) {
      alert("Erreur lors de la mise à jour : " + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteStudent = async (studentId, studentName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'étudiant ${studentName} ? Cette action est irréversible.`)) {
      return;
    }
    try {
      // On appelle la NOUVELLE route de suppression
      await axios.delete(`${API}/supervisor/students/${studentId}`);
      alert('Étudiant supprimé avec succès.');
      fetchDepartmentStudents(); // Recharger la liste
    } catch (error) {
      alert("Erreur lors de la suppression : " + (error.response?.data?.detail || error.message));
    }
  };

  useEffect(() => {
    fetchDepartmentStudents();
    if (user && user.department) {
      fetchCursusForFilters(user.department);
    }
  }, [user]);

  useEffect(() => {
    applyFilters();
  }, [students, filters]);

  const fetchCursusForFilters = async (department) => {
    try {
      const response = await axios.get(`${API}/structure/cursus?department=${department}`);
      setCursus(response.data.cursus);
    } catch (error) {
      console.error('Error fetching cursus for filters:', error);
    }
  };

  const fetchDepartmentStudents = async () => {
    try {
      const response = await axios.get(`${API}/department-students`);
      setStudents(response.data);
    } catch (error) {
      console.error('Error fetching department students:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchParcours = async (department, cursusValue) => {
    try {
      const response = await axios.get(`${API}/structure/parcours?department=${department}&cursus=${cursusValue}`);
      setParcours(response.data.parcours);
    } catch (error) {
      console.error('Error fetching parcours:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...students];

    if (filters.cursus) {
      filtered = filtered.filter(student => student.student_cursus === filters.cursus);
    }
    if (filters.parcours) {
      filtered = filtered.filter(student => student.student_parcours === filters.parcours);
    }
    if (filters.stage_status) {
      filtered = filtered.filter(student => student.stage_status === filters.stage_status);
    }
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(student => 
        (student.name && student.name.toLowerCase().includes(searchTerm)) ||
        (student.email && student.email.toLowerCase().includes(searchTerm))
      );
    }
    setFilteredStudents(filtered);
  };

  const updateFilter = (key, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      if (key === 'cursus') {
        newFilters.parcours = '';
        setParcours([]);
        if (value && user.department) {
          fetchParcours(user.department, value);
        }
      }
      return newFilters;
    });
  };

  const clearFilters = () => {
    setFilters({
      cursus: '',
      parcours: '',
      stage_status: '',
      search: ''
    });
    setParcours([]);
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Étudiants de mon Département</h2>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">🔍 Filtres</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Recherche</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => updateFilter('search', e.target.value)}
              placeholder="Nom ou email..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cursus</label>
            <select
              value={filters.cursus}
              onChange={(e) => updateFilter('cursus', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Tous les cursus</option>
              {cursus.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Parcours</label>
            <select
              name="parcours"
              value={filters.parcours}
              onChange={(e) => updateFilter('parcours', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              disabled={!filters.cursus}
            >
              <option value="">Tous les parcours</option>
              {parcours.map((parcoursItem) => <option key={parcoursItem} value={parcoursItem}>{parcoursItem}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Statut Stage</label>
            <select
              value={filters.stage_status}
              onChange={(e) => updateFilter('stage_status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Tous les statuts</option>
              <option value="Actif">Actif (en stage)</option>
              <option value="Inactif">Inactif (sans stage)</option>
            </select>
          </div>
        </div>
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {filteredStudents.length} étudiant(s) affiché(s) sur {students.length} total
          </div>
          <button
            onClick={clearFilters}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            Effacer les filtres
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">Chargement des étudiants...</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Étudiant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cursus / Parcours
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut Stage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredStudents.map((student) => (
                <tr key={student.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{student.name}</div>
                    <div className="text-sm text-gray-500">{student.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {student.student_cursus || 'N/A'} - {student.student_parcours || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      student.stage_status === 'Actif' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {student.stage_status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleOpenEditModal(student)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      Modifier
                    </button>
                    <button
                      onClick={() => handleDeleteStudent(student.id, student.name)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredStudents.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="text-gray-600">
                {students.length === 0 
                  ? "Aucun étudiant trouvé dans votre département." 
                  : "Aucun étudiant ne correspond aux critères de filtrage."
                }
              </p>
            </div>
          )}
        </div>
      )}
      {isEditModalOpen && (
        <EditUserModal 
          user={editingUser} 
          onClose={handleCloseEditModal} 
          onSave={handleUpdateStudent}
          // On passe "allRoles" mais on pourrait créer une version simplifiée de la modale
          allRoles={['student']} // L'encadreur ne peut pas changer le rôle
          departments={[user.department]} // Ne peut pas changer le département
          iutStructure={IUT_STRUCTURE}
        />
      )}
    </div>
  );
};

// ===============================================
// REMPLACEZ VOTRE COMPOSANT DepartmentInternshipsTab PAR CELUI-CI
// ===============================================
const DepartmentInternshipsTab = ({ user }) => {
  const [internships, setInternships] = useState([]);
  const [loading, setLoading] = useState(true);
const [filters, setFilters] = useState({ search: '', cursus: '', parcours: '', status: '' });
const [cursusList, setCursusList] = useState([]);
const [parcoursList, setParcoursList] = useState([]);

// --- REMPLACEZ LE useEffect ACTUEL PAR CELUI-CI ---
useEffect(() => {
  fetchDepartmentInternships();
  // Charger les listes de filtres pour le département de l'encadreur
  if (user && user.department) {
    fetchCursusForDept(user.department);
  }
}, [user]); // On le déclenche si 'user' change
// --- FIN DU REMPLACEMENT ---

  const fetchDepartmentInternships = async () => {
    try {
      const response = await axios.get(`${API}/department/internships`);
      setInternships(response.data);
    } catch (error) {
      console.error('Error fetching department internships:', error);
    } finally {
      setLoading(false);
    }
  };

  // --- NOUVELLE FONCTION AJOUTÉE ---
  const getStatusInfo = (status) => {
    switch (status) {
      case 'pending':
        return { text: 'En attente', className: 'bg-yellow-100 text-yellow-800' };
      case 'pending_academic_approval':
        return { text: 'Attente Validation', className: 'bg-yellow-100 text-yellow-800' };
      case 'accepted':
        return { text: 'Accepté', className: 'bg-green-100 text-green-800' };
      case 'rejected':
        return { text: 'Refusé', className: 'bg-red-100 text-red-800' };
      default:
        return { text: status, className: 'bg-gray-100 text-gray-800' };
    }
  };
  // --- FIN DE L'AJOUT ---

  // --- AJOUTEZ CE BLOC DE FONCTIONS ---
// --- REMPLACEZ LE BLOC DE FONCTIONS DE FILTRAGE PAR CELUI-CI ---
const fetchCursusForDept = async (dept) => { try { const res = await axios.get(`${API}/structure/cursus?department=${dept}`); setCursusList(res.data.cursus); } catch (e) { console.error(e); }};
const fetchParcoursForCursus = async (dept, curs) => { try { const res = await axios.get(`${API}/structure/parcours?department=${dept}&cursus=${curs}`); setParcoursList(res.data.parcours); } catch (e) { console.error(e); }};

const handleFilterChange = (key, value) => {
  setFilters(prev => {
    const newFilters = { ...prev, [key]: value };

    if (key === 'cursus') {
      newFilters.parcours = '';
      setParcoursList([]);
      if (value && user && user.department) {
        fetchParcoursForCursus(user.department, value);
      }
    }
    
    return newFilters;
  });
};

const clearFilters = () => {
  setFilters({ search: '', cursus: '', parcours: '', status: '' });
  setParcoursList([]);
};

const filteredInternships = internships.map(internship => {
  const filteredApps = internship.department_applications.filter(app => {
    if (!app) return false;
    const searchLower = filters.search.toLowerCase();
    const matchesSearch = !filters.search || app.student_name.toLowerCase().includes(searchLower);
    const matchesCursus = !filters.cursus || app.student_cursus === filters.cursus;
    const matchesParcours = !filters.parcours || app.student_parcours === filters.parcours;
    const matchesStatus = !filters.status || app.status === filters.status;
    return matchesSearch && matchesCursus && matchesParcours && matchesStatus;
  });

  if (filteredApps.length > 0) {
    return { ...internship, department_applications: filteredApps };
  }
  return null;
}).filter(Boolean);
// --- FIN DU REMPLACEMENT ---
  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Stages avec Étudiants de mon Département</h2>
  
<div className="bg-white rounded-lg shadow p-6 mb-6">
  <h3 className="text-lg font-semibold text-gray-900 mb-4">🔍 Filtres</h3>
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Recherche par nom</label>
      <input type="text" value={filters.search} onChange={(e) => handleFilterChange('search', e.target.value)} placeholder="Nom de l'étudiant..." className="w-full border border-gray-300 rounded-md px-3 py-2"/>
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Cursus</label>
      <select value={filters.cursus} onChange={(e) => handleFilterChange('cursus', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2">
        <option value="">Tous</option>
        {cursusList.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Parcours</label>
      <select value={filters.parcours} onChange={(e) => handleFilterChange('parcours', e.target.value)} disabled={!filters.cursus} className="w-full border border-gray-300 rounded-md px-3 py-2 disabled:bg-gray-100">
        <option value="">Tous</option>
        {parcoursList.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
      <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2">
        <option value="">Tous</option>
        <option value="pending">En attente</option>
        <option value="pending_academic_approval">Attente Validation</option>
        <option value="accepted">Accepté</option>
        <option value="rejected">Refusé</option>
      </select>
    </div>
  </div>
  <div className="mt-4 flex justify-end">
    <button onClick={clearFilters} className="text-gray-600 hover:text-gray-800 text-sm">Effacer les filtres</button>
  </div>
</div>



      {loading ? (
        <div className="text-center py-8">Chargement des stages...</div>
      ) : (
        <div className="space-y-6">
          {filteredInternships.map((internship) => (
            <div key={internship.id} className="bg-white rounded-lg shadow p-6">
              {/* ... (La partie supérieure de l'affichage du stage ne change pas) ... */}
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{internship.title}</h3>
                  <p className="text-lg text-blue-600 mb-2">{internship.company_name}</p>
                  <div className="flex items-center text-sm text-gray-600 mb-4">
                    <span className="mr-4">📍 {internship.location}</span>
                    <span className="mr-4">⏱️ {internship.duration_months} mois</span>
                    <span>📅 {new Date(internship.start_date).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="text-right">
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                    {internship.department_applications_count} candidature(s) département
                  </span>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-900 mb-3">Candidatures des étudiants de votre département:</h4>
                <div className="space-y-2">
                  {internship.department_applications.map((app) => (
                    <div key={app.id} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                      <div>
                        <span className="font-medium">{app.student_name}</span>
                        <span className="text-gray-600 ml-2">({app.student_cursus} - {app.student_parcours})</span>
                      </div>
                      {/* --- UTILISATION DE LA NOUVELLE FONCTION --- */}
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusInfo(app.status).className}`}>
                        {getStatusInfo(app.status).text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
          
          {filteredInternships.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600">Aucun stage avec candidatures de votre département.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const DepartmentApplicationsTab = ({ user }) => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  // --- AJOUTEZ CE BLOC ---
const [filters, setFilters] = useState({ search: '', cursus: '', parcours: '', status: '' });
const [cursusList, setCursusList] = useState([]);
const [parcoursList, setParcoursList] = useState([]);
// --- FIN DE L'AJOUT ---

  // --- REMPLACEZ LE useEffect ACTUEL PAR CELUI-CI ---
useEffect(() => {
  fetchDepartmentApplications();
  if (user && user.department) {
    fetchCursusForDept(user.department);
  }
}, [user]);
// --- FIN DU REMPLACEMENT ---

  const fetchDepartmentApplications = async () => {
    try {
      const response = await axios.get(`${API}/department/applications`);
      setApplications(response.data);
    } catch (error) {
      console.error('Error fetching department applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'En attente';
      case 'accepted': return 'Acceptée';
      case 'rejected': return 'Refusée';
      default: return status;
    }
  };
  // ===============================================
// DANS DepartmentApplicationsTab, AJOUTEZ CETTE FONCTION
// ===============================================

const handleAcademicReview = async (applicationId, newStatus) => {
  const confirmationMessage = newStatus === 'accepted'
    ? 'Voulez-vous vraiment valider cette candidature ? L\'étudiant sera notifié.'
    : 'Voulez-vous vraiment rejeter cette candidature ?';
    
  if (!window.confirm(confirmationMessage)) {
    return;
  }

  try {
    await axios.put(`${API}/applications/${applicationId}/academic-review`, null, {
      params: { status: newStatus }
    });
    alert('La candidature a été mise à jour.');
    fetchDepartmentApplications(); // Recharger les données
  } catch (error) {
    alert(error.response?.data?.detail || 'Une erreur est survenue lors de la mise à jour.');
  }
};

// --- AJOUTEZ CETTE FONCTION ---
const handleUpdateStatus = async (applicationId, newStatus, userId) => {
  const confirmationMessage = newStatus === 'accepted'
    ? 'Voulez-vous vraiment ACCEPTER cette candidature spontanée ? L\'étudiant sera notifié.'
    : 'Voulez-vous vraiment REJETER cette candidature spontanée ?';
    
  if (!window.confirm(confirmationMessage)) {
    return;
  }

  try {
    await axios.put(`${API}/applications/${applicationId}/status`, null, {
      params: { status: newStatus }
    });
    alert('Statut de la candidature mis à jour.');
    fetchDepartmentApplications(); // Recharger les données
  } catch (error) {
    alert(error.response?.data?.detail || 'Une erreur est survenue.');
  }
};
// --- FIN DE L'AJOUT ---


// --- AJOUTEZ CE BLOC DE FONCTIONS ---
const fetchCursusForDept = async (dept) => { try { const res = await axios.get(`${API}/structure/cursus?department=${dept}`); setCursusList(res.data.cursus); } catch (e) { console.error(e); }};
const fetchParcoursForCursus = async (dept, curs) => { try { const res = await axios.get(`${API}/structure/parcours?department=${dept}&cursus=${curs}`); setParcoursList(res.data.parcours); } catch (e) { console.error(e); }};

const handleFilterChange = (key, value) => {
  setFilters(prev => {
    const newFilters = { ...prev, [key]: value };

    if (key === 'cursus') {
      newFilters.parcours = '';
      setParcoursList([]);
      if (value && user && user.department) {
        fetchParcoursForCursus(user.department, value);
      }
    }
    
    return newFilters;
  });
};

const clearFilters = () => {
  setFilters({ search: '', cursus: '', parcours: '', status: '' });
  setParcoursList([]);
};

// Appliquer les filtres avant de rendre la liste
const filteredApplications = applications.filter(app => {
  if (!app) return false;
  const searchLower = filters.search.toLowerCase();
  const matchesSearch = !filters.search || app.student_name.toLowerCase().includes(searchLower);
  const matchesCursus = !filters.cursus || app.student_cursus === filters.cursus;
  const matchesParcours = !filters.parcours || app.student_parcours === filters.parcours;
  const matchesStatus = !filters.status || app.status === filters.status;
  return matchesSearch && matchesCursus && matchesParcours && matchesStatus;
});
// --- FIN DE L'AJOUT ---

  // =========================================================================
// DANS DepartmentApplicationsTab, REMPLACEZ LE BLOC return (...) PAR CELUI-CI
// =========================================================================

return (
  <div>
    <h2 className="text-2xl font-bold text-gray-900 mb-6">Candidatures des Étudiants de mon Département</h2>


<div className="bg-white rounded-lg shadow p-6 mb-6">
  <h3 className="text-lg font-semibold text-gray-900 mb-4">🔍 Filtres</h3>
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Recherche par nom</label>
      <input type="text" value={filters.search} onChange={(e) => handleFilterChange('search', e.target.value)} placeholder="Nom de l'étudiant..." className="w-full border border-gray-300 rounded-md px-3 py-2"/>
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Cursus</label>
      <select value={filters.cursus} onChange={(e) => handleFilterChange('cursus', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2">
        <option value="">Tous</option>
        {cursusList.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Parcours</label>
      <select value={filters.parcours} onChange={(e) => handleFilterChange('parcours', e.target.value)} disabled={!filters.cursus} className="w-full border border-gray-300 rounded-md px-3 py-2 disabled:bg-gray-100">
        <option value="">Tous</option>
        {parcoursList.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
      <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2">
        <option value="">Tous</option>
        <option value="pending">En attente</option>
        <option value="pending_academic_approval">Attente Validation</option>
        <option value="accepted">Accepté</option>
        <option value="rejected">Refusé</option>
      </select>
    </div>
  </div>
  <div className="mt-4 flex justify-end">
    <button onClick={clearFilters} className="text-gray-600 hover:text-gray-800 text-sm">Effacer les filtres</button>
  </div>
</div>


    {loading ? (
      <div className="text-center py-8">Chargement des candidatures...</div>
    ) : (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Étudiant
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Stage
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Statut
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              {/* --- NOUVELLE COLONNE --- */}
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredApplications.map((app) => (
              <tr key={app.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{app.student_name}</div>
                         <div className="text-xs text-blue-600 mt-1">
                          {app.student_cursus} {app.student_parcours && ` - ${app.student_parcours}`}
                      </div>
                    <div className="text-sm text-gray-500">{app.student_email}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{app.internship_title}</div>
                  <div className="text-sm text-gray-500">{app.company_name}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {/* --- LOGIQUE DE STATUT MISE À JOUR --- */}
                  <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(app.status)}`}>
                    {app.status === 'pending_academic_approval' 
                      ? 'Attente Validation' 
                      : getStatusText(app.status)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {new Date(app.applied_at).toLocaleDateString()}
                </td>

<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
  {/* Cas 1: L'encadreur doit valider après l'accord de l'employeur */}
  {app.status === 'pending_academic_approval' && (
    <div className="flex justify-end space-x-2">
      <button
        onClick={() => handleAcademicReview(app.id, 'accepted')}
        className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs"
      >
        Valider
      </button>
      <button
        onClick={() => handleAcademicReview(app.id, 'rejected')}
        className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs"
      >
        Rejeter
      </button>
    </div>
  )}

  {/* Cas 2: C'est un stage spontané (en attente ET posté par l'encadreur actuel) */}
  {app.status === 'pending' && app.internship_posted_by === user.id && (
      <div className="flex justify-end space-x-2">
      <button
        onClick={() => handleUpdateStatus(app.id, 'accepted')}
        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs"
      >
        Accepter
      </button>
      <button
        onClick={() => handleUpdateStatus(app.id, 'rejected')}
        className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs"
      >
        Rejeter
      </button>
    </div>
  )}
</td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredApplications.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600">Aucune candidature trouvée pour les étudiants de votre département.</p>
          </div>
        )}
      </div>
    )}
  </div>
);
};
// New Components for Advanced Features

// External Internship Submission for Students
const ExternalInternshipTab = () => {
  const [formData, setFormData] = useState({
    title: '',
    company_name: '',
    company_contact_name: '',
    company_contact_email: '',
    company_contact_phone: '',
    description: '',
    location: '',
    duration_months: 3,
    start_date: ''
  });
  const [loading, setLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/external-internships`);
      setSubmissions(response.data);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submissionData = {
        ...formData,
        start_date: new Date(formData.start_date).toISOString(),
      };

      await axios.post(`${API}/external-internships`, submissionData);
      alert('Stage externe soumis pour validation avec succès!');
      
      // Reset form
      setFormData({
        title: '',
        company_name: '',
        company_contact_name: '',
        company_contact_email: '',
        company_contact_phone: '',
        description: '',
        location: '',
        duration_months: 3,
        start_date: ''
      });
      
      fetchSubmissions(); // Refresh list
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted': return 'bg-blue-100 text-blue-800';
      case 'under_review': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'submitted': return 'Soumis';
      case 'under_review': return 'En cours de révision';
      case 'approved': return 'Approuvé';
      case 'rejected': return 'Rejeté';
      default: return status;
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Soumettre un Stage Externe</h2>
      
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-medium text-blue-900 mb-2">ℹ️ Stage externe</h3>
        <p className="text-blue-700 text-sm">
          Vous avez trouvé un stage en dehors de la plateforme ? Soumettez-le ici pour validation par votre encadreur académique.
          Une fois approuvé, ce stage sera officiellement reconnu par l'IUT.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Nouveau Stage Externe</h3>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Titre du stage *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom de l'entreprise *
              </label>
              <input
                type="text"
                value={formData.company_name}
                onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom du contact entreprise *
              </label>
              <input
                type="text"
                value={formData.company_contact_name}
                onChange={(e) => setFormData({...formData, company_contact_name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email du contact *
              </label>
              <input
                type="email"
                value={formData.company_contact_email}
                onChange={(e) => setFormData({...formData, company_contact_email: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Téléphone du contact *
              </label>
              <input
                type="tel"
                value={formData.company_contact_phone}
                onChange={(e) => setFormData({...formData, company_contact_phone: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Localisation *
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Durée (mois) *
              </label>
              <input
                type="number"
                value={formData.duration_months}
                onChange={(e) => setFormData({...formData, duration_months: parseInt(e.target.value)})}
                min="1"
                max="12"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date de début *
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description du stage *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows="4"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md disabled:opacity-50"
          >
            {loading ? 'Soumission en cours...' : 'Soumettre pour validation'}
          </button>
        </form>
      </div>

      {/* Existing submissions */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Mes Soumissions</h3>
        <div className="space-y-4">
          {submissions.map((submission) => (
            <div key={submission.id} className="bg-white rounded-lg shadow p-4">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{submission.title}</h4>
                  <p className="text-gray-600">{submission.company_name}</p>
                  <p className="text-sm text-gray-500">
                    Soumis le {new Date(submission.submitted_at).toLocaleDateString()}
                  </p>
                  {submission.rejection_reason && (
                    <p className="text-sm text-red-600 mt-2">
                      Raison du rejet: {submission.rejection_reason}
                    </p>
                  )}
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(submission.status)}`}>
                  {getStatusText(submission.status)}
                </span>
              </div>
            </div>
          ))}
          
          {submissions.length === 0 && (
            <p className="text-gray-600 text-center py-8">Aucune soumission de stage externe.</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Documents Management for Students
const MyDocumentsTab = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    category: 'cv',
    description: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        return;
      }

      const response = await axios.get(`${API}/documents`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      if (error.response?.status === 403) {
        alert('Erreur d\'authentification. Veuillez vous reconnecter.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier la taille (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Le fichier est trop volumineux. Taille maximale : 5MB');
        return;
      }

      // Vérifier le type de fichier
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain', 'image/jpeg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        alert('Type de fichier non supporté. Formats acceptés : PDF, DOC, DOCX, TXT, JPG, PNG, GIF');
        return;
      }

      setSelectedFile(file);
    }
  };

  const uploadDocument = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      alert('Veuillez sélectionner un fichier');
      return;
    }

    setUploading(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Erreur d\'authentification. Veuillez vous reconnecter.');
        return;
      }

      // Créer FormData pour l'upload
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('category', uploadForm.category);
      formData.append('description', uploadForm.description);

      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      alert('Document téléversé avec succès!');
      setUploadForm({ category: 'cv', description: '' });
      setSelectedFile(null);
      
      // Reset file input
      const fileInput = document.getElementById('file-input');
      if (fileInput) {
        fileInput.value = '';
      }
      
      fetchDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      if (error.response?.status === 403) {
        alert('Erreur d\'authentification. Veuillez vous reconnecter.');
      } else if (error.response?.status === 413) {
        alert('Le fichier est trop volumineux. Taille maximale : 5MB');
      } else {
        alert('Erreur lors du téléversement');
      }
    } finally {
      setUploading(false);
    }
  };

  const downloadDocument = async (documentId, filename) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Erreur d\'authentification. Veuillez vous reconnecter.');
        return;
      }

      const response = await axios.get(`${API}/files/${documentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading document:', error);
      if (error.response?.status === 403) {
        alert('Erreur d\'authentification. Veuillez vous reconnecter.');
      } else {
        alert('Erreur lors du téléchargement');
      }
    }
  };

  const getCategoryDisplayName = (category) => {
    const categories = {
      cv: 'CV',
      cover_letter: 'Lettre de motivation',
      transcript: 'Relevé de notes',
      internship_report: 'Rapport de stage',
      recommendation: 'Lettre de recommandation',
      other: 'Autre'
    };
    return categories[category] || category;
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Mes Documents</h2>

      {/* Upload Form */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Téléverser un Document</h3>
        
        <form onSubmit={uploadDocument} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Sélecteur de fichier */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fichier *
              </label>
              <div className="flex items-center space-x-3">
                <input
                  id="file-input"
                  type="file"
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif"
                  className="hidden"
                />
                <label
                  htmlFor="file-input"
                  className="cursor-pointer bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-md px-4 py-2 text-sm font-medium text-gray-700 flex items-center space-x-2"
                >
                  <span>📁</span>
                  <span>Choisir un fichier</span>
                </label>
                {selectedFile && (
                  <span className="text-sm text-gray-600 flex items-center space-x-2">
                    <span>✅</span>
                    <span>{selectedFile.name}</span>
                    <span className="text-xs text-gray-400">({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Formats acceptés : PDF, DOC, DOCX, TXT, JPG, PNG, GIF (max 5MB)
              </p>
            </div>

            {/* Catégorie */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Catégorie *
              </label>
              <select
                value={uploadForm.category}
                onChange={(e) => setUploadForm({...uploadForm, category: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="cv">CV</option>
                <option value="cover_letter">Lettre de motivation</option>
                <option value="transcript">Relevé de notes</option>
                <option value="internship_report">Rapport de stage</option>
                <option value="recommendation">Lettre de recommandation</option>
                <option value="other">Autre</option>
              </select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description (optionnel)
            </label>
            <textarea
              value={uploadForm.description}
              onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
              placeholder="Ajoutez une description pour ce document..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <button
            type="submit"
            disabled={!selectedFile || uploading}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-md flex items-center space-x-2"
          >
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Téléversement...</span>
              </>
            ) : (
              <>
                <span>📤</span>
                <span>Téléverser</span>
              </>
            )}
          </button>
        </form>
      </div>

      {/* Documents List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Documents Téléversés</h3>
        </div>
        
        {loading ? (
          <div className="text-center py-8">Chargement des documents...</div>
        ) : (
          <div className="divide-y divide-gray-200">
            {documents.map((doc) => (
              <div key={doc.id} className="px-6 py-4 flex justify-between items-center">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{doc.original_filename}</h4>
                  <div className="flex items-center text-sm text-gray-500">
                    <span className="mr-4">{getCategoryDisplayName(doc.category)}</span>
                    <span className="mr-4">{doc.file_type.toUpperCase()}</span>
                    <span>Téléversé le {new Date(doc.uploaded_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <button
                  onClick={() => downloadDocument(doc.id, doc.original_filename)}
                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm"
                >
                  Télécharger
                </button>
              </div>
            ))}
            
            {documents.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-600">Aucun document téléversé.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// External Internships Review for Academic Supervisors
const ExternalInternshipsReviewTab = () => {
  const [externals, setExternals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchExternals();
  }, []);

  const fetchExternals = async () => {
    try {
      const response = await axios.get(`${API}/external-internships`);
      setExternals(response.data);
    } catch (error) {
      console.error('Error fetching external internships:', error);
    } finally {
      setLoading(false);
    }
  };

  const reviewExternal = async (externalId, status, rejectionReason = '') => {
    try {
      const params = new URLSearchParams();
      params.append('status', status);
      if (rejectionReason) {
        params.append('rejection_reason', rejectionReason);
      }

      await axios.put(`${API}/external-internships/${externalId}/review?${params.toString()}`);
      alert(`Stage externe ${status === 'approved' ? 'approuvé' : 'rejeté'} avec succès!`);
      fetchExternals(); // Refresh list
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la révision');
    }
  };

  const handleReject = (externalId) => {
    const reason = prompt('Raison du rejet (optionnelle):');
    if (reason !== null) { // User didn't cancel
      reviewExternal(externalId, 'rejected', reason);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted': return 'bg-blue-100 text-blue-800';
      case 'under_review': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'submitted': return 'Soumis';
      case 'under_review': return 'En cours de révision';
      case 'approved': return 'Approuvé';
      case 'rejected': return 'Rejeté';
      default: return status;
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Stages Externes à Valider</h2>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-medium text-blue-900 mb-2">📋 Validation des stages externes</h3>
        <p className="text-blue-700 text-sm">
          Validez les stages externes soumis par les étudiants de votre département. 
          Une fois approuvés, ces stages seront officiellement reconnus par l'IUT.
        </p>
      </div>

      {loading ? (
        <div className="text-center py-8">Chargement des stages externes...</div>
      ) : (
        <div className="space-y-6">
          {externals.map((external) => (
            <div key={external.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {external.title}
                  </h3>
                  <p className="text-lg text-blue-600 mb-2">{external.company_name}</p>
                  <div className="flex items-center text-sm text-gray-600 mb-4">
                    <span className="mr-4">📍 {external.location}</span>
                    <span className="mr-4">⏱️ {external.duration_months} mois</span>
                    <span className="mr-4">📅 Début: {new Date(external.start_date).toLocaleDateString()}</span>
                    <span className="mr-4">📝 Soumis: {new Date(external.submitted_at).toLocaleDateString()}</span>
                  </div>
                </div>
                
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(external.status)}`}>
                  {getStatusText(external.status)}
                </span>
              </div>
              {external.student_info && (
                <div className="mb-4 p-4 border rounded-md bg-gray-50">
                  <h4 className="font-medium text-gray-900 mb-2">Soumis par l'étudiant :</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <p><strong>Nom :</strong> {external.student_info.name}</p>
                    <p><strong>Téléphone :</strong> {external.student_info.phone || 'Non fourni'}</p>
                    <p><strong>Département :</strong> {external.student_info.student_department}</p>
                    <p><strong>Cursus :</strong> {external.student_info.student_cursus}</p>
                    <p className="sm:col-span-2"><strong>Parcours :</strong> {external.student_info.student_parcours}</p>
                  </div>
                </div>
              )}

              <div className="mb-4">
                <h4 className="font-medium text-gray-900 mb-2">Description:</h4>
                <p className="text-gray-700 bg-gray-50 p-3 rounded">{external.description}</p>
              </div>

              <div className="mb-4">
                <h4 className="font-medium text-gray-900 mb-2">Contact Entreprise:</h4>
                <div className="bg-gray-50 p-3 rounded">
                  <p><strong>Nom:</strong> {external.company_contact_name}</p>
                  <p><strong>Email:</strong> {external.company_contact_email}</p>
                  <p><strong>Téléphone:</strong> {external.company_contact_phone}</p>
                </div>
              </div>

              {external.rejection_reason && (
                <div className="mb-4">
                  <h4 className="font-medium text-red-900 mb-2">Raison du rejet:</h4>
                  <p className="text-red-700 bg-red-50 p-3 rounded">{external.rejection_reason}</p>
                </div>
              )}

              {external.status === 'submitted' && (
                <div className="flex space-x-3">
                  <button
                    onClick={() => reviewExternal(external.id, 'approved')}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium"
                  >
                    Approuver
                  </button>
                  <button
                    onClick={() => handleReject(external.id)}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md font-medium"
                  >
                    Rejeter
                  </button>
                  <button
                    onClick={() => reviewExternal(external.id, 'under_review')}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-md font-medium"
                  >
                    Mettre en révision
                  </button>
                </div>
              )}
            </div>
          ))}
          
          {externals.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600">Aucun stage externe soumis pour votre département.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AdminExternalInternshipsTab = () => {
  const [externals, setExternals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // États pour les filtres
  const [filters, setFilters] = useState({ search: '', department: '', cursus: '', parcours: '', status: '' });
  const [departments, setDepartments] = useState([]);
  const [cursusList, setCursusList] = useState([]);
  const [parcoursList, setParcoursList] = useState([]);

  useEffect(() => {
    fetchExternals();
    fetchDepartmentsForFilter();
  }, []);
  
  const fetchExternals = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/external-internships`);
      setExternals(response.data);
    } catch (error) {
      console.error('Error fetching external internships:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartmentsForFilter = async () => {
      try {
        const response = await axios.get(`${API}/structure/departments`);
        setDepartments(response.data.departments);
      } catch (error) { console.error("Erreur chargement départements:", error); }
  };
  
  const handleFilterChange = async (name, value) => {
    const newFilters = { ...filters, [name]: value };

    if (name === 'department') {
      newFilters.cursus = '';
      newFilters.parcours = '';
      setCursusList([]);
      setParcoursList([]);
      if (value) {
        try {
          const response = await axios.get(`${API}/structure/cursus?department=${value}`);
          setCursusList(response.data.cursus);
        } catch (error) { console.error("Erreur chargement cursus:", error); }
      }
    } else if (name === 'cursus') {
      newFilters.parcours = '';
      setParcoursList([]);
      if (value && filters.department) {
        try {
          const response = await axios.get(`${API}/structure/parcours?department=${filters.department}&cursus=${value}`);
          setParcoursList(response.data.parcours);
        } catch (error) { console.error("Erreur chargement parcours:", error); }
      }
    }
    setFilters(newFilters);
  };
  
  const resetFilters = () => {
    setFilters({ search: '', department: '', cursus: '', parcours: '', status: '' });
    setCursusList([]);
    setParcoursList([]);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
        // --- DÉBUT DE LA CORRECTION ---
        const params = new URLSearchParams();
        if (filters.search) params.append('search', filters.search);
        if (filters.department) params.append('department', filters.department);
        if (filters.cursus) params.append('cursus', filters.cursus);
        if (filters.parcours) params.append('parcours', filters.parcours);
        if (filters.status) params.append('status', filters.status);
        
        const url = `${API}/admin/export/external-internships?${params.toString()}`;
        // --- FIN DE LA CORRECTION ---
        
        const token = localStorage.getItem('token');
        const response = await axios.get(url, { // On utilise la nouvelle URL avec les filtres
            headers: { 'Authorization': `Bearer ${token}` },
            responseType: 'blob',
        });

        // ... (le reste de la logique de téléchargement ne change pas)
        const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', 'export_stages_externes.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        
    } catch (error) {
        alert("Erreur lors de l'exportation.");
        console.error("Export error:", error);
    } finally {
        setExporting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted': return 'bg-blue-100 text-blue-800';
      case 'under_review': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'submitted': return 'Soumis';
      case 'under_review': return 'En révision';
      case 'approved': return 'Approuvé';
      case 'rejected': return 'Rejeté';
      default: return status;
    }
  };

  const reviewExternal = async (externalId, status, rejectionReason = '') => {
    try {
      const params = new URLSearchParams();
      params.append('status', status);
      if (rejectionReason) {
        params.append('rejection_reason', rejectionReason);
      }

      await axios.put(`${API}/external-internships/${externalId}/review?${params.toString()}`);
      alert(`Stage externe mis à jour avec le statut : ${status}`);
      fetchExternals(); // Recharger la liste pour voir le changement
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleReject = (externalId) => {
    const reason = prompt("Veuillez fournir une raison pour le rejet (cette raison sera visible par l'étudiant) :");
    if (reason !== null) { // L'utilisateur n'a pas cliqué sur "Annuler"
      reviewExternal(externalId, 'rejected', reason);
    }
  };

  // Logique de filtrage du frontend
  const filteredExternals = externals.filter(ext => {
      const student = ext.student_info;
      const searchLower = filters.search.toLowerCase();

      const matchesSearch = !filters.search ||
          ext.title.toLowerCase().includes(searchLower) ||
          ext.company_name.toLowerCase().includes(searchLower) ||
          (student && student.name.toLowerCase().includes(searchLower));
      
      const matchesStatus = !filters.status || ext.status === filters.status;
      const matchesDepartment = !filters.department || (student && student.student_department === filters.department);
      const matchesCursus = !filters.cursus || (student && student.student_cursus === filters.cursus);
      const matchesParcours = !filters.parcours || (student && student.student_parcours === filters.parcours);
      
      return matchesSearch && matchesStatus && matchesDepartment && matchesCursus && matchesParcours;
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Gestion des Stages Externes</h2>
        <button
          onClick={handleExport}
          disabled={exporting}
          className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md disabled:opacity-50 flex items-center"
        >
          {exporting ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
          ) : (
            <span className="mr-2">📥</span>
          )}
          {exporting ? 'Exportation...' : 'Exporter en Excel'}
        </button>
      </div>

      {/* Section des filtres */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">🔍 Filtres</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Recherche</label>
            <input type="text" value={filters.search} onChange={(e) => handleFilterChange('search', e.target.value)} placeholder="Titre, entreprise, étudiant..." className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Département</label>
            <select value={filters.department} onChange={(e) => handleFilterChange('department', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                <option value="">Tous</option>
                {departments.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cursus</label>
            <select value={filters.cursus} onChange={(e) => handleFilterChange('cursus', e.target.value)} disabled={!filters.department} className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-100">
                <option value="">Tous</option>
                {cursusList.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Parcours</label>
            <select value={filters.parcours} onChange={(e) => handleFilterChange('parcours', e.target.value)} disabled={!filters.cursus} className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-100">
                <option value="">Tous</option>
                {parcoursList.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">État</label>
            <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                <option value="">Tous</option>
                <option value="submitted">Soumis</option>
                <option value="under_review">En révision</option>
                <option value="approved">Approuvé</option>
                <option value="rejected">Rejeté</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end">
            <button onClick={resetFilters} className="text-sm text-blue-600 hover:underline">Réinitialiser les filtres</button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Chargement des stages...</p>
        </div>
      ) : (
        <div className="space-y-6">
          {filteredExternals.map((external) => (
            <div key={external.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{external.title}</h3>
                  <p className="text-lg text-blue-600 mb-2">{external.company_name}</p>
                  <div className="flex flex-wrap items-center text-sm text-gray-600 gap-x-4 gap-y-1">
                    <span>📍 {external.location}</span>
                    <span>⏱️ {external.duration_months} mois</span>
                    <span>📅 Début: {new Date(external.start_date).toLocaleDateString()}</span>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(external.status)}`}>
                  {getStatusText(external.status)}
                </span>
              </div>

              {external.student_info && (
                <div className="mb-4 p-4 border rounded-md bg-gray-50">
                  <h4 className="font-medium text-gray-900 mb-2">Soumis par l'étudiant :</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <p><strong>Nom :</strong> {external.student_info.name}</p>
                    <p><strong>Téléphone :</strong> {external.student_info.phone || 'Non fourni'}</p>
                    <p><strong>Département :</strong> {external.student_info.student_department}</p>
                    <p><strong>Cursus :</strong> {external.student_info.student_cursus}</p>
                    <p className="sm:col-span-2"><strong>Parcours :</strong> {external.student_info.student_parcours}</p>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <h4 className="font-medium text-gray-900 mb-2">Description du stage :</h4>
                    <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded h-full">{external.description}</p>
                </div>
                <div>
                    <h4 className="font-medium text-gray-900 mb-2">Contact Entreprise :</h4>
                    <div className="bg-gray-50 p-3 rounded text-sm h-full">
                        <p><strong>Nom :</strong> {external.company_contact_name}</p>
                        <p><strong>Email :</strong> {external.company_contact_email}</p>
                        <p><strong>Téléphone :</strong> {external.company_contact_phone}</p>
                    </div>
                </div>
              </div>

              {external.rejection_reason && (
                <div className="mt-4">
                  <h4 className="font-medium text-red-900 mb-2">Raison du rejet :</h4>
                  <p className="text-sm text-red-700 bg-red-50 p-3 rounded">{external.rejection_reason}</p>
                </div>
              )}
              {(external.status === 'submitted' || external.status === 'under_review') && (
    <div className="mt-6 pt-4 border-t border-gray-200 flex items-center space-x-3">
        <span className="text-sm font-medium text-gray-700">Actions :</span>
        <button
            onClick={() => reviewExternal(external.id, 'approved')}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium"
        >
            Approuver
        </button>
        <button
            onClick={() => handleReject(external.id)}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
        >
            Rejeter
        </button>
        {/* Le bouton "Mettre en révision" n'a de sens que si le statut est "Soumis" */}
        {external.status === 'submitted' && (
            <button
                onClick={() => reviewExternal(external.id, 'under_review')}
                className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
                Mettre en révision
            </button>
        )}
    </div>
)}
            </div>
          ))}
          

          {filteredExternals.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <p className="text-gray-600">Aucun stage externe ne correspond à vos filtres.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AdminProAssignmentTab = () => {
  // --- STATES ---
  const [assignments, setAssignments] = useState([]);
  const [allUsersRes, setAllUsesR] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [allProSupervisors, setAllProSupervisors] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false); // Prêt pour le futur
  const [editingAssignment, setEditingAssignment] = useState(null); // Prêt pour le futur
  
  const [newAssignment, setNewAssignment] = useState({
    student_id: '',
    professional_supervisor_id: ''
  });
  
  // Filtres (Identiques à la version académique)
  const [filters, setFilters] = useState({ department: '', cursus: '', parcours: '', search: '',  supervisorSearch: '' });
  const [studentModalFilters, setStudentModalFilters] = useState({ department: '', cursus: '', parcours: '', search: '' });
  const [supervisorModalFilters, setSupervisorModalFilters] = useState({ search: '' });
  const [departments, setDepartments] = useState([]);
  const [cursus, setCursus] = useState([]);
  const [parcours, setParcours] = useState([]);

  // --- FONCTIONS DE RÉCUPÉRATION ---
  useEffect(() => {
    fetchAllData();
    fetchDepartments();
  }, []);

const fetchAllData = async () => {
  setLoading(true);
  try {
    const [assignmentsRes, usersRes] = await Promise.all([
      axios.get(`${API}/pro-assignments`), // Récupère les affectations existantes
      axios.get(`${API}/admin/users`)      // Récupère TOUS les utilisateurs
    ]);
    
    const currentAssignments = assignmentsRes.data.assignments || [];
    const allUsers = usersRes.data || [];

    setAssignments(currentAssignments);
    setAllStudents(allUsers.filter(u => u.role === 'student'));
    setAllProSupervisors(allUsers.filter(u => u.role === 'professional_supervisor'));

    // Recalculer les stats côté client pour être sûr
    const assignedStudentIds = new Set(currentAssignments.map(a => a.student_id));
    const unassignedCount = allUsers.filter(u => u.role === 'student' && !assignedStudentIds.has(u.id)).length;
    setStats({
      assigned_students: currentAssignments.length,
      unassigned_students: unassignedCount,
      active_supervisors: allUsers.filter(u => u.role === 'professional_supervisor').length
    });

  } catch (error) {
    console.error("Failed to fetch data:", error);
  } finally {
    setLoading(false);
  }
};
  // --- FONCTIONS POUR LES FILTRES (Copiées à l'identique) ---
  const fetchDepartments = async () => { try { const res = await axios.get(`${API}/structure/departments`); setDepartments(res.data.departments); } catch (e) { console.error(e); }};
  const fetchCursus = async (d) => { try { const res = await axios.get(`${API}/structure/cursus?department=${d}`); setCursus(res.data.cursus); } catch (e) { console.error(e); }};
  const fetchParcours = async (d, c) => { try { const res = await axios.get(`${API}/structure/parcours?department=${d}&cursus=${c}`); setParcours(res.data.parcours); } catch (e) { console.error(e); }};
  const handleDepartmentChange = (d) => { setFilters({ ...filters, department: d, cursus: '', parcours: '' }); if (d) fetchCursus(d); };
  const handleCursusChange = (c) => { setFilters({ ...filters, cursus: c, parcours: '' }); if (c) fetchParcours(filters.department, c); };
  const clearFilters = () => { setFilters({ department: '', cursus: '', parcours: '', search: '' , supervisorSearch: '' }); };
  
  // --- FONCTIONS D'ACTION ---
const createAssignment = async () => {
  if (!newAssignment.student_id || !newAssignment.professional_supervisor_id) return;
  try {
    // On envoie un objet JSON simple, sans FormData
    await axios.post(`${API}/pro-assignments`, {
      student_id: newAssignment.student_id,
      professional_supervisor_id: newAssignment.professional_supervisor_id
    });
    
    alert('Affectation créée avec succès !');
    setShowCreateModal(false);
    setNewAssignment({ student_id: '', professional_supervisor_id: '' });
    fetchAllData();
  } catch (error) {
    const errorMessage = error.response?.data?.detail || 'Une erreur est survenue lors de la création.';
    alert(`Erreur : ${errorMessage}`);
    console.error('Error creating assignment:', error.response || error);
  }
};
  
  const deleteAssignment = async (assignmentId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette affectation ?")) return;
    try {
      await axios.delete(`${API}/pro-assignments/${assignmentId}`);
      alert('Affectation supprimée !');
      fetchAllData();
    } catch (error) { alert('Erreur lors de la suppression.'); }
  };

const fetchProSupervisorsForCompany = async (companyName) => {
  if (!companyName) {
    setProSupervisors([]);
    return;
  }
  try {
    const response = await axios.get(`${API}/users/supervisors-by-company/${companyName}`);
    setProSupervisors(response.data || []);
  } catch (error) {
    console.error('Error fetching pro supervisors:', error);
    setProSupervisors([]);
  }
};

const openEditModal = (assignment) => {
  setEditingAssignment(assignment);
  setNewAssignment({
    student_id: assignment.student_id, // Important: utiliser le student_id de l'affectation
    professional_supervisor_id: assignment.professional_supervisor_id
  });
  setShowEditModal(true);
};


  
  // --- LOGIQUE DE FILTRAGE ---
  const assignedStudentIds = new Set(assignments.map(a => a.student_id));
  const unassignedStudents = allStudents.filter(s => !assignedStudentIds.has(s.id));

  const unassignedStudentsFiltered = unassignedStudents.filter(student => {
    const searchLower = filters.search.toLowerCase();
    const matchesSearch = !filters.search || student.name.toLowerCase().includes(searchLower) || student.email.toLowerCase().includes(searchLower);
    const matchesDepartment = !filters.department || student.student_department === filters.department;
    const matchesCursus = !filters.cursus || student.student_cursus === filters.cursus;
    const matchesParcours = !filters.parcours || student.student_parcours === filters.parcours;
    return matchesSearch && matchesDepartment && matchesCursus && matchesParcours;
  });

  // Filtrer les affectations existantes pour le tableau principal
// Filtrer les affectations existantes pour le tableau principal
const filteredAssignments = assignments.filter(assignment => {
  // Filtre de recherche générale (sur l'étudiant)
  const searchLower = filters.search.toLowerCase();
  const matchesStudentSearch = !filters.search || 
    (assignment.student_name && assignment.student_name.toLowerCase().includes(searchLower)) ||
    (assignment.student_email && assignment.student_email.toLowerCase().includes(searchLower));

  // Nouveau filtre de recherche (sur l'encadreur ou l'entreprise)
  const supervisorSearchLower = filters.supervisorSearch.toLowerCase();
  const matchesSupervisorSearch = !filters.supervisorSearch ||
    (assignment.professional_supervisor_name && assignment.professional_supervisor_name.toLowerCase().includes(supervisorSearchLower)) ||
    (assignment.company_name && assignment.company_name.toLowerCase().includes(supervisorSearchLower));

  // Filtres académiques (sur l'étudiant)
  const matchesDepartment = !filters.department || assignment.student_department === filters.department;
  const matchesCursus = !filters.cursus || assignment.student_cursus === filters.cursus;
  const matchesParcours = !filters.parcours || assignment.student_parcours === filters.parcours;
  
  return matchesStudentSearch && matchesSupervisorSearch && matchesDepartment && matchesCursus && matchesParcours;
});

const unassignedStudentsForModal = unassignedStudents.filter(student => {
  const f = studentModalFilters;
  const searchLower = f.search.toLowerCase();
  return (
    (!f.search || (student.name && student.name.toLowerCase().includes(searchLower)) || (student.email && student.email.toLowerCase().includes(searchLower))) &&
    (!f.department || student.student_department === f.department) &&
    (!f.cursus || student.student_cursus === f.cursus) &&
    (!f.parcours || student.student_parcours === f.parcours)
  );
});

const filteredProSupervisorsForModal = allProSupervisors.filter(supervisor => {
  const f = supervisorModalFilters;
  const searchLower = f.search.toLowerCase();
  return !f.search || (supervisor.name && supervisor.name.toLowerCase().includes(searchLower));
});

const clearModalFilters = () => {
  setStudentModalFilters({ department: '', cursus: '', parcours: '', search: '' });
  setSupervisorModalFilters({ search: '' });
};

const filteredProSupervisors = allProSupervisors.filter(supervisor => {
  const searchLower = filters.supervisorSearch.toLowerCase();
  // Si le champ de recherche est vide, on affiche tous les encadreurs
  if (!searchLower) {
    return true;
  }
  // Sinon, on vérifie si le nom de l'encadreur OU le nom de l'entreprise correspond
  const matchesName = supervisor.name.toLowerCase().includes(searchLower);
  const matchesCompany = supervisor.company_name && supervisor.company_name.toLowerCase().includes(searchLower);
  
  return matchesName || matchesCompany;
});
const updateAssignment = async () => {
  if (!editingAssignment || !newAssignment.professional_supervisor_id) return;
  try {
    // On appelle la nouvelle route PUT avec les bonnes données
    await axios.put(
      `${API}/pro-assignments/${editingAssignment.id}`,
      { 
        // Le backend a besoin du student_id même s'il ne change pas, selon le modèle ProAssignmentCreate
        student_id: editingAssignment.student_id, 
        professional_supervisor_id: newAssignment.professional_supervisor_id 
      }
    );
    
    alert('Affectation mise à jour !');
    setShowEditModal(false);
    fetchAllData(); // Recharger les données pour voir le changement
  } catch (error) {
    const errorMessage = error.response?.data?.detail || 'Erreur lors de la mise à jour.';
    alert(`Erreur : ${errorMessage}`);
  }
};
  if (loading) { return <div>Chargement...</div>; }

  // --- AFFICHAGE (JSX CLONÉ) ---
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Affectation des Encadreurs Professionnels</h2>
          <p className="text-gray-600">Associez un étudiant à un encadreur professionnel.</p>
        </div>
        <div className="flex space-x-3">
          <button onClick={fetchAllData} className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-lg flex items-center space-x-2"><span>🔄</span><span>Actualiser</span></button>
          <button onClick={() => setShowCreateModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"><span>➕</span><span>Nouvelle Affectation</span></button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">🔍 Filtres</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-2">Recherche</label><input type="text" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} placeholder="Nom étudiant, email, encadreur..." className="w-full border border-gray-300 rounded-md px-3 py-2"/></div>
          <div> <label className="block text-sm font-medium text-gray-700 mb-2">Recherche Encadreur/Entreprise</label>   <input   type="text"  value= {filters.supervisorSearch}   onChange={(e) => setFilters({ ...filters, supervisorSearch: e.target.value })}  placeholder="Nom encadreur ou entreprise..."    className="w-full border border-gray-300 rounded-md px-3 py-2"/> </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-2">Département</label><select value={filters.department} onChange={(e) => handleDepartmentChange(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2"><option value="">Tous les départements</option>{departments.map((d) => <option key={d} value={d}>{d}</option>)}</select></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-2">Cursus</label><select value={filters.cursus} onChange={(e) => handleCursusChange(e.target.value)} disabled={!filters.department} className="w-full border border-gray-300 rounded-md px-3 py-2 disabled:bg-gray-100"><option value="">Tous les cursus</option>{cursus.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-2">Parcours</label><select value={filters.parcours} onChange={(e) => setFilters({ ...filters, parcours: e.target.value })} disabled={!filters.cursus} className="w-full border border-gray-300 rounded-md px-3 py-2 disabled:bg-gray-100"><option value="">Tous les parcours</option>{parcours.map((p) => <option key={p} value={p}>{p}</option>)}</select></div>
        </div>
        <div className="mt-4 flex justify-end"><button onClick={clearFilters} className="text-gray-600 hover:text-gray-800 text-sm">Effacer les filtres</button></div>
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-6">
        <div className="bg-white rounded-lg shadow p-3 sm:p-6"><p className="text-xs sm:text-sm text-gray-500">Étudiants assignés</p><p className="text-lg sm:text-2xl font-bold text-green-600">{assignments.length}</p></div>
        <div className="bg-white rounded-lg shadow p-3 sm:p-6"><p className="text-xs sm:text-sm text-gray-500">Étudiants non assignés</p><p className="text-lg sm:text-2xl font-bold text-orange-600">{unassignedStudents.length}</p></div>
        <div className="bg-white rounded-lg shadow p-3 sm:p-6"><p className="text-xs sm:text-sm text-gray-500">Encadreurs Pro actifs</p><p className="text-lg sm:text-2xl font-bold text-blue-600">{allProSupervisors.length}</p></div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200"><h3 className="text-lg font-semibold text-gray-900">Affectations ({filteredAssignments.length})</h3></div>
        
{filteredAssignments.length === 0 ? (
  <div className="p-8 text-center">
    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
      <span className="text-gray-400 text-2xl">🧑‍💼</span>
    </div>
    <h3 className="text-lg font-medium text-gray-900">
      {filters.search || filters.department || filters.cursus || filters.parcours 
        ? 'Aucune affectation ne correspond à vos filtres' 
        : 'Aucune affectation professionnelle pour le moment'}
    </h3>
    <p className="text-gray-500 mt-2">
      {filters.search || filters.department || filters.cursus || filters.parcours 
        ? "Essayez d'ajuster vos critères de recherche." 
        : 'Cliquez sur "Nouvelle Affectation" pour commencer.'}
    </p>
  </div>
) : (
  <div className="overflow-x-auto">
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Étudiant</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Département/Cursus</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entreprise</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Encadreur Professionnel</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date d'Affectation</th>
          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {filteredAssignments.map((a) => (
          <tr key={a.id} className="hover:bg-gray-50">
            <td className="px-6 py-4">
              <div className="font-medium">{a.student_name}</div>
              <div className="text-sm text-gray-500">{a.student_email}</div>
            </td>
            <td className="px-6 py-4">
              <div className="text-sm text-gray-900">{a.student_department || 'N/A'}</div>
              <div className="text-sm text-gray-500">{a.student_cursus || 'N/A'} • {a.student_parcours || 'N/A'}</div>
            </td>
            <td className="px-6 py-4 text-sm text-gray-900">{a.company_name}</td>
            <td className="px-6 py-4">
              <div className="font-medium">{a.professional_supervisor_name}</div>
              <div className="text-sm text-gray-500">{a.professional_supervisor_email}</div>
            </td>
            <td className="px-6 py-4 text-sm text-gray-900">{new Date(a.assigned_at).toLocaleDateString()}</td>
            <td className="px-6 py-4 text-right text-sm font-medium">
              <button onClick={() => openEditModal(a)} className="text-blue-600 hover:text-blue-900 mr-3">Modifier</button>
              <button onClick={() => deleteAssignment(a.id)} className="text-red-600 hover:text-red-900">Supprimer</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Nouvelle Affectation Professionnelle</h3>
            <div className="space-y-6 mb-6">
  {/* Filtres pour les étudiants */}
  <div className="p-4 bg-gray-50 rounded-lg border">
    <h4 className="font-medium text-gray-900 mb-3">🔍 Filtrer les étudiants</h4>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <input type="text" value={studentModalFilters.search} onChange={(e) => setStudentModalFilters({...studentModalFilters, search: e.target.value})} placeholder="Rechercher étudiant..." className="w-full border border-gray-300 rounded-md px-3 py-2"/>
      <select value={studentModalFilters.department} onChange={(e) => { setStudentModalFilters({...studentModalFilters, department: e.target.value, cursus: '', parcours: ''}); if (e.target.value) fetchCursus(e.target.value); else setCursus([]); }} className="w-full border border-gray-300 rounded-md px-3 py-2">
        <option value="">Département</option>
        {departments.map(d => <option key={d} value={d}>{d}</option>)}
      </select>
      <select value={studentModalFilters.cursus} onChange={(e) => { setStudentModalFilters({...studentModalFilters, cursus: e.target.value, parcours: ''}); if (e.target.value) fetchParcours(studentModalFilters.department, e.target.value); else setParcours([]); }} disabled={!studentModalFilters.department} className="w-full border border-gray-300 rounded-md px-3 py-2">
        <option value="">Cursus</option>
        {cursus.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
      <select value={studentModalFilters.parcours} onChange={(e) => setStudentModalFilters({...studentModalFilters, parcours: e.target.value})} disabled={!studentModalFilters.cursus} className="w-full border border-gray-300 rounded-md px-3 py-2">
        <option value="">Parcours</option>
        {parcours.map(p => <option key={p} value={p}>{p}</option>)}
      </select>
    </div>
  </div>

  {/* Filtres pour les encadreurs */}
  <div className="p-4 bg-gray-50 rounded-lg border">
    <h4 className="font-medium text-gray-900 mb-3">🔍 Filtrer les encadreurs professionnels</h4>
    <input type="text" value={supervisorModalFilters.search} onChange={(e) => setSupervisorModalFilters({...supervisorModalFilters, search: e.target.value})} placeholder="Rechercher par nom..." className="w-full border border-gray-300 rounded-md px-3 py-2" />
  </div>
  <button onClick={clearModalFilters} className="text-sm text-blue-600 hover:text-blue-800 w-full text-right">Effacer tous les filtres</button>
</div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Étudiant</label>
                <select value={newAssignment.student_id} onChange={(e) => setNewAssignment({ ...newAssignment, student_id: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2">
                  <option value="">Sélectionner un étudiant...</option>
                 {unassignedStudentsForModal.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.student_department || 'N/A'})</option>)}     </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Encadreur Professionnel</label>
<select
  value={newAssignment.professional_supervisor_id}
  onChange={(e) => setNewAssignment({ ...newAssignment, professional_supervisor_id: e.target.value })}
  className="w-full border border-gray-300 rounded-md px-3 py-2"
>
  <option value="">Sélectionner un encadreur...</option>
  {/* ON UTILISE LA NOUVELLE LISTE FILTRÉE ICI */}
{filteredProSupervisorsForModal.map((s) => (
  <option key={s.id} value={s.id}>
    {s.name} ({s.company_name || 'N/A'})
  </option>
  ))}
</select>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-gray-600">Annuler</button>
              <button onClick={createAssignment} disabled={!newAssignment.student_id || !newAssignment.professional_supervisor_id} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md disabled:opacity-50">Créer l'Affectation</button>
            </div>
          </div>
        </div>
      )}
      {showEditModal && editingAssignment && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
      <h3 className="text-lg font-semibold mb-4">Modifier l'Affectation Professionnelle</h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Étudiant</label>
          <input type="text" value={editingAssignment.student_name} disabled className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100"/>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Nouveau Encadreur Professionnel</label>
          <select 
            value={newAssignment.professional_supervisor_id} 
            onChange={(e) => setNewAssignment({ ...newAssignment, professional_supervisor_id: e.target.value })} 
            className="w-full border border-gray-300 rounded-md px-3 py-2"
          >
            <option value="">Sélectionner un encadreur...</option>
           {allProSupervisors.map((s) => ( <option key={s.id} value={s.id}>  {s.name} ({s.company_name || 'N/A'})
            </option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex justify-end space-x-3 mt-6">
        <button onClick={() => setShowEditModal(false)} className="px-4 py-2 text-gray-600">Annuler</button>
        <button onClick={updateAssignment} disabled={!newAssignment.professional_supervisor_id} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md disabled:opacity-50">Sauvegarder</button>
      </div>
    </div>
  </div>
)}
    </div>
  );
};
// Super Admin Components
const SuperAdminCreateTab = () => {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'admin',
    permissions: {
      can_manage_users: false,
      can_manage_internships: false,
      can_view_reports: false,
      can_export_data: false,
      can_manage_applications: false
    }
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/super-admin/create-admin`, formData);
      alert('Administrateur créé avec succès!');
      
      // Reset form
      setFormData({
        email: '',
        name: '',
        password: '',
        role: 'admin',
        permissions: {
          can_manage_users: false,
          can_manage_internships: false,
          can_view_reports: false,
          can_export_data: false,
          can_manage_applications: false
        }
      });
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (permission) => {
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions,
        [permission]: !formData.permissions[permission]
      }
    });
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Créer un Administrateur</h2>

      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom complet *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mot de passe *
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rôle *
              </label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="admin">Administrateur</option>
                <option value="super_admin">Super Administrateur</option>
              </select>
            </div>
          </div>

          {formData.role === 'admin' && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Permissions</h3>
              <div className="space-y-3">
                {Object.entries({
                  can_manage_users: 'Gérer les utilisateurs',
                  can_manage_internships: 'Gérer les stages',
                  can_view_reports: 'Voir les rapports',
                  can_export_data: 'Exporter les données',
                  can_manage_applications: 'Gérer les candidatures'
                }).map(([key, label]) => (
                  <label key={key} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.permissions[key]}
                      onChange={() => handlePermissionChange(key)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">{label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md disabled:opacity-50"
          >
            {loading ? 'Création en cours...' : 'Créer Administrateur'}
          </button>
        </form>
      </div>
    </div>
  );
};

const SuperAdminPermissionsTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      // Filter to show only admin users
      const adminUsers = response.data.filter(user => 
        user.role === 'admin' || user.role === 'super_admin'
      );
      setUsers(adminUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const changeUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/super-admin/change-user-role/${userId}?new_role=${newRole}`);
      alert('Rôle modifié avec succès!');
      fetchUsers();
    } catch (error) {
      alert('Erreur lors de la modification du rôle');
    }
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      admin: 'Administrateur',
      super_admin: 'Super Administrateur'
    };
    return roleNames[role] || role;
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Gérer les Permissions</h2>

      {loading ? (
        <div className="text-center py-8">Chargement des administrateurs...</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Administrateur
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rôle Actuel
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date création
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{user.name}</div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                      {getRoleDisplayName(user.role)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <select
                      value={user.role}
                      onChange={(e) => changeUserRole(user.id, e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded text-xs"
                    >
                      <option value="admin">Administrateur</option>
                      <option value="super_admin">Super Administrateur</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {users.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600">Aucun administrateur trouvé.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Super Admin Role Management Component
const SuperAdminRolesTab = () => {
  const [roleConfigs, setRoleConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchRoleConfigs();
  }, []);

  const fetchRoleConfigs = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/role-config`);
      setRoleConfigs(response.data);
    } catch (error) {
      console.error('Error fetching role configs:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateRoleConfig = (role, field, value) => {
    setRoleConfigs(prev => 
      prev.map(config => 
        config.role === role 
          ? { ...config, [field]: value }
          : config
      )
    );
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      const updates = roleConfigs.map(config => ({
        role: config.role,
        is_visible: config.is_visible,
        is_public_registration: config.is_public_registration,
        description: config.description
      }));

      await axios.put(`${API}/super-admin/role-config`, updates);
      alert('Configuration des rôles mise à jour avec succès!');
    } catch (error) {
      alert('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      'student': 'Étudiant',
      'professional_supervisor': 'Entreprise/Encadreur professionnel',
      'academic_supervisor': 'Encadreur académique',
      'teacher_mission': 'Enseignant en mission',
      'admin': 'Administrateur',
      'super_admin': 'Super Administrateur'
    };
    return roleNames[role] || role;
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Gérer la Visibilité des Rôles</h2>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-medium text-blue-900 mb-2">🎛️ Configuration des rôles</h3>
        <p className="text-blue-700 text-sm">
          Contrôlez quels rôles sont visibles dans l'interface d'inscription et lesquels permettent l'auto-inscription.
          Les modifications seront appliquées immédiatement pour tous les nouveaux utilisateurs.
        </p>
      </div>

      {loading ? (
        <div className="text-center py-8">Chargement de la configuration...</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rôle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Visible
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Auto-inscription
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {roleConfigs.map((config) => (
                <tr key={config.role}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {getRoleDisplayName(config.role)}
                    </div>
                    <div className="text-sm text-gray-500">{config.role}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={config.is_visible}
                        onChange={(e) => updateRoleConfig(config.role, 'is_visible', e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-600">
                        {config.is_visible ? 'Visible' : 'Masqué'}
                      </span>
                    </label>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={config.is_public_registration}
                        onChange={(e) => updateRoleConfig(config.role, 'is_public_registration', e.target.checked)}
                        disabled={!config.is_visible}
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded disabled:opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-600">
                        {config.is_public_registration ? 'Autorisée' : 'Restreinte'}
                      </span>
                    </label>
                    {!config.is_visible && config.is_public_registration && (
                      <p className="text-xs text-amber-600 mt-1">
                        ⚠️ Rôle invisible mais auto-inscription activée
                      </p>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <textarea
                      value={config.description}
                      onChange={(e) => updateRoleConfig(config.role, 'description', e.target.value)}
                      placeholder="Description du rôle..."
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      rows="2"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <p><strong>Visible:</strong> Le rôle apparaît dans la liste des rôles</p>
                <p><strong>Auto-inscription:</strong> Les utilisateurs peuvent s'inscrire avec ce rôle</p>
              </div>
              <button
                onClick={saveChanges}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md disabled:opacity-50"
              >
                {saving ? 'Sauvegarde...' : 'Sauvegarder les Modifications'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Summary of current configuration */}
      <div className="mt-6 bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Résumé de la Configuration</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="font-medium text-green-900 mb-2">Rôles avec Auto-inscription</h4>
            <ul className="text-sm text-green-700 space-y-1">
              {roleConfigs
                .filter(config => config.is_visible && config.is_public_registration)
                .map(config => (
                  <li key={config.role}>• {getRoleDisplayName(config.role)}</li>
                ))
              }
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-amber-900 mb-2">Rôles Restreints (Admin uniquement)</h4>
            <ul className="text-sm text-amber-700 space-y-1">
              {roleConfigs
                .filter(config => config.is_visible && !config.is_public_registration)
                .map(config => (
                  <li key={config.role}>• {getRoleDisplayName(config.role)}</li>
                ))
              }
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Super Admin Academic Year Management Component
const SuperAdminAcademicYearTab = () => {
  const [academicYears, setAcademicYears] = useState([]);
  const [currentYear, setCurrentYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    start_date: '',
    end_date: ''
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchAcademicYears();
    fetchCurrentAcademicYear();
  }, []);

  const fetchAcademicYears = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/academic-years`);
      setAcademicYears(response.data);
    } catch (error) {
      console.error('Error fetching academic years:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentAcademicYear = async () => {
    try {
      const response = await axios.get(`${API}/academic-year/current`);
      setCurrentYear(response.data);
    } catch (error) {
      console.error('Error fetching current academic year:', error);
    }
  };

  const generateAcademicYearName = (startDate, endDate) => {
    const startYear = new Date(startDate).getFullYear();
    const endYear = new Date(endDate).getFullYear();
    return `${startYear}-${endYear}`;
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      
      // Auto-generate name when both dates are filled
      if (newData.start_date && newData.end_date) {
        newData.name = generateAcademicYearName(newData.start_date, newData.end_date);
      }
      
      return newData;
    });
  };

  const createAcademicYear = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const academicYearData = {
        name: formData.name,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString()
      };

      await axios.post(`${API}/super-admin/academic-years`, academicYearData);
      alert('Année académique créée et ouverte avec succès!');
      
      // Reset form
      setFormData({ name: '', start_date: '', end_date: '' });
      
      // Refresh data
      fetchAcademicYears();
      fetchCurrentAcademicYear();
      
      // Refresh parent component (to update banner)
      window.location.reload();
      
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const closeAcademicYear = async (yearId) => {
    if (!confirm('Êtes-vous sûr de vouloir fermer cette année académique ? Cette action rendra toutes les données non modifiables.')) {
      return;
    }

    try {
      await axios.put(`${API}/super-admin/academic-years/${yearId}/close`);
      alert('Année académique fermée avec succès!');
      fetchAcademicYears();
      fetchCurrentAcademicYear();
      window.location.reload();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la fermeture');
    }
  };

  const reopenAcademicYear = async (yearId) => {
    if (!confirm('Êtes-vous sûr de vouloir rouvrir cette année académique ?')) {
      return;
    }

    try {
      await axios.put(`${API}/super-admin/academic-years/${yearId}/reopen`);
      alert('Année académique rouverte avec succès!');
      fetchAcademicYears();
      fetchCurrentAcademicYear();
      window.location.reload();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la réouverture');
    }
  };

  const getStatusColor = (status) => {
    return status === 'open' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  const getStatusText = (status) => {
    return status === 'open' ? 'Ouverte' : 'Fermée';
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Gestion de l'Année Académique</h2>

      {/* Current Academic Year Status */}
      <div className={`${
        currentYear?.is_open 
          ? 'bg-green-50 border-green-200' 
          : 'bg-red-50 border-red-200'
      } border rounded-lg p-6 mb-8`}>
        <h3 className="text-lg font-medium mb-4">
          {currentYear?.is_open ? '📅 Année Académique Actuelle' : '⚠️ Aucune Année Académique Ouverte'}
        </h3>
        
        {currentYear?.is_open ? (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <span className="font-medium text-gray-700">Nom:</span>
                <p className="text-lg">{currentYear.name}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Date de début:</span>
                <p>{new Date(currentYear.start_date).toLocaleDateString()}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Date de fin:</span>
                <p>{new Date(currentYear.end_date).toLocaleDateString()}</p>
              </div>
            </div>
            <button
              onClick={() => closeAcademicYear(currentYear.id)}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md"
            >
              Fermer l'Année Académique
            </button>
          </div>
        ) : (
          <div>
            <p className="text-red-700 mb-4">
              Aucune année académique n'est actuellement ouverte. Toutes les opérations sont bloquées jusqu'à l'ouverture d'une nouvelle année académique.
            </p>
          </div>
        )}
      </div>

      {/* Create New Academic Year */}
      {!currentYear?.is_open && (
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Créer une Nouvelle Année Académique</h3>
          
          <form onSubmit={createAcademicYear} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date de début *
                </label>
                <input
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date de fin *
                </label>
                <input
                  type="date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom de l'année académique
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  placeholder="Ex: 2024-2025"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Généré automatiquement si vide</p>
              </div>
            </div>

            <button
              type="submit"
              disabled={creating}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md disabled:opacity-50"
            >
              {creating ? 'Création en cours...' : 'Créer et Ouvrir l\'Année Académique'}
            </button>
          </form>
        </div>
      )}

      {/* Academic Years History */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Historique des Années Académiques</h3>
        </div>

        {loading ? (
          <div className="text-center py-8">Chargement...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Année Académique
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Période
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Créée le
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {academicYears.map((year) => (
                  <tr key={year.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{year.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(year.start_date).toLocaleDateString()} - {new Date(year.end_date).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(year.status)}`}>
                        {getStatusText(year.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(year.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {year.status === 'open' ? (
                        <button
                          onClick={() => closeAcademicYear(year.id)}
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs"
                        >
                          Fermer
                        </button>
                      ) : (
                        <button
                          onClick={() => reopenAcademicYear(year.id)}
                          className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs"
                          disabled={currentYear?.is_open}
                        >
                          Rouvrir
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {academicYears.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-600">Aucune année académique créée.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Information Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">ℹ️ Information Important</h4>
        <div className="text-blue-700 text-sm space-y-2">
          <p>• <strong>Une seule année académique</strong> peut être ouverte à la fois.</p>
          <p>• <strong>Toutes les opérations</strong> (inscriptions, candidatures, validations) nécessitent une année académique ouverte.</p>
          <p>• <strong>Fermer une année académique</strong> la rend non modifiable définitivement.</p>
          <p>• <strong>Rouvrir une année académique</strong> n'est possible que s'il n'y en a aucune autre d'ouverte.</p>
        </div>
      </div>
    </div>
  );
};

// Create Student Component for Academic Supervisors
const CreateStudentTab = ({ user }) => {
const [formData, setFormData] = useState({
  role: 'student',
  email: '',
  name: '',
  password: '',
  phone: '',
  student_matricule: '',
  student_cursus: '',
  student_parcours: '',
  company_name: '' // Ajout du champ pour l'encadreur pro
});
  const [loading, setLoading] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [file, setFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);

  const getRoleDisplayName = (role) => {
  const roleNames = {
    student: 'Étudiant',
    professional_supervisor: 'Encadreur Professionnel'
    // Ajoutez d'autres rôles ici si nécessaire
  };
  return roleNames[role] || role;
};

  // Get current user's department
  const getCurrentUserDepartment = () => {
    // Use user prop if available
    if (user && user.department) {
      return user.department;
    }
    
    // Try to get from localStorage as fallback
    const localUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (localUser.department) {
      return localUser.department;
    }
    
    // Default fallback for testing
    return 'GENIE INFORMATIQUE';
  };

  const department = getCurrentUserDepartment();

  // Get available cursus for the department
  const getAvailableCursus = () => {
    if (!department || !IUT_STRUCTURE[department]) {
      console.log('Debug - Department:', department);
      console.log('Debug - IUT_STRUCTURE keys:', Object.keys(IUT_STRUCTURE));
      return [];
    }
    return Object.keys(IUT_STRUCTURE[department]);
  };

  // Get available parcours for selected cursus
  const getAvailableParcours = () => {
    if (!department || !formData.student_cursus) {
      return [];
    }
    const dept = IUT_STRUCTURE[department];
    return dept && dept[formData.student_cursus] ? dept[formData.student_cursus] : [];
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Reset dependent fields when parent field changes
    if (name === 'student_cursus') {
      setFormData({
        ...formData,
        [name]: value,
        student_parcours: ''
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);
  
  try {
    let dataToSend = { ...formData };
    if (formData.role === 'student') {
      dataToSend.student_department = department;
    }
    
    // On s'assure d'appeler la NOUVELLE route
    await axios.post(`${API}/supervisor/create-user`, dataToSend);

    alert('Utilisateur créé avec succès!');
    
    setFormData({
      role: 'student', name: '', email: '', password: '', phone: '',
      student_matricule: '', student_cursus: '', student_parcours: '', company_name: ''
    });
    setUploadResult(null);
  } catch (error) {
    alert(error.response?.data?.detail || 'Erreur lors de la création');
  } finally {
    setLoading(false);
  }
};

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      alert('Veuillez sélectionner un fichier');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/academic-supervisor/students/bulk-create`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setUploadResult(response.data);
      setFile(null);
    } catch (error) {
      alert('Erreur lors du téléversement : ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/academic-supervisor/students/template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'modele_etudiants.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      alert('Erreur lors du téléchargement du modèle : ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Créer un Nouvel Utilisateur</h2>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-medium text-blue-900 mb-2">👨‍🎓 Création de compte</h3>
        <p className="text-blue-700 text-sm">
          Vous pouvez créer des comptes étudiants pour votre département: <strong>{department}</strong>
        </p>
      </div>

 <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* --- SÉLECTEUR DE RÔLE --- */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type de compte à créer *</label>
            <select name="role" value={formData.role} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500">
              <option value="student">Étudiant</option>
              <option value="professional_supervisor">Encadreur Professionnel</option>
            </select>
          </div>

          <div className="border-t border-gray-200 pt-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* --- CHAMPS COMMUNS --- */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nom complet *</label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mot de passe *</label>
                <input type="password" name="password" value={formData.password} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
              </div>

              {/* --- CHAMPS CONDITIONNELS --- */}
              {formData.role === 'student' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Matricule *</label>
                    <input type="text" name="student_matricule" value={formData.student_matricule} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="ex: MAT001" required={formData.role === 'student'} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cursus *</label>
                    <select name="student_cursus" value={formData.student_cursus} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" required={formData.role === 'student'}>
                      <option value="">Sélectionner un cursus</option>
                      {getAvailableCursus().map((c) => (<option key={c} value={c}>{c}</option>))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Parcours *</label>
                    <select name="student_parcours" value={formData.student_parcours} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" required={formData.role === 'student'} disabled={!formData.student_cursus}>
                      <option value="">Sélectionner un parcours</option>
                      {getAvailableParcours().map((p) => (<option key={p} value={p}>{p}</option>))}
                    </select>
                  </div>
                </>
              )}
              {formData.role === 'professional_supervisor' && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom de l'entreprise *</label>
                  <input type="text" name="company_name" value={formData.company_name} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" required={formData.role === 'professional_supervisor'} />
                </div>
              )}
            </div>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Informations automatiques</h4>
            <p className="text-sm text-gray-600">
              {formData.role === 'student' && <><strong>Département:</strong> {department}<br/></>}
              <strong>Rôle:</strong> {getRoleDisplayName(formData.role)}
            </p>
          </div>
          <button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md disabled:opacity-50">
            {loading ? 'Création en cours...' : `Créer l'Utilisateur (${getRoleDisplayName(formData.role)})`}
          </button>
        </form>
      </div>

      {/* Section de création en masse */}
      <div className="bg-white rounded-lg shadow p-6 mt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">📂 Création en Masse</h3>
        
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-medium text-yellow-900 mb-2">📋 Instructions</h4>
            <ol className="text-sm text-yellow-800 space-y-1">
              <li>1. Téléchargez le fichier modèle Excel</li>
              <li>2. Remplissez les informations des étudiants</li>
              <li>3. Téléversez le fichier rempli</li>
            </ol>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={downloadTemplate}
              className="flex items-center justify-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium"
            >
              📥 Télécharger le Modèle Excel
            </button>
            
            <div className="flex-1">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setFile(e.target.files[0])}
                className="hidden"
                id="excel-upload"
              />
              <label
                htmlFor="excel-upload"
                className="flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium cursor-pointer"
              >
                📤 Choisir un Fichier Excel
              </label>
            </div>
          </div>

          {file && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Fichier sélectionné:</strong> {file.name}
              </p>
              <button
                onClick={handleFileUpload}
                disabled={loading}
                className="mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium disabled:opacity-50"
              >
                {loading ? 'Téléversement en cours...' : 'Créer les Étudiants'}
              </button>
            </div>
          )}

          {uploadResult && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-medium text-green-900 mb-2">✅ Résultat du téléversement</h4>
              <p className="text-sm text-green-800 mb-2">
                <strong>{uploadResult.message}</strong>
              </p>
              <div className="text-sm text-green-700">
                <p>• Total traité: {uploadResult.total_processed}</p>
                <p>• Créés avec succès: {uploadResult.created_students?.length || 0}</p>
                <p>• Erreurs: {uploadResult.errors?.length || 0}</p>
              </div>
              
              {uploadResult.created_students && uploadResult.created_students.length > 0 && (
                <div className="mt-3">
                  <h5 className="font-medium text-green-900 mb-1">Étudiants créés:</h5>
                  <ul className="text-sm text-green-700 space-y-1">
                    {uploadResult.created_students.map((student, index) => (
                      <li key={index}>• {student.name} ({student.email}) - {student.matricule}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {uploadResult.errors && uploadResult.errors.length > 0 && (
                <div className="mt-3">
                  <h5 className="font-medium text-red-900 mb-1">Erreurs:</h5>
                  <ul className="text-sm text-red-700 space-y-1">
                    {uploadResult.errors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Create Users Component for Super Admin
const SuperAdminCreateUsersTab = () => {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'academic_supervisor',
    company_name: '',
    department: '',
    phone: '',
    permissions: {
      can_manage_users: false,
      can_manage_internships: false,
      can_view_reports: false,
      can_export_data: false,
      can_manage_applications: false
    }
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/super-admin/create-user`, formData);
      alert('Utilisateur créé avec succès!');
      
      // Reset form
      setFormData({
        email: '',
        name: '',
        password: '',
        role: 'academic_supervisor',
        company_name: '',
        department: '',
        phone: '',
        permissions: {
          can_manage_users: false,
          can_manage_internships: false,
          can_view_reports: false,
          can_export_data: false,
          can_manage_applications: false
        }
      });
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePermissionChange = (permission) => {
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions,
        [permission]: !formData.permissions[permission]
      }
    });
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      professional_supervisor: 'Entreprise/Encadreur professionnel',
      academic_supervisor: 'Encadreur académique',
      teacher_mission: 'Enseignant en mission',
      admin: 'Administrateur',
      super_admin: 'Super Administrateur'
    };
    return roleNames[role] || role;
  };

  const availableRoles = ['employer','professional_supervisor', 'academic_supervisor', 'teacher_mission', 'admin', 'super_admin'];
  const departmentRequiredRoles = ['academic_supervisor', 'teacher_mission'];
  const companyRequiredRoles = ['professional_supervisor', 'employer'];
  const adminRoles = ['admin', 'super_admin'];

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Créer des Utilisateurs</h2>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
        <h3 className="font-medium text-amber-900 mb-2">⚠️ Restriction</h3>
        <p className="text-amber-700 text-sm">
          En tant que super administrateur, vous pouvez créer tous les types d'utilisateurs <strong>sauf les étudiants</strong>. 
          Les comptes étudiants doivent être créés par les encadreurs académiques.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom complet *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mot de passe *
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rôle *
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              >
                {availableRoles.map((role) => (
                  <option key={role} value={role}>{getRoleDisplayName(role)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Téléphone
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {companyRequiredRoles.includes(formData.role) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom de l'entreprise *
              </label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          )}

          {departmentRequiredRoles.includes(formData.role) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Département *
              </label>
              <select
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Sélectionner un département</option>
                {Object.keys(IUT_STRUCTURE).map((dept) => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
          )}

          {adminRoles.includes(formData.role) && (
            <div className="col-span-full">
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Permissions
              </label>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="can_manage_users"
                      checked={formData.permissions.can_manage_users}
                      onChange={() => handlePermissionChange('can_manage_users')}
                      className="mr-2"
                    />
                    <label htmlFor="can_manage_users" className="text-sm text-gray-700">
                      Gérer les utilisateurs
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="can_manage_internships"
                      checked={formData.permissions.can_manage_internships}
                      onChange={() => handlePermissionChange('can_manage_internships')}
                      className="mr-2"
                    />
                    <label htmlFor="can_manage_internships" className="text-sm text-gray-700">
                      Gérer les stages
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="can_view_reports"
                      checked={formData.permissions.can_view_reports}
                      onChange={() => handlePermissionChange('can_view_reports')}
                      className="mr-2"
                    />
                    <label htmlFor="can_view_reports" className="text-sm text-gray-700">
                      Voir les rapports
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="can_export_data"
                      checked={formData.permissions.can_export_data}
                      onChange={() => handlePermissionChange('can_export_data')}
                      className="mr-2"
                    />
                    <label htmlFor="can_export_data" className="text-sm text-gray-700">
                      Exporter les données
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="can_manage_applications"
                      checked={formData.permissions.can_manage_applications}
                      onChange={() => handlePermissionChange('can_manage_applications')}
                      className="mr-2"
                    />
                    <label htmlFor="can_manage_applications" className="text-sm text-gray-700">
                      Gérer les candidatures
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md disabled:opacity-50"
          >
            {loading ? 'Création en cours...' : 'Créer l\'Utilisateur'}
          </button>
        </form>
      </div>
    </div>
  );
};

const AdminCreateUsersTab = () => {
const [formData, setFormData] = useState({
  email: '', name: '', password: '',
  role: 'academic_supervisor', // ou 'student' si vous préférez
  company_name: '', department: '', phone: '',
  student_department: '', student_cursus: '', student_parcours: '',
   permissions: {
    can_manage_users: false,
    can_manage_internships: false,
    can_view_reports: false,
    can_export_data: false,
    can_manage_applications: false
  }
});
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [file, setFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null); 

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/structure/departments`);
      setDepartments(response.data.departments);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/admin/create-user`, formData);
      alert('Utilisateur créé avec succès!');
      
      // Reset form
      setFormData({
        email: '',
        name: '',
        password: '',
        role: 'academic_supervisor',
        company_name: '',
        department: '',
        phone: '',
        student_department: '', student_cursus: '', student_parcours: '',
         permissions: {
          can_manage_users: false,
          can_manage_internships: false,
          can_view_reports: false,
          can_export_data: false,
          can_manage_applications: false
         }
      });
    } catch (error) {
      alert('Erreur lors de la création: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

const handleInputChange = (e) => {
  const { name, value } = e.target;
  setFormData(prev => {
    const newState = { ...prev, [name]: value };
    // Si on change le département de l'étudiant, on reset le cursus et le parcours
    if (name === 'student_department') {
      newState.student_cursus = '';
      newState.student_parcours = '';
    }
    // Si on change le cursus, on reset le parcours
    if (name === 'student_cursus') {
      newState.student_parcours = '';
    }
    return newState;
  });
};
const handlePermissionChange = (key) => {
  setFormData(prev => ({
    ...prev,
    permissions: {
      ...prev.permissions,
      [key]: !prev.permissions[key] // On inverse la valeur actuelle (true devient false, et vice-versa)
    }
  }));
};
  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      alert('Veuillez sélectionner un fichier');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/academic-supervisor/students/bulk-create`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setUploadResult(response.data);
      setFile(null);
    } catch (error) {
      alert('Erreur lors du téléversement : ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/academic-supervisor/students/template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'modele_etudiants.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      alert('Erreur lors du téléchargement du modèle : ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Créer des Utilisateurs</h2>
      
      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Rôle *</label>
            <select
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            >
  <option value="student">Étudiant</option>
  <option value="academic_supervisor">Encadreur Académique</option>
  <option value="professional_supervisor">Encadreur Professionnel</option>
  <option value="employer">Employeur</option>
  <option value="teacher_mission">Enseignant en Mission</option>
  <option value="admin">Administrateur</option>
            </select>
          </div>
           {formData.role === 'academic_supervisor' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Département *</label>
              <select
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Sélectionner un département</option>
                {departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nom complet *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Mot de passe *</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
           <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {['professional_supervisor', 'employer'].includes(formData.role) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nom de l'entreprise *</label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          )}

{/* Permissions for Admins (votre code) */}
{formData.role === 'admin' && (
  <div className="p-4 border rounded-md bg-gray-50"> {/* J'ai gardé le style de conteneur pour la clarté */}
    <h3 className="text-lg font-medium text-gray-900 mb-4">Permissions</h3>
    <div className="space-y-3">
      {Object.entries({
        can_manage_users: 'Gérer les utilisateurs',
        can_manage_internships: 'Gérer les stages',
        can_view_reports: 'Voir les rapports',
        can_export_data: 'Exporter les données',
        can_manage_applications: 'Gérer les candidatures'
      }).map(([key, label]) => (
        <label key={key} className="flex items-center">
          <input
            type="checkbox"
            checked={formData.permissions[key]}
            onChange={() => handlePermissionChange(key)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <span className="ml-2 text-sm text-gray-700">{label}</span>
        </label>
      ))}
    </div>
  </div>
)}

          {/* Department for Teachers */}
{formData.role === 'teacher_mission' && (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">Département *</label>
    <select
      name="department"
      value={formData.department}
      onChange={handleInputChange}
      className="w-full px-3 py-2 border border-gray-300 rounded-md"
      required
    >
      <option value="">Sélectionner un département</option>
      {departments.map(dept => (
        <option key={dept} value={dept}>{dept}</option>
      ))}
    </select>
  </div>
)}

{/* Fields for Students */}
{formData.role === 'student' && (
  <>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Département de l'étudiant *</label>
      <select name="student_department" value={formData.student_department} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" required>
        <option value="">Sélectionner un département</option>
        {Object.keys(IUT_STRUCTURE).map(dept => (<option key={dept} value={dept}>{dept}</option>))}
      </select>
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Cursus *</label>
      <select name="student_cursus" value={formData.student_cursus} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" required disabled={!formData.student_department}>
        <option value="">Sélectionner un cursus</option>
        {formData.student_department && IUT_STRUCTURE[formData.student_department] && Object.keys(IUT_STRUCTURE[formData.student_department]).map(cursus => (<option key={cursus} value={cursus}>{cursus}</option>))}
      </select>
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Parcours *</label>
      <select name="student_parcours" value={formData.student_parcours} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" required disabled={!formData.student_cursus}>
        <option value="">Sélectionner un parcours</option>
        {formData.student_department && formData.student_cursus && IUT_STRUCTURE[formData.student_department][formData.student_cursus] && IUT_STRUCTURE[formData.student_department][formData.student_cursus].map(parcours => (<option key={parcours} value={parcours}>{parcours}</option>))}
      </select>
    </div>
  </>
)}
          <div className="flex items-center justify-between pt-4">
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline disabled:opacity-50"
            >
              {loading ? 'Création...' : 'Créer l\'utilisateur'}
            </button>
          </div>
        </form>
      </div>
            {formData.role === 'student' && (
        <div className="bg-white rounded-lg shadow p-6 mt-6">
<div className="bg-gray-50 rounded-lg p-6 mt-8 border-t-4 border-blue-500 shadow-inner"> {/* Fond légèrement différent pour contraster */}
  
  {/* Titre et séparation */}
  <div className="relative text-center mb-6">
    <div className="absolute inset-0 flex items-center" aria-hidden="true">
      <div className="w-full border-t border-gray-300"></div>
    </div>
    <div className="relative flex justify-center">
      <span className="bg-gray-50 px-4 text-lg font-medium text-gray-700">OU</span>
    </div>
  </div>

  {/* Titre et description de la section */}
  <div className="text-center mb-6">
    <h3 className="text-xl font-semibold text-gray-900">Faire une Création en Masse</h3>
    <p className="mt-1 text-sm text-gray-600">
      Pour créer plusieurs étudiants à la fois à partir d'un fichier Excel.
    </p>
  </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="button" // Important: type="button" pour ne pas soumettre le formulaire principal
                onClick={downloadTemplate}
                className="flex items-center justify-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium"
              >
                📥 Télécharger le Modèle
              </button>
              
              <div className="flex-1">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setFile(e.target.files[0])}
                  className="hidden"
                  id="admin-excel-upload"
                />
                <label
                  htmlFor="admin-excel-upload"
                  className="flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium cursor-pointer"
                >
                  📤 Choisir un Fichier Excel
                </label>
              </div>
            </div>

            {file && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Fichier sélectionné:</strong> {file.name}
                </p>
                <button
                  type="button" // Important: type="button"
                  onClick={handleFileUpload}
                  disabled={loading}
                  className="mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium disabled:opacity-50"
                >
                  {loading ? 'Téléversement...' : 'Lancer la Création en Masse'}
                </button>
              </div>
            )}

            {uploadResult && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-medium text-green-900 mb-2">✅ Résultat du téléversement</h4>
                <p className="text-sm text-green-800 mb-2">
                  <strong>{uploadResult.message}</strong>
                </p>
                <div className="text-sm text-green-700">
                  <p>• Total traité: {uploadResult.total_processed}</p>
                  <p>• Créés avec succès: {uploadResult.created_students?.length || 0}</p>
                  <p>• Erreurs: {uploadResult.errors?.length || 0}</p>
                </div>
                
                {uploadResult.errors && uploadResult.errors.length > 0 && (
                  <div className="mt-3">
                    <h5 className="font-medium text-red-900 mb-1">Détail des erreurs:</h5>
                    <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                      {uploadResult.errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


// ==================== ADMIN MESSAGING TAB ====================
const AdminMessagingTab = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [selectedContact, setSelectedContact] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Filtres hiérarchisés
  const [filters, setFilters] = useState({
    role: '',
    department: '',
    cursus: '',
    parcours: '',
    search: ''
  });
  const [departments, setDepartments] = useState([]);
  const [cursus, setCursus] = useState([]);
  const [parcours, setParcours] = useState([]);

  useEffect(() => {
    fetchConversations();
    fetchContacts();
    fetchDepartments();
  }, []);

  useEffect(() => {
    if (filters.department) {
      fetchCursus(filters.department);
    } else {
      setCursus([]);
      setParcours([]);
    }
  }, [filters.department]);

  useEffect(() => {
    if (filters.department && filters.cursus) {
      fetchParcours(filters.department, filters.cursus);
    } else {
      setParcours([]);
    }
  }, [filters.department, filters.cursus]);

  useEffect(() => {
    applyFilters();
  }, [contacts, filters]);

  // Utility function to get the other participant (not current user)
  const getOtherParticipant = (conversation) => {
    if (!conversation || !conversation.participants || !conversation.participant_names) {
      return { name: 'Utilisateur', role: 'unknown', index: 0 };
    }
    
    const currentUserId = getCurrentUserId();
    const otherParticipantIndex = conversation.participants.findIndex(p => p !== currentUserId);
    
    if (otherParticipantIndex === -1) {
      return { name: 'Utilisateur', role: 'unknown', index: 0 };
    }
    
    return {
      name: conversation.participant_names[otherParticipantIndex] || 'Utilisateur',
      role: conversation.participant_roles[otherParticipantIndex] || 'unknown',
      department: conversation.participant_departments?.[otherParticipantIndex],
      cursus: conversation.participant_cursus?.[otherParticipantIndex],
      parcours: conversation.participant_parcours?.[otherParticipantIndex],
      index: otherParticipantIndex
    };
  };

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        alert('Erreur d\'authentification. Veuillez vous reconnecter.');
        return;
      }
      
      const response = await axios.get(`${API}/conversations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      if (error.response?.status === 403) {
        alert('Accès refusé. Veuillez vous reconnecter.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        return;
      }
      
      const response = await axios.get(`${API}/users/contacts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setContacts(response.data.contacts || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      if (error.response?.status === 403) {
        alert('Accès refusé pour récupérer les contacts.');
      }
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/structure/departments`);
      setDepartments(response.data.departments);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchCursus = async (department) => {
    try {
      const response = await axios.get(`${API}/structure/cursus?department=${department}`);
      setCursus(response.data.cursus);
    } catch (error) {
      console.error('Error fetching cursus:', error);
    }
  };

  const fetchParcours = async (department, cursusValue) => {
    try {
      const response = await axios.get(`${API}/structure/parcours?department=${department}&cursus=${cursusValue}`);
      setParcours(response.data.parcours);
    } catch (error) {
      console.error('Error fetching parcours:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...contacts];

    // Filter by role
    if (filters.role) {
      filtered = filtered.filter(contact => contact.role === filters.role);
    }

    // Filter by department
    if (filters.department) {
      filtered = filtered.filter(contact => {
        // For students, check student_department
        if (contact.role === 'student') {
          return contact.student_department === filters.department;
        }
        // For staff (academic_supervisor, teacher_mission), check department
        else if (contact.role === 'academic_supervisor' || contact.role === 'teacher_mission') {
          return contact.department === filters.department;
        }
        // For other roles, check both fields
        else {
          return contact.department === filters.department || contact.student_department === filters.department;
        }
      });
    }

    // Filter by cursus (only for students)
    if (filters.cursus) {
      filtered = filtered.filter(contact => {
        if (contact.role === 'student') {
          return contact.student_cursus === filters.cursus;
        }
        // For non-students, cursus filter doesn't apply
        return true;
      });
    }

    // Filter by parcours (only for students)
    if (filters.parcours) {
      filtered = filtered.filter(contact => {
        if (contact.role === 'student') {
          return contact.student_parcours === filters.parcours;
        }
        // For non-students, parcours filter doesn't apply
        return true;
      });
    }

    // Filter by search
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(contact => 
        contact.name.toLowerCase().includes(searchTerm) ||
        contact.email.toLowerCase().includes(searchTerm)
      );
    }

    setFilteredContacts(filtered);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      // Reset dependent filters
      ...(field === 'department' && { cursus: '', parcours: '' }),
      ...(field === 'cursus' && { parcours: '' }),
      // If role changes, reset department-specific filters
      ...(field === 'role' && { department: '', cursus: '', parcours: '' })
    }));
  };

  const clearFilters = () => {
    setFilters({
      role: '',
      department: '',
      cursus: '',
      parcours: '',
      search: ''
    });
  };

  const fetchMessages = async (conversationId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        alert('Erreur d\'authentification. Veuillez vous reconnecter.');
        return;
      }
      
      const response = await axios.get(`${API}/conversations/${conversationId}/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      if (error.response?.status === 403) {
        alert('Accès refusé pour récupérer les messages.');
      }
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert('La taille du fichier ne peut pas dépasser 5MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const uploadFile = async () => {
    if (!selectedFile) return null;

    setUploadingFile(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        alert('Erreur d\'authentification. Veuillez vous reconnecter.');
        return null;
      }

      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      return response.data.file_id;
    } catch (error) {
      console.error('Error uploading file:', error);
      if (error.response?.status === 403) {
        alert('Accès refusé pour l\'upload du fichier.');
      } else {
        alert('Erreur lors de l\'upload du fichier');
      }
      return null;
    } finally {
      setUploadingFile(false);
    }
  };

  const sendMessage = async () => {
    if ((!newMessage.trim() && !selectedFile) || !selectedConversation) return;

    setSendingMessage(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        alert('Erreur d\'authentification. Veuillez vous reconnecter.');
        setSendingMessage(false);
        return;
      }

      let fileId = null;
      if (selectedFile) {
        fileId = await uploadFile();
        if (!fileId) {
          setSendingMessage(false);
          return;
        }
      }

      const recipientId = selectedConversation.participants.find(p => p !== getCurrentUserId());
      await axios.post(`${API}/conversations/${selectedConversation.id}/messages`, {
        recipient_id: recipientId,
        content: newMessage.trim() || `Fichier envoyé: ${selectedFile?.name}`,
        message_type: selectedFile ? 'document' : 'text',
        document_id: fileId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setNewMessage('');
      setSelectedFile(null);
      document.getElementById('file-input').value = '';
      fetchMessages(selectedConversation.id);
      fetchConversations();
    } catch (error) {
      console.error('Error sending message:', error);
      if (error.response?.status === 403) {
        alert('Accès refusé pour envoyer le message.');
      } else {
        alert('Erreur lors de l\'envoi du message.');
      }
    } finally {
      setSendingMessage(false);
    }
  };

  const createConversation = async () => {
    if (!selectedContact) {
      alert('Veuillez sélectionner un utilisateur');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        alert('Erreur d\'authentification. Veuillez vous reconnecter.');
        return;
      }

      const response = await axios.post(`${API}/conversations`, {
        participant_ids: [selectedContact],
        title: null,
        initial_message: null
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data) {
        setShowNewConversation(false);
        setSelectedContact('');
        clearFilters();
        fetchConversations();
        alert('Conversation créée avec succès!');
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      if (error.response?.status === 403) {
        alert('Accès refusé pour créer la conversation.');
      } else {
        alert('Erreur: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const getCurrentUserId = () => {
    // Try to get user ID from token payload
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.sub;
      } catch (e) {
        console.error('Error parsing token:', e);
      }
    }
    return localStorage.getItem('userId') || 'current-user-id';
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      'student': 'Étudiant',
      'academic_supervisor': 'Encadreur Académique',
      'professional_supervisor': 'Encadreur Professionnel',
      'teacher_mission': 'Enseignant en Mission',
      'admin': 'Administrateur',
      'super_admin': 'Super Administrateur'
    };
    return roleNames[role] || role;
  };

  const downloadFile = async (fileId, fileName) => {
    try {
      const response = await axios.get(`${API}/files/${fileId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Erreur lors du téléchargement du fichier');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des conversations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar - Conversations */}
      <div className="w-1/3 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Conversations</h2>
            <button
              onClick={() => setShowNewConversation(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-sm"
            >
              Nouvelle
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              Aucune conversation
            </div>
          ) : (
            conversations.map((conversation) => (
              <div
                key={conversation.id}
                onClick={() => {
                  setSelectedConversation(conversation);
                  fetchMessages(conversation.id);
                }}
                className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                  selectedConversation?.id === conversation.id ? 'bg-blue-50 border-blue-200' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-blue-600 font-semibold text-sm">
                          {getOtherParticipant(conversation).name.charAt(0) || '?'}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {getOtherParticipant(conversation).name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {getRoleDisplayName(getOtherParticipant(conversation).role)}
                        </p>
                      </div>
                    </div>
                    {conversation.last_message_content && (
                      <p className="text-sm text-gray-600 truncate mt-1">
                        {conversation.last_message_content}
                      </p>
                    )}
                  </div>
                  {conversation.unread_count?.[getCurrentUserId()] > 0 && (
                    <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1">
                      {conversation.unread_count[getCurrentUserId()]}
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="bg-white p-4 border-b border-gray-200">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                  <span className="text-blue-600 font-semibold">
                    {getOtherParticipant(selectedConversation).name.charAt(0) || '?'}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {getOtherParticipant(selectedConversation).name}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {getRoleDisplayName(getOtherParticipant(selectedConversation).role)}
                  </p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.sender_id === getCurrentUserId() ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                      message.sender_id === getCurrentUserId()
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-900'
                    }`}
                  >
                    <p className="text-sm">{message.content}</p>
                    
                    {message.message_type === 'document' && message.document_id && (
                      <div className="mt-2 p-2 bg-white bg-opacity-20 rounded border">
                        <div className="flex items-center">
                          <span className="text-xs mr-2">📎</span>
                          <button
                            onClick={() => downloadFile(message.document_id, 'document')}
                            className="text-xs underline hover:no-underline"
                          >
                            Télécharger le fichier
                          </button>
                        </div>
                      </div>
                    )}
                    
                    <p className={`text-xs mt-1 ${
                      message.sender_id === getCurrentUserId() ? 'text-blue-100' : 'text-gray-500'
                    }`}>
                      {new Date(message.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Message Input */}
            <div className="bg-white p-4 border-t border-gray-200">
              {selectedFile && (
                <div className="mb-3 p-2 bg-gray-50 rounded-md flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-sm text-gray-600 mr-2">📎</span>
                    <span className="text-sm text-gray-800">{selectedFile.name}</span>
                    <span className="text-xs text-gray-500 ml-2">
                      ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedFile(null);
                      document.getElementById('file-input').value = '';
                    }}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    Supprimer
                  </button>
                </div>
              )}
              
              <div className="flex items-center space-x-3">
                <input
                  id="file-input"
                  type="file"
                  onChange={handleFileSelect}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif"
                />
                <button
                  onClick={() => document.getElementById('file-input').click()}
                  className="p-2 text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg"
                  title="Joindre un fichier"
                >
                  📎
                </button>
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Tapez votre message..."
                  className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={sendMessage}
                  disabled={sendingMessage || uploadingFile || (!newMessage.trim() && !selectedFile)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
                >
                  {sendingMessage || uploadingFile ? 'Envoi...' : 'Envoyer'}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gray-400 text-2xl">💬</span>
              </div>
              <h3 className="text-lg font-medium text-gray-900">Sélectionnez une conversation</h3>
              <p className="text-gray-500 mt-2">
                Choisissez une conversation dans la liste de gauche pour commencer à chatter
              </p>
            </div>
          </div>
        )}
      </div>

      {/* New Conversation Modal */}
      {showNewConversation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Nouvelle Conversation</h3>
            
            {/* Information sur les filtres */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                  <span className="text-blue-600 text-sm">💡</span>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-blue-800 mb-1">Utilisation des filtres</h3>
                  <p className="text-sm text-blue-700">
                    Les filtres ci-dessous s'appliquent à la fois au <strong>tableau des affectations existantes</strong> et aux <strong>listes déroulantes de création d'affectation</strong>. 
                    Utilisez-les pour limiter les étudiants et encadreursdisponibles par département, cursus ou parcours.
                  </p>
                </div>
              </div>
            </div>

            {/* Filtres */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-3">🔍 Filtres</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rôle
                  </label>
                  <select
                    value={filters.role}
                    onChange={(e) => handleFilterChange('role', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Tous les rôles</option>
                    <option value="student">Étudiant</option>
                    <option value="academic_supervisor">Encadreur Académique</option>
                    <option value="professional_supervisor">Encadreur Professionnel</option>
                    <option value="teacher_mission">Enseignant en Mission</option>
                    <option value="admin">Administrateur</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Département
                  </label>
                  <select
                    value={filters.department}
                    onChange={(e) => handleFilterChange('department', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Tous les départements</option>
                    {departments.map((dept) => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cursus {filters.role !== 'student' && filters.role !== '' && (
                      <span className="text-xs text-gray-500">(Disponible uniquement pour les étudiants)</span>
                    )}
                  </label>
                  <select
                    value={filters.cursus}
                    onChange={(e) => handleFilterChange('cursus', e.target.value)}
                    disabled={!filters.department || (filters.role !== 'student' && filters.role !== '')}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    <option value="">Tous les cursus</option>
                    {cursus.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Parcours {filters.role !== 'student' && filters.role !== '' && (
                      <span className="text-xs text-gray-500">(Disponible uniquement pour les étudiants)</span>
                    )}
                  </label>
                  <select
                    value={filters.parcours}
                    onChange={(e) => handleFilterChange('parcours', e.target.value)}
                    disabled={!filters.cursus || (filters.role !== 'student' && filters.role !== '')}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    <option value="">Tous les parcours</option>
                    {parcours.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rechercher
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    placeholder="Nom ou email..."
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md"
                  >
                    Effacer
                  </button>
                </div>
              </div>
            </div>

            {/* Liste des contacts filtrés */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sélectionner un utilisateur ({filteredContacts.length} résultat{filteredContacts.length !== 1 ? 's' : ''})
              </label>
              <div className="max-h-64 overflow-y-auto border border-gray-300 rounded-md">
                {filteredContacts.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    Aucun utilisateur ne correspond aux critères
                  </div>
                ) : (
                  filteredContacts.map((contact) => (
                    <div
                      key={contact.id}
                      onClick={() => setSelectedContact(contact.id)}
                      className={`p-3 border-b border-gray-200 cursor-pointer hover:bg-gray-50 ${
                        selectedContact === contact.id ? 'bg-blue-50 border-blue-200' : ''
                      }`}
                    >
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          <span className="text-blue-600 font-semibold text-sm">
                            {contact.name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{contact.name}</p>
                          <p className="text-xs text-gray-500">
                            {getRoleDisplayName(contact.role)} • {contact.email}
                          </p>
                          {/* Affichage des informations selon le rôle */}
                          {contact.role === 'student' && (
                            <p className="text-xs text-gray-400">
                              {contact.student_department}
                              {contact.student_cursus && ` • ${contact.student_cursus}`}
                              {contact.student_parcours && ` • ${contact.student_parcours}`}
                            </p>
                          )}
                          {(contact.role === 'academic_supervisor' || contact.role === 'teacher_mission') && contact.department && (
                            <p className="text-xs text-gray-400">
                              {contact.department}
                            </p>
                          )}
                          {contact.role === 'professional_supervisor' && contact.company_name && (
                            <p className="text-xs text-gray-400">
                              {contact.company_name}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowNewConversation(false);
                  setSelectedContact('');
                  clearFilters();
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Annuler
              </button>
              <button
                onClick={createConversation}
                disabled={!selectedContact}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md disabled:opacity-50"
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== ADMIN PLACEMENTS TAB ====================
const AdminPlacementsTab = () => {
  const [placements, setPlacements] = useState([]);
  const [students, setStudents] = useState([]);
  const [internships, setInternships] = useState([]);
  const [academicSupervisors, setAcademicSupervisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPlacement, setNewPlacement] = useState({
    student_id: '',
    internship_id: '',
    academic_supervisor_id: '',
    start_date: '',
    end_date: '',
    validation_message: ''
  });
  const [studentFilters, setStudentFilters] = useState({ department: '', cursus: '', parcours: '' });
  const [supervisorSearch, setSupervisorSearch] = useState('');

 // States pour les listes de filtres
  const [departments, setDepartments] = useState([]);
  const [cursus, setCursus] = useState([]);
  const [parcours, setParcours] = useState([]);

useEffect(() => {
    fetchPlacements();
    fetchStudents();
    fetchInternships();
    fetchAcademicSupervisors();
    fetchDepartments(); 
}, []);

  const fetchPlacements = async () => {
  try {
      const response = await axios.get(`${API}/internship-placements`);
      setPlacements(response.data.placements);
  } catch (error) {
      console.error('Error fetching placements:', error);
  } finally {
    setLoading(false);
  }
};

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      const studentsData = response.data.filter(user => user.role === 'student');
      setStudents(studentsData);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const fetchInternships = async () => {
    try {
      const response = await axios.get(`${API}/admin/internships`);
      setInternships(response.data);
    } catch (error) {
      console.error('Error fetching internships:', error);
    }
  };

  const fetchAcademicSupervisors = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      const supervisorsData = response.data.filter(user => user.role === 'academic_supervisor');
      setAcademicSupervisors(supervisorsData);
    } catch (error) {
      console.error('Error fetching academic supervisors:', error);
    }
  };
  const fetchDepartments = async () => { try { const res = await axios.get(`${API}/structure/departments`); setDepartments(res.data.departments); } catch (e) {} };
  const fetchCursus = async (d) => { try { const res = await axios.get(`${API}/structure/cursus?department=${d}`); setCursus(res.data.cursus); } catch (e) {} };
  const fetchParcours = async (d, c) => { try { const res = await axios.get(`${API}/structure/parcours?department=${d}&cursus=${c}`); setParcours(res.data.parcours); } catch (e) {} };

  const handleStudentFilterChange = (key, value) => {
  setStudentFilters(prev => {
    const newFilters = { ...prev, [key]: value };
    if (key === 'department') {
      newFilters.cursus = '';
      newFilters.parcours = '';
      if (value) fetchCursus(value); else { setCursus([]); setParcours([]); }
    } else if (key === 'cursus') {
      newFilters.parcours = '';
      if (value) fetchParcours(newFilters.department, value); else { setParcours([]); }
    }
    return newFilters;
  });
 };

  const createPlacement = async () => {
    try {
      await axios.post(`${API}/internship-placements`, newPlacement);
      setShowCreateModal(false);
      setNewPlacement({
        student_id: '',
        internship_id: '',
        academic_supervisor_id: '',
        start_date: '',
        end_date: '',
        validation_message: ''
      });
      fetchPlacements();
      alert('Placement créé avec succès!');
    } catch (error) {
      console.error('Error creating placement:', error);
      alert('Erreur lors de la création du placement');
    }
  };

  const updatePlacementStatus = async (placementId, status) => {
    try {
      await axios.put(`${API}/internship-placements/${placementId}`, { status });
      fetchPlacements();
      alert('Statut du placement mis à jour!');
    } catch (error) {
      console.error('Error updating placement status:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending_validation':
        return 'bg-yellow-100 text-yellow-800';
      case 'validated':
        return 'bg-green-100 text-green-800';
      case 'placed':
        return 'bg-blue-100 text-blue-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending_validation':
        return 'En attente de validation';
      case 'validated':
        return 'Validé';
      case 'placed':
        return 'Placé en stage';
      case 'rejected':
        return 'Rejeté';
      case 'cancelled':
        return 'Annulé';
      default:
        return status;
    }
  };

const blockingStatuses = ['pending_validation', 'validated', 'placed'];

// 2. Filtrer les placements pour ne garder que ceux qui sont "bloquants"
const placedStudentIds = new Set(
  placements
    .filter(p => blockingStatuses.includes(p.status))
    .map(p => p.student_id)
);

// 3. La logique pour trouver les étudiants non placés est maintenant correcte
const unplacedStudents = students.filter(s => !placedStudentIds.has(s.id));

const filteredUnplacedStudents = unplacedStudents.filter(student => {
  const matchesDept = !studentFilters.department || student.student_department === studentFilters.department;
  const matchesCursus = !studentFilters.cursus || student.student_cursus === studentFilters.cursus;
  const matchesParcours = !studentFilters.parcours || student.student_parcours === studentFilters.parcours;
  return matchesDept && matchesCursus && matchesParcours;
});

const filteredSupervisors = academicSupervisors.filter(s => {
  const search = supervisorSearch.toLowerCase();
  return !search || s.name.toLowerCase().includes(search) || (s.department && s.department.toLowerCase().includes(search));
});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des placements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Mise en Stage des Étudiants</h2>
          <p className="text-gray-600">Gérez les placements des étudiants en stage</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
        >
          Nouveau Placement
        </button>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Placements ({placements.length})
          </h3>
        </div>

        {placements.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-gray-400 text-2xl">🎯</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900">Aucun placement</h3>
            <p className="text-gray-500 mt-2">
              Commencez par créer un nouveau placement d'étudiant
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Étudiant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Encadreur Académique
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Période
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {placements.map((placement) => (
                  <tr key={placement.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {placement.student_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {placement.student_department} • {placement.student_cursus}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {placement.internship_title}
                        </div>
                        <div className="text-sm text-gray-500">
                          {placement.company_name}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {placement.academic_supervisor_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(placement.status)}`}>
                        {getStatusText(placement.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(placement.start_date).toLocaleDateString()} - {new Date(placement.end_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {placement.status === 'validated' && (
                        <button
                          onClick={() => updatePlacementStatus(placement.id, 'placed')}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          Placer en stage
                        </button>
                      )}
                      {placement.status === 'pending_validation' && (
                        <button
                          onClick={() => updatePlacementStatus(placement.id, 'cancelled')}
                          className="text-red-600 hover:text-red-900"
                        >
                          Annuler
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Placement Modal */}
{showCreateModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
      <h3 className="text-xl font-bold mb-6 text-gray-800">Nouveau Placement</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
        
        {/* --- COLONNE DE GAUCHE : Étudiant & Stage --- */}
        <div className="space-y-6">
          {/* Bloc de filtres pour l'étudiant */}
          <div className="p-4 border rounded-md bg-gray-50">
            <p className="font-semibold text-sm text-gray-800 mb-3">1. Filtrer les étudiants disponibles</p>
            <div className="space-y-2">
              <select value={studentFilters.department} onChange={e => handleStudentFilterChange('department', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"><option value="">Tous les Départements</option>{departments.map(d=><option key={d} value={d}>{d}</option>)}</select>
              <select value={studentFilters.cursus} onChange={e => handleStudentFilterChange('cursus', e.target.value)} disabled={!studentFilters.department} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm disabled:bg-gray-100"><option value="">Tous les Cursus</option>{cursus.map(c=><option key={c} value={c}>{c}</option>)}</select>
              <select value={studentFilters.parcours} onChange={e => handleStudentFilterChange('parcours', e.target.value)} disabled={!studentFilters.cursus} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm disabled:bg-gray-100"><option value="">Tous les Parcours</option>{parcours.map(p=><option key={p} value={p}>{p}</option>)}</select>
            </div>
          </div>

          {/* Sélection de l'étudiant */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Étudiant *</label>
            <select name="student_id" value={newPlacement.student_id} onChange={(e) => setNewPlacement({...newPlacement, student_id: e.target.value})} required className="w-full px-3 py-2 border border-gray-300 rounded-md">
              <option value="">Sélectionner un étudiant...</option>
              {filteredUnplacedStudents.map(s => <option key={s.id} value={s.id}>{s.name} ({s.student_department})</option>)}
            </select>
          </div>

          {/* Sélection du Stage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Stage *</label>
            <select name="internship_id" value={newPlacement.internship_id} onChange={(e) => setNewPlacement({...newPlacement, internship_id: e.target.value})} required className="w-full px-3 py-2 border border-gray-300 rounded-md">
              <option value="">Sélectionner un stage...</option>
              {internships.map(i => <option key={i.id} value={i.id}>{i.title} ({i.company_name})</option>)}
            </select>
          </div>
        </div>

        {/* --- COLONNE DE DROITE : Encadreur & Dates --- */}
        <div className="space-y-6">
          {/* Bloc de filtre pour l'encadreur */}
          <div className="p-4 border rounded-md bg-gray-50">
            <label className="font-semibold text-sm text-gray-800 mb-2 block">2. Rechercher un encadreur</label>
            <input type="text" value={supervisorSearch} onChange={e => setSupervisorSearch(e.target.value)} placeholder="Nom ou département..." className="w-full px-3 py-2 border border-gray-300 rounded-md"/>
          </div>

          {/* Sélection de l'encadreur */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Encadreur Académique *</label>
            <select name="academic_supervisor_id" value={newPlacement.academic_supervisor_id} onChange={(e) => setNewPlacement({...newPlacement, academic_supervisor_id: e.target.value})} required className="w-full px-3 py-2 border border-gray-300 rounded-md">
              <option value="">Sélectionner un encadreur...</option>
              {filteredSupervisors.map(s => <option key={s.id} value={s.id}>{s.name} ({s.department})</option>)}
            </select>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date de début *</label>
              <input type="date" name="start_date" value={newPlacement.start_date} onChange={(e) => setNewPlacement({...newPlacement, start_date: e.target.value})} required className="w-full px-3 py-2 border border-gray-300 rounded-md"/>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date de fin *</label>
              <input type="date" name="end_date" value={newPlacement.end_date} onChange={(e) => setNewPlacement({...newPlacement, end_date: e.target.value})} required className="w-full px-3 py-2 border border-gray-300 rounded-md"/>
            </div>
          </div>
        </div>
      </div>
      
      {/* --- SECTION DU BAS : Message & Boutons --- */}
      <div className="mt-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Message de validation (optionnel)</label>
        <textarea name="validation_message" value={newPlacement.validation_message} onChange={(e) => setNewPlacement({...newPlacement, validation_message: e.target.value})} rows="3" className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Message pour l'encadreur..."/>
      </div>

      <div className="flex justify-end space-x-4 pt-8 border-t mt-8">
        <button type="button" onClick={() => setShowCreateModal(false)} className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md">Annuler</button>
<button
  onClick={createPlacement}
  disabled={
    !newPlacement.student_id || 
    !newPlacement.internship_id || 
    !newPlacement.academic_supervisor_id ||
    !newPlacement.start_date ||
    !newPlacement.end_date
  }
  className="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
>
  Créer le placement
</button>      </div>
    </div>
  </div>
   )}
    </div>
  );
};

// ==================== PLACEMENT VALIDATION TAB ====================
const PlacementValidationTab = () => {
  const [pendingPlacements, setPendingPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validationModal, setValidationModal] = useState(null);
  const [validationMessage, setValidationMessage] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    fetchPendingPlacements();
  }, []);

  const fetchPendingPlacements = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        alert('Erreur d\'authentification. Veuillez vous reconnecter.');
        return;
      }
      
      const response = await axios.get(`${API}/internship-placements/pending-validation`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      console.log('Pending placements fetched:', response.data.placements); // Debug log
      setPendingPlacements(response.data.placements || []);
    } catch (error) {
      console.error('Error fetching pending placements:', error);
      if (error.response?.status === 403) {
        alert('Accès refusé. Vous devez être connecté en tant qu\'encadreur académique.');
      } else if (error.response?.status === 404) {
        console.log('Endpoint not found, possibly no pending placements');
        setPendingPlacements([]);
      } else {
        alert('Erreur lors du chargement des placements en attente');
      }
    } finally {
      setLoading(false);
    }
  };

  const validatePlacement = async (placementId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Erreur d\'authentification. Veuillez vous reconnecter.');
        return;
      }
      
      await axios.put(`${API}/internship-placements/${placementId}`, {
        status: 'validated',
        validation_message: validationMessage
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setValidationModal(null);
      setValidationMessage('');
      fetchPendingPlacements();
      alert('Placement validé avec succès!');
    } catch (error) {
      console.error('Error validating placement:', error);
      if (error.response?.status === 403) {
        alert('Accès refusé. Vous n\'avez pas les permissions nécessaires.');
      } else {
        alert('Erreur lors de la validation');
      }
    }
  };

  const rejectPlacement = async (placementId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Erreur d\'authentification. Veuillez vous reconnecter.');
        return;
      }
      
      await axios.put(`${API}/internship-placements/${placementId}`, {
        status: 'rejected',
        rejection_reason: rejectionReason
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setValidationModal(null);
      setRejectionReason('');
      fetchPendingPlacements();
      alert('Placement rejeté avec succès!');
    } catch (error) {
      console.error('Error rejecting placement:', error);
      if (error.response?.status === 403) {
        alert('Accès refusé. Vous n\'avez pas les permissions nécessaires.');
      } else {
        alert('Erreur lors du rejet');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des placements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Debug Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center">
          <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3">
            <span className="text-blue-600 text-sm">🔍</span>
          </div>
          <div>
            <h3 className="text-sm font-medium text-blue-800">Debug - Validation des Placements</h3>
            <p className="text-sm text-blue-700">
              Placements en attente: {pendingPlacements.length} | 
              Chargement terminé: {loading ? 'Non' : 'Oui'}
            </p>
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold text-gray-900">Validation des Placements</h2>
        <p className="text-gray-600">Validez ou rejetez les placements d'étudiants qui vous sont assignés</p>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Placements en attente ({pendingPlacements.length})
          </h3>
        </div>

        {pendingPlacements.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-gray-400 text-2xl">✅</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900">Aucun placement en attente</h3>
            <p className="text-gray-500 mt-2">
              Tous les placements ont été traités
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {pendingPlacements.map((placement) => (
              <div key={placement.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-semibold text-sm">
                          {placement.student_name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">
                          {placement.student_name}
                        </h4>
                        <p className="text-sm text-gray-500">
                          {placement.student_email}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <h5 className="font-medium text-gray-900">Étudiant</h5>
                        <p className="text-sm text-gray-600">
                          {placement.student_department} • {placement.student_cursus}
                        </p>
                        <p className="text-sm text-gray-600">
                          {placement.student_parcours}
                        </p>
                      </div>
                      <div>
                        <h5 className="font-medium text-gray-900">Stage</h5>
                        <p className="text-sm text-gray-600">
                          {placement.internship_title}
                        </p>
                        <p className="text-sm text-gray-600">
                          {placement.company_name}
                        </p>
                      </div>
                    </div>

                    <div className="mb-4">
                      <h5 className="font-medium text-gray-900">Période</h5>
                      <p className="text-sm text-gray-600">
                        Du {new Date(placement.start_date).toLocaleDateString()} 
                        au {new Date(placement.end_date).toLocaleDateString()}
                      </p>
                    </div>

                    {placement.validation_message && (
                      <div className="mb-4">
                        <h5 className="font-medium text-gray-900">Message de l'administrateur</h5>
                        <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                          {placement.validation_message}
                        </p>
                      </div>
                    )}

                    <div className="text-xs text-gray-500 mb-4">
                      Créé le {new Date(placement.created_at).toLocaleDateString()}
                    </div>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      onClick={() => setValidationModal({...placement, action: 'validate'})}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md"
                    >
                      Valider
                    </button>
                    <button
                      onClick={() => setValidationModal({...placement, action: 'reject'})}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md"
                    >
                      Rejeter
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Validation Modal */}
      {validationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              {validationModal.action === 'validate' ? 'Valider le placement' : 'Rejeter le placement'}
            </h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Étudiant: <span className="font-medium">{validationModal.student_name}</span>
              </p>
              <p className="text-sm text-gray-600 mb-2">
                Stage: <span className="font-medium">{validationModal.internship_title}</span>
              </p>
              <p className="text-sm text-gray-600">
                Entreprise: <span className="font-medium">{validationModal.company_name}</span>
              </p>
            </div>

            {validationModal.action === 'validate' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message de validation (optionnel)
                </label>
                <textarea
                  value={validationMessage}
                  onChange={(e) => setValidationMessage(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Message pour l'administrateur..."
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Raison du rejet *
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Expliquez pourquoi vous rejetez ce placement..."
                  required
                />
              </div>
            )}

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setValidationModal(null);
                  setValidationMessage('');
                  setRejectionReason('');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Annuler
              </button>
              <button
                onClick={() => {
                  if (validationModal.action === 'validate') {
                    validatePlacement(validationModal.id);
                  } else {
                    rejectPlacement(validationModal.id);
                  }
                }}
                disabled={validationModal.action === 'reject' && !rejectionReason.trim()}
                className={`px-4 py-2 rounded-md text-white disabled:opacity-50 ${
                  validationModal.action === 'validate' 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {validationModal.action === 'validate' ? 'Valider' : 'Rejeter'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== STUDENT MESSAGING TAB ====================
const StudentMessagingTab = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [selectedContact, setSelectedContact] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Filtres pour les étudiants
  const [filters, setFilters] = useState({
    role: '',
    search: ''
  });
  const [filteredContacts, setFilteredContacts] = useState([]);

  useEffect(() => {
    fetchConversations();
    fetchContacts();
  }, []);

  useEffect(() => {
    filterContacts();
  }, [contacts, filters]);

  // Utility function to get the other participant (not current user)
  const getOtherParticipant = (conversation) => {
    if (!conversation || !conversation.participants || !conversation.participant_names) {
      return { name: 'Utilisateur', role: 'unknown', index: 0 };
    }
    
    const currentUserId = getCurrentUserId();
    const otherParticipantIndex = conversation.participants.findIndex(p => p !== currentUserId);
    
    if (otherParticipantIndex === -1) {
      return { name: 'Utilisateur', role: 'unknown', index: 0 };
    }
    
    return {
      name: conversation.participant_names[otherParticipantIndex] || 'Utilisateur',
      role: conversation.participant_roles[otherParticipantIndex] || 'unknown',
      department: conversation.participant_departments?.[otherParticipantIndex],
      cursus: conversation.participant_cursus?.[otherParticipantIndex],
      parcours: conversation.participant_parcours?.[otherParticipantIndex],
      index: otherParticipantIndex
    };
  };

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        alert('Erreur d\'authentification. Veuillez vous reconnecter.');
        return;
      }
      
      const response = await axios.get(`${API}/conversations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      if (error.response?.status === 403) {
        alert('Accès refusé. Veuillez vous reconnecter.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        return;
      }
      
      const response = await axios.get(`${API}/users/contacts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setContacts(response.data.contacts || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      if (error.response?.status === 403) {
        alert('Accès refusé pour récupérer les contacts.');
      }
    }
  };
  const filterContacts = () => {
    let filtered = [...contacts];
    
    // Filtrer par rôle
    if (filters.role) {
      filtered = filtered.filter(contact => contact.role === filters.role);
    }
    
    // Filtrer par recherche (nom)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(contact => 
        contact.name.toLowerCase().includes(searchLower)
      );
    }
    
    setFilteredContacts(filtered);
  };

  const clearFilters = () => {
    setFilters({
      role: '',
      search: ''
    });
  };

  const fetchMessages = async (conversationId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        alert('Erreur d\'authentification. Veuillez vous reconnecter.');
        return;
      }
      
      const response = await axios.get(`${API}/conversations/${conversationId}/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      if (error.response?.status === 403) {
        alert('Accès refusé pour récupérer les messages.');
      }
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('La taille du fichier ne peut pas dépasser 5MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const uploadFile = async () => {
    if (!selectedFile) return null;

    setUploadingFile(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        alert("Erreur d'authentification. Veuillez vous reconnecter.");
        return null;
      }

      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      return response.data.file_id;
    } catch (error) {
      console.error('Error uploading file:', error);
      if (error.response?.status === 403) {
        alert("Accès refusé pour l'upload du fichier.");
      } else {
        alert("Erreur lors de l'upload du fichier");
      }
      return null;
    } finally {
      setUploadingFile(false);
    }
  };

  const sendMessage = async () => {
    if ((!newMessage.trim() && !selectedFile) || !selectedConversation) return;

    setSendingMessage(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        alert('Erreur d\'authentification. Veuillez vous reconnecter.');
        setSendingMessage(false);
        return;
      }

      let fileId = null;
      if (selectedFile) {
        fileId = await uploadFile();
        if (!fileId) {
          setSendingMessage(false);
          return;
        }
      }

      const recipientId = selectedConversation.participants.find(p => p !== getCurrentUserId());
      await axios.post(`${API}/conversations/${selectedConversation.id}/messages`, {
        recipient_id: recipientId,
        content: newMessage.trim() || `Fichier envoyé: ${selectedFile?.name}`,
        message_type: selectedFile ? 'document' : 'text',
        document_id: fileId
      });

      setNewMessage('');
      setSelectedFile(null);
      document.getElementById('student-file-input').value = '';
      fetchMessages(selectedConversation.id);
      fetchConversations();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Erreur lors de l\'envoi du message');
    } finally {
      setSendingMessage(false);
    }
  };

  const createConversation = async () => {
    if (!selectedContact) {
      alert('Veuillez sélectionner un contact');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        alert("Erreur d'authentification. Veuillez vous reconnecter.");
        return;
      }

      const response = await axios.post(`${API}/conversations`, {
        participant_ids: [selectedContact],
        title: null,
        initial_message: null
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data) {
        setShowNewConversation(false);
        setSelectedContact('');
        fetchConversations();
        alert('Conversation créée avec succès!');
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      if (error.response?.status === 403) {
        alert('Accès refusé pour créer la conversation.');
      } else {
        alert('Erreur: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const getCurrentUserId = () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.sub;
      } catch (e) {
        console.error('Error parsing token:', e);
      }
    }
    return localStorage.getItem('userId') || 'current-user-id';
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      'student': 'Étudiant',
      'academic_supervisor': 'Encadreur Académique',
      'professional_supervisor': 'Encadreur Professionnel',
      'teacher_mission': 'Enseignant en Mission',
      'admin': 'Administrateur',
      'super_admin': 'Super Administrateur'
    };
    return roleNames[role] || role;
  };

  const downloadFile = async (fileId, fileName) => {
    try {
      const response = await axios.get(`${API}/files/${fileId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Erreur lors du téléchargement du fichier');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des conversations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Messagerie Étudiant</h2>
          <p className="text-gray-600">Communiquez avec vos encadreurs et les administrateurs</p>
        </div>
        <button
          onClick={() => setShowNewConversation(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
        >
          Nouvelle Conversation
        </button>
      </div>

      <div className="flex h-[600px] bg-white rounded-lg shadow">
        {/* Sidebar - Conversations */}
        <div className="w-1/3 border-r border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Conversations</h3>
          </div>

          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                Aucune conversation
              </div>
            ) : (
              conversations.map((conversation) => (
                <div
                  key={conversation.id}
                  onClick={() => {
                    setSelectedConversation(conversation);
                    fetchMessages(conversation.id);
                  }}
                  className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                    selectedConversation?.id === conversation.id ? 'bg-blue-50 border-blue-200' : ''
                  }`}
                >
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-blue-600 font-semibold text-sm">
                        {getOtherParticipant(conversation).name?.charAt(0) || '?'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {getOtherParticipant(conversation).name || 'Utilisateur'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {getRoleDisplayName(getOtherParticipant(conversation).role)}
                      </p>
                      {/* Ajout des informations contextuelles pour les étudiants */}
                      {getOtherParticipant(conversation).role === 'student' && (
                        <div className="text-xs text-blue-600 mt-1">
                          <span className="font-medium">📚 Votre étudiant</span>
                          {conversation.participant_departments && conversation.participant_departments[1] && (
                            <div className="text-xs text-gray-500 mt-0.5">
                              {conversation.participant_departments[1]}
                              {conversation.participant_cursus && conversation.participant_cursus[1] && (
                                <span> • {conversation.participant_cursus[1]}</span>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      {conversation.last_message_content && (
                        <p className="text-sm text-gray-600 truncate mt-1">
                          {conversation.last_message_content}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col">
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-blue-600 font-semibold">
                      {getOtherParticipant(selectedConversation).name?.charAt(0) || '?'}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {getOtherParticipant(selectedConversation).name || 'Utilisateur'}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {getRoleDisplayName(getOtherParticipant(selectedConversation).role)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.sender_id === getCurrentUserId() ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                        message.sender_id === getCurrentUserId()
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-900'
                      }`}
                    >
                      <p className="text-sm">{message.content}</p>
                      
                      {message.message_type === 'document' && message.document_id && (
                        <div className="mt-2 p-2 bg-white bg-opacity-20 rounded border">
                          <div className="flex items-center">
                            <span className="text-xs mr-2">📎</span>
                            <button
                              onClick={() => downloadFile(message.document_id, 'document')}
                              className="text-xs underline hover:no-underline"
                            >
                              Télécharger le fichier
                            </button>
                          </div>
                        </div>
                      )}
                      
                      <p className={`text-xs mt-1 ${
                        message.sender_id === getCurrentUserId() ? 'text-blue-100' : 'text-gray-500'
                      }`}>
                        {new Date(message.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Message Input */}
              <div className="p-4 border-t border-gray-200">
                {selectedFile && (
                  <div className="mb-3 p-2 bg-gray-50 rounded-md flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-sm text-gray-600 mr-2">📎</span>
                      <span className="text-sm text-gray-800">{selectedFile.name}</span>
                      <span className="text-xs text-gray-500 ml-2">
                        ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedFile(null);
                        document.getElementById('student-file-input').value = '';
                      }}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Supprimer
                    </button>
                  </div>
                )}
                
                <div className="flex items-center space-x-3">
                  <input
                    id="student-file-input"
                    type="file"
                    onChange={handleFileSelect}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif"
                  />
                  <button
                    onClick={() => document.getElementById('student-file-input').click()}
                    className="p-2 text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg"
                    title="Joindre un fichier"
                  >
                    📎
                  </button>
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    placeholder="Tapez votre message..."
                    className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={sendingMessage || uploadingFile || (!newMessage.trim() && !selectedFile)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
                  >
                    {sendingMessage || uploadingFile ? 'Envoi...' : 'Envoyer'}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-gray-400 text-2xl">💬</span>
                </div>
                <h3 className="text-lg font-medium text-gray-900">Sélectionnez une conversation</h3>
                <p className="text-gray-500 mt-2">
                  Choisissez une conversation dans la liste de gauche ou créez-en une nouvelle
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Conversation Modal */}
      {showNewConversation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Nouvelle Conversation</h3>
            
            {/* Filtres pour les étudiants */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-3">🔍 Filtres</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recherche
                  </label>
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => setFilters({...filters, search: e.target.value})}
                    placeholder="Nom de l'encadreur..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type d'encadreur
                  </label>
                  <select
                    value={filters.role}
                    onChange={(e) => setFilters({...filters, role: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Tous les encadreurs</option>
                    <option value="academic_supervisor">Encadreur Académique</option>
                    <option value="professional_supervisor">Encadreur Professionnel</option>
                    <option value="teacher_mission">Enseignant en Mission</option>
                    <option value="admin">Administrateur</option>
                  </select>
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setFilters({role: '', search: ''})}
                  className="text-gray-600 hover:text-gray-800 text-sm"
                >
                  Effacer les filtres
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sélectionner un contact ({filteredContacts.length} contacts disponibles)
              </label>
              <select
                value={selectedContact}
                onChange={(e) => setSelectedContact(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Choisir un contact...</option>
                {filteredContacts.map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {contact.name} ({getRoleDisplayName(contact.role)})
                    {contact.department && ` - ${contact.department}`}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowNewConversation(false);
                  setSelectedContact('');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Annuler
              </button>
              <button
                onClick={createConversation}
                disabled={!selectedContact}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md disabled:opacity-50"
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== SUPERVISOR MESSAGING TAB ====================
const SupervisorMessagingTab = () => {
  const [filters, setFilters] = useState({ role: '', search: '' });
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [selectedContact, setSelectedContact] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    fetchConversations();
    fetchContacts();
  }, []);

   // -- LOGIQUE DE FILTRAGE AJOUTÉE --
  useEffect(() => {
    let filtered = [...contacts];
    
    if (filters.role) {
      filtered = filtered.filter(contact => contact.role === filters.role);
    }
    
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(contact => 
        contact.name.toLowerCase().includes(searchLower)
      );
    }
    
    setFilteredContacts(filtered);
  }, [contacts, filters]);
  // -- FIN DES AJOUTS --

  // Utility function to get the other participant (not current user)
  const getOtherParticipant = (conversation) => {
    if (!conversation || !conversation.participants || !conversation.participant_names) {
      return { name: 'Utilisateur', role: 'unknown', index: 0 };
    }
    
    const currentUserId = getCurrentUserId();
    const otherParticipantIndex = conversation.participants.findIndex(p => p !== currentUserId);
    
    if (otherParticipantIndex === -1) {
      return { name: 'Utilisateur', role: 'unknown', index: 0 };
    }
    
    return {
      name: conversation.participant_names[otherParticipantIndex] || 'Utilisateur',
      role: conversation.participant_roles[otherParticipantIndex] || 'unknown',
      department: conversation.participant_departments?.[otherParticipantIndex],
      cursus: conversation.participant_cursus?.[otherParticipantIndex],
      parcours: conversation.participant_parcours?.[otherParticipantIndex],
      index: otherParticipantIndex
    };
  };

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        alert('Erreur d\'authentification. Veuillez vous reconnecter.');
        return;
      }
      
      const response = await axios.get(`${API}/conversations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      if (error.response?.status === 403) {
        alert('Accès refusé. Veuillez vous reconnecter.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        return;
      }
      
      const response = await axios.get(`${API}/users/contacts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setContacts(response.data.contacts || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      if (error.response?.status === 403) {
        alert('Accès refusé pour récupérer les contacts.');
      }
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        alert('Erreur d\'authentification. Veuillez vous reconnecter.');
        return;
      }
      
      const response = await axios.get(`${API}/conversations/${conversationId}/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      if (error.response?.status === 403) {
        alert('Accès refusé pour récupérer les messages.');
      }
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('La taille du fichier ne peut pas dépasser 5MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const uploadFile = async () => {
    if (!selectedFile) return null;

    setUploadingFile(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        alert("Erreur d'authentification. Veuillez vous reconnecter.");
        return null;
      }

      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      return response.data.file_id;
    } catch (error) {
      console.error('Error uploading file:', error);
      if (error.response?.status === 403) {
        alert("Accès refusé pour l'upload du fichier.");
      } else {
        alert("Erreur lors de l'upload du fichier");
      }
      return null;
    } finally {
      setUploadingFile(false);
    }
  };

  const sendMessage = async () => {
    if ((!newMessage.trim() && !selectedFile) || !selectedConversation) return;

    setSendingMessage(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        alert('Erreur d\'authentification. Veuillez vous reconnecter.');
        setSendingMessage(false);
        return;
      }

      let fileId = null;
      if (selectedFile) {
        fileId = await uploadFile();
        if (!fileId) {
          setSendingMessage(false);
          return;
        }
      }

      const recipientId = selectedConversation.participants.find(p => p !== getCurrentUserId());
      await axios.post(`${API}/conversations/${selectedConversation.id}/messages`, {
        recipient_id: recipientId,
        content: newMessage.trim() || `Fichier envoyé: ${selectedFile?.name}`,
        message_type: selectedFile ? 'document' : 'text',
        document_id: fileId
      });

      setNewMessage('');
      setSelectedFile(null);
      document.getElementById('supervisor-file-input').value = '';
      fetchMessages(selectedConversation.id);
      fetchConversations();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Erreur lors de l\'envoi du message');
    } finally {
      setSendingMessage(false);
    }
  };

  const createConversation = async () => {
    if (!selectedContact) {
      alert('Veuillez sélectionner un contact');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        alert("Erreur d'authentification. Veuillez vous reconnecter.");
        return;
      }

      const response = await axios.post(`${API}/conversations`, {
        participant_ids: [selectedContact],
        title: null,
        initial_message: null
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data) {
        setShowNewConversation(false);
        setSelectedContact('');
        fetchConversations();
        alert('Conversation créée avec succès!');
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      if (error.response?.status === 403) {
        alert('Accès refusé pour créer la conversation.');
      } else {
        alert('Erreur: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const getCurrentUserId = () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.sub;
      } catch (e) {
        console.error('Error parsing token:', e);
      }
    }
    return localStorage.getItem('userId') || 'current-user-id';
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      'student': 'Étudiant',
      'academic_supervisor': 'Encadreur Académique',
      'professional_supervisor': 'Encadreur Professionnel',
      'teacher_mission': 'Enseignant en Mission',
      'admin': 'Administrateur',
      'super_admin': 'Super Administrateur'
    };
    return roleNames[role] || role;
  };

  const downloadFile = async (fileId, fileName) => {
    try {
      const response = await axios.get(`${API}/files/${fileId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Erreur lors du téléchargement du fichier');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des conversations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Messagerie</h2>
          <p className="text-gray-600">Communiquez avec vos étudiants et les administrateurs</p>
        </div>
        <button
          onClick={() => setShowNewConversation(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
        >
          Nouvelle Conversation
        </button>
      </div>

      <div className="flex h-[600px] bg-white rounded-lg shadow">
        {/* Sidebar - Conversations */}
        <div className="w-1/3 border-r border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Conversations</h3>
          </div>

          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                Aucune conversation
              </div>
            ) : (
              conversations.map((conversation) => (
                <div
                  key={conversation.id}
                  onClick={() => {
                    setSelectedConversation(conversation);
                    fetchMessages(conversation.id);
                  }}
                  className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                    selectedConversation?.id === conversation.id ? 'bg-blue-50 border-blue-200' : ''
                  }`}
                >
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-blue-600 font-semibold text-sm">
                        {getOtherParticipant(conversation).name?.charAt(0) || '?'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {getOtherParticipant(conversation).name || 'Utilisateur'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {getRoleDisplayName(getOtherParticipant(conversation).role)}
                      </p>
                      {/* Ajout des informations contextuelles pour les étudiants */}
                      {getOtherParticipant(conversation).role === 'student' && (
                        <div className="text-xs text-blue-600 mt-1">
                          <span className="font-medium">📚 Votre étudiant</span>
                          {conversation.participant_departments && conversation.participant_departments[1] && (
                            <div className="text-xs text-gray-500 mt-0.5">
                              {conversation.participant_departments[1]}
                              {conversation.participant_cursus && conversation.participant_cursus[1] && (
                                <span> • {conversation.participant_cursus[1]}</span>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      {conversation.last_message_content && (
                        <p className="text-sm text-gray-600 truncate mt-1">
                          {conversation.last_message_content}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col">
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-blue-600 font-semibold">
                      {getOtherParticipant(selectedConversation).name?.charAt(0) || '?'}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {getOtherParticipant(selectedConversation).name || 'Utilisateur'}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {getRoleDisplayName(getOtherParticipant(selectedConversation).role)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.sender_id === getCurrentUserId() ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                        message.sender_id === getCurrentUserId()
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-900'
                      }`}
                    >
                      <p className="text-sm">{message.content}</p>
                      
                      {message.message_type === 'document' && message.document_id && (
                        <div className="mt-2 p-2 bg-white bg-opacity-20 rounded border">
                          <div className="flex items-center">
                            <span className="text-xs mr-2">📎</span>
                            <button
                              onClick={() => downloadFile(message.document_id, 'document')}
                              className="text-xs underline hover:no-underline"
                            >
                              Télécharger le fichier
                            </button>
                          </div>
                        </div>
                      )}
                      
                      <p className={`text-xs mt-1 ${
                        message.sender_id === getCurrentUserId() ? 'text-blue-100' : 'text-gray-500'
                      }`}>
                        {new Date(message.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Message Input */}
              <div className="p-4 border-t border-gray-200">
                {selectedFile && (
                  <div className="mb-3 p-2 bg-gray-50 rounded-md flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-sm text-gray-600 mr-2">📎</span>
                      <span className="text-sm text-gray-800">{selectedFile.name}</span>
                      <span className="text-xs text-gray-500 ml-2">
                        ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedFile(null);
                        document.getElementById('supervisor-file-input').value = '';
                      }}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Supprimer
                    </button>
                  </div>
                )}
                
                <div className="flex items-center space-x-3">
                  <input
                    id="supervisor-file-input"
                    type="file"
                    onChange={handleFileSelect}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif"
                  />
                  <button
                    onClick={() => document.getElementById('supervisor-file-input').click()}
                    className="p-2 text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg"
                    title="Joindre un fichier"
                  >
                    📎
                  </button>
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    placeholder="Tapez votre message..."
                    className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={sendingMessage || uploadingFile || (!newMessage.trim() && !selectedFile)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
                  >
                    {sendingMessage || uploadingFile ? 'Envoi...' : 'Envoyer'}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-gray-400 text-2xl">💬</span>
                </div>
                <h3 className="text-lg font-medium text-gray-900">Sélectionnez une conversation</h3>
                <p className="text-gray-500 mt-2">
                  Choisissez une conversation dans la liste de gauche ou créez-en une nouvelle
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Conversation Modal */}
      {showNewConversation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Nouvelle Conversation</h3>
            
            {/* Filtres pour les encadreurs*/}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-3">🔍 Filtres</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recherche
                  </label>
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => setFilters({...filters, search: e.target.value})}
                    placeholder="Nom de l'utilisateur..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type d'utilisateur
                  </label>
                  <select
                    value={filters.role}
                    onChange={(e) => setFilters({...filters, role: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Tous les utilisateurs</option>
                    <option value="student">Étudiant</option>
                    <option value="academic_supervisor">Encadreur Académique</option>
                    <option value="teacher_mission">Enseignant en Mission</option>
                    <option value="admin">Administrateur</option>
                  </select>
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setFilters({role: '', search: ''})}
                  className="text-gray-600 hover:text-gray-800 text-sm"
                >
                  Effacer les filtres
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sélectionner un contact ({filteredContacts.length} contacts disponibles)
              </label>
              <select
                value={selectedContact}
                onChange={(e) => setSelectedContact(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Choisir un contact...</option>
                {filteredContacts.map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {contact.name} ({getRoleDisplayName(contact.role)})
                    {contact.department && ` - ${contact.department}`}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowNewConversation(false);
                  setSelectedContact('');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Annuler
              </button>
              <button
                onClick={createConversation}
                disabled={!selectedContact}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md disabled:opacity-50"
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ========================================================================
// COMPOSANT POUR L'ÉVALUATION DES STAGIAIRES
// ========================================================================
const ProfessionalSupervisorEvaluationTab = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    technical_skills_rating: 3,
    communication_skills_rating: 3,
    autonomy_rating: 3,
    overall_rating: 3,
    general_comment: '',
  });
  const [submitStatus, setSubmitStatus] = useState({ loading: false, error: '', success: '' });
  // States pour les filtres
const [filters, setFilters] = useState({ search: '', department: '', cursus: '', parcours: '' });
const [departments, setDepartments] = useState([]);
const [cursus, setCursus] = useState([]);
const [parcours, setParcours] = useState([]);

useEffect(() => {
  fetchSupervisedStudents();
  // On charge les listes pour les filtres
  fetchDepartments();
}, []);

  const fetchSupervisedStudents = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/supervisions/my-students`);
      setStudents(response.data);
    } catch (err) {
      setError('Erreur lors de la récupération des étudiants supervisés.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openEvaluationModal = (studentData) => {
    setSelectedStudent(studentData);
    // Reset form for new evaluation
    setFormData({
      application_id: studentData.application_id,
      technical_skills_rating: 3,
      communication_skills_rating: 3,
      autonomy_rating: 3,
      overall_rating: 3,
      general_comment: '',
    });
    setIsModalOpen(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: name.includes('rating') ? parseInt(value) : value }));
  };

  const handleSubmitEvaluation = async (e) => {
    e.preventDefault();
    setSubmitStatus({ loading: true, error: '', success: '' });
    try {
      await axios.post(`${API}/evaluations`, formData);
      setSubmitStatus({ loading: false, error: '', success: 'Évaluation soumise avec succès !' });
      setTimeout(() => {
        setIsModalOpen(false);
        // Optionnel: rafraîchir la liste pour marquer l'étudiant comme évalué
        fetchSupervisedStudents(); 
      }, 2000);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Une erreur est survenue.';
      setSubmitStatus({ loading: false, error: errorMessage, success: '' });
    }
  };

  const fetchDepartments = async () => { try { const res = await axios.get(`${API}/structure/departments`); setDepartments(res.data.departments); } catch (e) { console.error(e); }};
const fetchCursus = async (d) => { try { const res = await axios.get(`${API}/structure/cursus?department=${d}`); setCursus(res.data.cursus); } catch (e) { console.error(e); }};
const fetchParcours = async (d, c) => { try { const res = await axios.get(`${API}/structure/parcours?department=${d}&cursus=${c}`); setParcours(res.data.parcours); } catch (e) { console.error(e); }};

const handleFilterChange = (key, value) => {
  setFilters(prev => {
    const newFilters = { ...prev, [key]: value };
    
    if (key === 'department') {
      // Si on change le département, on réinitialise cursus ET parcours
      newFilters.cursus = '';
      newFilters.parcours = '';
      setCursus([]); // Vider immédiatement la liste
      setParcours([]); // Vider immédiatement la liste
      if (value) {
        fetchCursus(value); // Charger les nouveaux cursus
      }
    } else if (key === 'cursus') {
      // Si on change le cursus, on réinitialise seulement le parcours
      newFilters.parcours = '';
      setParcours([]); // Vider immédiatement la liste
      if (value) {
        // Utiliser newFilters.department car filters.department n'est pas encore à jour
        fetchParcours(newFilters.department, value); 
      }
    }
    
    return newFilters;
  });
};

const clearFilters = () => {
  setFilters({ search: '', department: '', cursus: '', parcours: '' });
};
  
  if (loading) return <div className="p-6">Chargement des étudiants...</div>;
  if (error) return <div className="p-6 text-red-600">{error}</div>;

  const renderRatingInput = (label, name) => (
    <div>
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <select 
        name={name} 
        value={formData[name]} 
        onChange={handleFormChange}
        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
      >
        {[1, 2, 3, 4, 5].map(v => (
          <option key={v} value={v}>
            {v} - {['Très Faible', 'Faible', 'Moyen', 'Bon', 'Excellent'][v - 1]}
          </option>
        ))}
      </select>
    </div>
  );

  // Appliquer les filtres avant de rendre la liste
const filteredStudents = students.filter(studentData => {
  const student = studentData.student_info;
  if (!student) return false;

  const searchLower = filters.search.toLowerCase();
  const matchesSearch = !filters.search || 
    student.name.toLowerCase().includes(searchLower) ||
    student.email.toLowerCase().includes(searchLower);
  
  const matchesDepartment = !filters.department || student.student_department === filters.department;
  const matchesCursus = !filters.cursus || student.student_cursus === filters.cursus;
  const matchesParcours = !filters.parcours || student.student_parcours === filters.parcours;

  return matchesSearch && matchesDepartment && matchesCursus && matchesParcours;
});

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Évaluation des Stagiaires</h2>
      <div className="bg-white rounded-lg shadow p-6 mb-6">
  <h3 className="text-lg font-semibold text-gray-900 mb-4">🔍 Filtres</h3>
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Recherche par nom</label>
      <input 
        type="text"
        value={filters.search}
        onChange={(e) => handleFilterChange('search', e.target.value)}
        placeholder="Nom ou email..."
        className="w-full border border-gray-300 rounded-md px-3 py-2"
      />
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Département</label>
      <select value={filters.department} onChange={(e) => handleFilterChange('department', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2">
        <option value="">Tous</option>
        {departments.map((d) => <option key={d} value={d}>{d}</option>)}
      </select>
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Cursus</label>
      <select value={filters.cursus} onChange={(e) => handleFilterChange('cursus', e.target.value)} disabled={!filters.department} className="w-full border border-gray-300 rounded-md px-3 py-2 disabled:bg-gray-100">
        <option value="">Tous</option>
        {cursus.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Parcours</label>
      <select value={filters.parcours} onChange={(e) => handleFilterChange('parcours', e.target.value)} disabled={!filters.cursus} className="w-full border border-gray-300 rounded-md px-3 py-2 disabled:bg-gray-100">
        <option value="">Tous</option>
        {parcours.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
    </div>
  </div>
  <div className="mt-4 flex justify-end">
    <button onClick={clearFilters} className="text-gray-600 hover:text-gray-800 text-sm">Effacer les filtres</button>
  </div>
</div>
     {filteredStudents.length === 0 ? (
  <p className="text-gray-600 text-center py-8">
    {students.length === 0 
      ? "Vous n'avez actuellement aucun étudiant à évaluer."
      : "Aucun étudiant ne correspond à vos critères de filtre."}
  </p>
) : (
  <div className="bg-white shadow rounded-lg overflow-hidden">
    <ul className="divide-y divide-gray-200">
      {filteredStudents.map(({ student_info, application_id }) => (
        <li key={student_info.id} className="px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-lg font-semibold text-gray-900">{student_info.name}</p>
            <p className="text-sm text-gray-500">{student_info.email}</p>
            <p className="text-sm text-blue-600"> {student_info.student_department} • {student_info.student_cursus} • {student_info.student_parcours}</p>
          </div>
          <button 
            onClick={() => openEvaluationModal({ student_info, application_id })}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Évaluer
          </button>
        </li>
      ))}
    </ul>
  </div>
)}

      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center">
          <div className="relative mx-auto p-8 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <h3 className="text-xl font-semibold mb-4">Évaluer {selectedStudent?.student_info.name}</h3>
            <form onSubmit={handleSubmitEvaluation} className="space-y-4">
              {renderRatingInput("Compétences Techniques", "technical_skills_rating")}
              {renderRatingInput("Compétences en Communication", "communication_skills_rating")}
              {renderRatingInput("Autonomie et Initiative", "autonomy_rating")}
              {renderRatingInput("Appréciation Globale", "overall_rating")}
              <div>
                <label className="block text-sm font-medium text-gray-700">Commentaire Général</label>
                <textarea 
                  name="general_comment" 
                  value={formData.general_comment}
                  onChange={handleFormChange}
                  rows="4"
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div className="flex justify-end space-x-4 mt-6">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Annuler</button>
                <button type="submit" disabled={submitStatus.loading} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-green-300">
                  {submitStatus.loading ? 'Envoi...' : 'Soumettre'}
                </button>
              </div>
              {submitStatus.success && <p className="text-green-600 mt-2">{submitStatus.success}</p>}
              {submitStatus.error && <p className="text-red-600 mt-2">{submitStatus.error}</p>}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== ADMIN ASSIGNMENTS TAB ====================
const AdminAssignmentsTab = () => {
  const [assignments, setAssignments] = useState([]);
  const [students, setStudents] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [newAssignment, setNewAssignment] = useState({
    student_id: '',
    academic_supervisor_id: ''
  });
  const [filters, setFilters] = useState({
    department: '',
    cursus: '',
    parcours: '',
    search: ''
  });
const [studentModalFilters, setStudentModalFilters] = useState({ department: '', cursus: '', parcours: '', search: '' });
const [supervisorModalFilters, setSupervisorModalFilters] = useState({ department: '', search: '' });  
  // États pour les listes de filtres
  const [departments, setDepartments] = useState([]);
  const [cursus, setCursus] = useState([]);
  const [parcours, setParcours] = useState([]);

  useEffect(() => {
    fetchAssignments();
    fetchStudents();
    fetchSupervisors();
    fetchDepartments();
  }, []);

  const fetchAssignments = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        alert('Erreur d\'authentification. Veuillez vous reconnecter.');
        return;
      }
      
      const response = await axios.get(`${API}/student-assignments`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setAssignments(response.data.assignments);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      if (error.response?.status === 403) {
        alert('Accès refusé. Vérifiez vos permissions.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        alert('Erreur d\'authentification. Veuillez vous reconnecter.');
        return;
      }
      
      const response = await axios.get(`${API}/student-assignments/unassigned`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      console.log('Unassigned students fetched:', response.data.unassigned_students); // Debug log
      setStudents(response.data.unassigned_students || []);
    } catch (error) {
      console.error('Error fetching unassigned students:', error);
      if (error.response?.status === 403) {
        alert('Accès refusé. Vérifiez vos permissions.');
      } else {
        alert('Erreur lors du chargement des étudiants non assignés');
      }
    }
  };

  const fetchSupervisors = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        alert('Erreur d\'authentification. Veuillez vous reconnecter.');
        return;
      }
      
      const response = await axios.get(`${API}/academic-supervisors`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      console.log('Academic supervisors fetched:', response.data.supervisors); // Debug log
      setSupervisors(response.data.supervisors || []);
    } catch (error) {
      console.error('Error fetching academic supervisors:', error);
      if (error.response?.status === 403) {
        alert('Accès refusé. Vérifiez vos permissions.');
      } else {
        alert('Erreur lors du chargement des encadreursacadémiques');
      }
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/structure/departments`);
      setDepartments(response.data.departments);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchCursus = async (department) => {
    try {
      const response = await axios.get(`${API}/structure/cursus?department=${department}`);
      setCursus(response.data.cursus);
    } catch (error) {
      console.error('Error fetching cursus:', error);
    }
  };

  const fetchParcours = async (department, cursusValue) => {
    try {
      const response = await axios.get(`${API}/structure/parcours?department=${department}&cursus=${cursusValue}`);
      setParcours(response.data.parcours);
    } catch (error) {
      console.error('Error fetching parcours:', error);
    }
  };

  const handleDepartmentChange = (department) => {
    setFilters({ ...filters, department, cursus: '', parcours: '' });
    setCursus([]);
    setParcours([]);
    if (department) {
      fetchCursus(department);
    }
  };

  const handleCursusChange = (cursusValue) => {
    setFilters({ ...filters, cursus: cursusValue, parcours: '' });
    setParcours([]);
    if (cursusValue && filters.department) {
      fetchParcours(filters.department, cursusValue);
    }
  };

  const createAssignment = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        alert('Erreur d\'authentification. Veuillez vous reconnecter.');
        return;
      }
      
      await axios.post(`${API}/student-assignments`, newAssignment, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setShowCreateModal(false);
      setNewAssignment({ student_id: '', academic_supervisor_id: '' });
      fetchAssignments();
      fetchStudents(); // Refresh unassigned students
      alert('Affectation créée avec succès!');
    } catch (error) {
      console.error('Error creating assignment:', error);
      if (error.response?.status === 403) {
        alert('Accès refusé. Vérifiez vos permissions.');
      } else if (error.response?.data?.detail) {
        alert(`Erreur: ${error.response.data.detail}`);
      } else {
        alert('Erreur lors de la création de l\'affectation');
      }
    }
  };

const updateAssignment = async () => {
  if (!editingAssignment || !newAssignment.academic_supervisor_id) return;
  try {
    const token = localStorage.getItem('token');
    if (!token) { return; }
    
    // On appelle la NOUVELLE route POST avec du JSON simple
    await axios.post(
      `${API}/student-assignments/${editingAssignment.id}/assign-academic`,
      { academic_supervisor_id: newAssignment.academic_supervisor_id },
      { headers: { 'Authorization': `Bearer ${token}` } }
    );
    
    alert('Affectation mise à jour avec succès!');
    setShowEditModal(false);
    fetchAssignments();
  } catch (error) {
    const errorMessage = error.response?.data?.detail || 'Une erreur est survenue.';
    alert(`Erreur : ${errorMessage}`);
  }
};
  const deleteAssignment = async (assignmentId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette affectation?')) {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          console.error('No token found');
          alert('Erreur d\'authentification. Veuillez vous reconnecter.');
          return;
        }
        
        await axios.delete(`${API}/student-assignments/${assignmentId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        fetchAssignments();
        fetchStudents(); // Refresh unassigned students
        alert('Affectation supprimée avec succès!');
      } catch (error) {
        console.error('Error deleting assignment:', error);
        if (error.response?.status === 403) {
          alert('Accès refusé. Vérifiez vos permissions.');
        } else {
          alert('Erreur lors de la suppression de l\'affectation');
        }
      }
    }
  };

  const openEditModal = (assignment) => {
    setEditingAssignment(assignment);
    setNewAssignment({
      student_id: assignment.student_id,
      academic_supervisor_id: assignment.academic_supervisor_id
    });
    setShowEditModal(true);
  };

  const clearFilters = () => {
    setFilters({ department: '', cursus: '', parcours: '', search: '' });
    setCursus([]);
    setParcours([]);
  };

  // Filtrer les affectations
  const filteredAssignments = assignments.filter(assignment => {
    const matchesSearch = !filters.search || 
      assignment.student_name.toLowerCase().includes(filters.search.toLowerCase()) ||
      assignment.student_email.toLowerCase().includes(filters.search.toLowerCase()) ||
      assignment.academic_supervisor_name.toLowerCase().includes(filters.search.toLowerCase()) ||
      assignment.academic_supervisor_email.toLowerCase().includes(filters.search.toLowerCase());
    
    const matchesDepartment = !filters.department || assignment.student_department === filters.department;
    const matchesCursus = !filters.cursus || assignment.student_cursus === filters.cursus;
    const matchesParcours = !filters.parcours || assignment.student_parcours === filters.parcours;
    
    return matchesSearch && matchesDepartment && matchesCursus && matchesParcours;
  });

  // Filtrer les étudiants non affectés selon les filtres actifs
  const unassignedStudents = students.filter(student => {
    const matchesDepartment = !filters.department || student.student_department === filters.department;
    const matchesCursus = !filters.cursus || student.student_cursus === filters.cursus;
    const matchesParcours = !filters.parcours || student.student_parcours === filters.parcours;
    const matchesSearch = !filters.search || 
      student.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      student.email.toLowerCase().includes(filters.search.toLowerCase()) ||
      (student.student_department && student.student_department.toLowerCase().includes(filters.search.toLowerCase()));
    
    return matchesDepartment && matchesCursus && matchesParcours && matchesSearch;
  });

  // Filtrer les encadreursselon les filtres actifs
  const filteredSupervisors = supervisors.filter(supervisor => {
    const matchesDepartment = !filters.department || supervisor.department === filters.department;
    const matchesSearch = !filters.search || 
      supervisor.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      supervisor.email.toLowerCase().includes(filters.search.toLowerCase()) ||
      (supervisor.department && supervisor.department.toLowerCase().includes(filters.search.toLowerCase()));
    
    return matchesDepartment && matchesSearch;
  });

const filteredUnassignedStudentsForModal = students.filter(student => {
  const filters = studentModalFilters; // Utilise les filtres étudiants
  const searchLower = filters.search.toLowerCase();
  return (
    (!filters.search || (student.name && student.name.toLowerCase().includes(searchLower)) || (student.email && student.email.toLowerCase().includes(searchLower))) &&
    (!filters.department || student.student_department === filters.department) &&
    (!filters.cursus || student.student_cursus === filters.cursus) &&
    (!filters.parcours || student.student_parcours === filters.parcours)
  );
});

const filteredSupervisorsForModal = supervisors.filter(supervisor => {
  const filters = supervisorModalFilters; // Utilise les filtres encadreurs
  const searchLower = filters.search.toLowerCase();
  return (
    (!filters.search || (supervisor.name && supervisor.name.toLowerCase().includes(searchLower)) || (supervisor.email && supervisor.email.toLowerCase().includes(searchLower))) &&
    (!filters.department || supervisor.department === filters.department)
  );
});

// NOUVEAU: Fonctions pour gérer les filtres de la modale
const handleModalFilterChange = (key, value) => {
  setModalFilters(prev => {
    const newFilters = { ...prev, [key]: value };

    // Logique de mise à jour des listes déroulantes dépendantes
    if (key === 'department') {
      newFilters.cursus = '';
      newFilters.parcours = '';
      setCursus([]); // Utilise le même état que les filtres principaux
      setParcours([]);
      if (value) fetchCursus(value); // Réutilise la fonction de fetch
    } else if (key === 'cursus') {
      newFilters.parcours = '';
      setParcours([]);
      if (value) fetchParcours(newFilters.department, value); // Réutilise la fonction de fetch
    }

    return newFilters;
  });
};

const clearModalFilters = () => {
  setModalFilters({ department: '', cursus: '', parcours: '', search: '' });
};

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des affectations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Affectation des Encadreurs Académiques</h2>
          <p className="text-gray-600">Gérez les affectations permanentes des étudiants aux encadreurs académiques</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => {
              fetchStudents();
              fetchSupervisors();
              fetchAssignments();
            }}
            className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-lg flex items-center space-x-2"
          >
            <span>🔄</span>
            <span>Actualiser</span>
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
          >
            <span>➕</span>
            <span>Nouvelle Affectation</span>
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">🔍 Filtres</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Recherche</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Nom étudiant, email, encadreur..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Département</label>
            <select
              value={filters.department}
              onChange={(e) => handleDepartmentChange(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tous les départements</option>
              {departments.map((dept) => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cursus</label>
            <select
              value={filters.cursus}
              onChange={(e) => handleCursusChange(e.target.value)}
              disabled={!filters.department}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            >
              <option value="">Tous les cursus</option>
              {cursus.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Parcours</label>
            <select
              value={filters.parcours}
              onChange={(e) => setFilters({ ...filters, parcours: e.target.value })}
              disabled={!filters.cursus}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            >
              <option value="">Tous les parcours</option>
              {parcours.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={clearFilters}
            className="text-gray-600 hover:text-gray-800 text-sm"
          >
            Effacer les filtres
          </button>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-3 gap-2 sm:gap-6">
        <div className="bg-white rounded-lg shadow p-3 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-500">Étudiants assignés</p>
              <p className="text-lg sm:text-2xl font-bold text-green-600">{assignments.length}</p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600 text-sm sm:text-xl">👨‍🎓</span>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-3 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-500">Étudiants non assignés</p>
              <p className="text-lg sm:text-2xl font-bold text-orange-600">{unassignedStudents.length}</p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <span className="text-orange-600 text-sm sm:text-xl">⚠️</span>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-3 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-500">encadreursactifs</p>
              <p className="text-lg sm:text-2xl font-bold text-blue-600">{filteredSupervisors.length}</p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 text-sm sm:text-xl">👨‍🏫</span>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des affectations */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Affectations ({filteredAssignments.length})
          </h3>
        </div>

        {filteredAssignments.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-gray-400 text-2xl">👨‍🏫</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900">
              {filters.search || filters.department || filters.cursus || filters.parcours 
                ? 'Aucune affectation trouvée' 
                : 'Aucune affectation'}
            </h3>
            <p className="text-gray-500 mt-2">
              {filters.search || filters.department || filters.cursus || filters.parcours 
                ? 'Essayez d\'ajuster vos filtres' 
                : 'Commencez par créer une nouvelle affectation'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Étudiant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Département/Cursus
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    encadreur Académique
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date d'Affectation
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAssignments.map((assignment) => (
                  <tr key={assignment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 font-semibold text-sm">
                            {assignment.student_name.charAt(0)}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {assignment.student_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {assignment.student_email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {assignment.student_department}
                      </div>
                      <div className="text-sm text-gray-500">
                        {assignment.student_cursus} • {assignment.student_parcours}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <span className="text-green-600 font-semibold text-xs">
                            {assignment.academic_supervisor_name.charAt(0)}
                          </span>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {assignment.academic_supervisor_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {assignment.academic_supervisor_email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(assignment.assigned_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => openEditModal(assignment)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        Modifier
                      </button>
                      <button
                        onClick={() => deleteAssignment(assignment.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Création */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Nouvelle Affectation</h3>
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
     <h4 className="font-medium text-gray-900 mb-3">🔍 Filtrer les étudiants</h4>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <input type="text" value={studentModalFilters.search} onChange={(e) => setStudentModalFilters({...studentModalFilters, search: e.target.value})} placeholder="Rechercher étudiant..." className="w-full border border-gray-300 rounded-md px-3 py-2" />
      <select value={studentModalFilters.department} onChange={(e) => { setStudentModalFilters({...studentModalFilters, department: e.target.value, cursus: '', parcours: ''}); if (e.target.value) fetchCursus(e.target.value); else setCursus([]); }} className="w-full border border-gray-300 rounded-md px-3 py-2">
        <option value="">Département</option>
        {departments.map(d => <option key={d} value={d}>{d}</option>)}
      </select>
      <select value={studentModalFilters.cursus} onChange={(e) => { setStudentModalFilters({...studentModalFilters, cursus: e.target.value, parcours: ''}); if (e.target.value) fetchParcours(studentModalFilters.department, e.target.value); else setParcours([]); }} disabled={!studentModalFilters.department} className="w-full border border-gray-300 rounded-md px-3 py-2">
        <option value="">Cursus</option>
        {cursus.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
      <select value={studentModalFilters.parcours} onChange={(e) => setStudentModalFilters({...studentModalFilters, parcours: e.target.value})} disabled={!studentModalFilters.cursus} className="w-full border border-gray-300 rounded-md px-3 py-2">
        <option value="">Parcours</option>
        {parcours.map(p => <option key={p} value={p}>{p}</option>)}
      </select>
    </div>

     {/* Filtres pour les encadreurs */}
  <div className="p-4 bg-gray-50 rounded-lg border">
    <h4 className="font-medium text-gray-900 mb-3">🔍 Filtrer les encadreurs</h4>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <input type="text" value={supervisorModalFilters.search} onChange={(e) => setSupervisorModalFilters({...supervisorModalFilters, search: e.target.value})} placeholder="Rechercher encadreur..." className="w-full border border-gray-300 rounded-md px-3 py-2" />
      <select value={supervisorModalFilters.department} onChange={(e) => setSupervisorModalFilters({...supervisorModalFilters, department: e.target.value})} className="w-full border border-gray-300 rounded-md px-3 py-2">
        <option value="">Département</option>
        {departments.map(d => <option key={d} value={d}>{d}</option>)}
      </select>
    </div>
  </div>
</div>
            
      <div className="space-y-4">
<div>
  <label className="block text-sm font-medium text-gray-700 mb-2">
    Étudiant ({filteredUnassignedStudentsForModal.length})
  </label>
  <select
    value={newAssignment.student_id}
    onChange={(e) => setNewAssignment({...newAssignment, student_id: e.target.value})}
    className="w-full border border-gray-300 rounded-md px-3 py-2"
  >
    <option value="">Sélectionner un étudiant...</option>
    {filteredUnassignedStudentsForModal.map((student) => (
      <option key={student.id} value={student.id}>
        {student.name} ({student.student_parcours || student.student_cursus})
      </option>
    ))}
  </select>
</div>

<div>
  <label className="block text-sm font-medium text-gray-700 mb-2">
    Encadreur Académique ({filteredSupervisorsForModal.length})
  </label>
  <select
    value={newAssignment.academic_supervisor_id}
    onChange={(e) => setNewAssignment({...newAssignment, academic_supervisor_id: e.target.value})}
    className="w-full border border-gray-300 rounded-md px-3 py-2"
  >
    <option value="">Sélectionner un encadreur...</option>
    {filteredSupervisorsForModal.map((supervisor) => (
      <option key={supervisor.id} value={supervisor.id}>
        {supervisor.name} ({supervisor.department || 'N/A'})
      </option>
    ))}
  </select>
</div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewAssignment({ student_id: '', academic_supervisor_id: '' });
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Annuler
              </button>
              <button
                onClick={createAssignment}
                disabled={!newAssignment.student_id || !newAssignment.academic_supervisor_id}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md disabled:opacity-50"
              >
                Créer l'Affectation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modification */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Modifier l'Affectation</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Étudiant
                </label>
                <input
                  type="text"
                  value={editingAssignment?.student_name || ''}
                  disabled
                  className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100 text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nouveau encadreur Académique
                </label>
                <select
                  value={newAssignment.academic_supervisor_id}
                  onChange={(e) => setNewAssignment({...newAssignment, academic_supervisor_id: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Sélectionner un encadreur...</option>
                  {filteredSupervisors.length > 0 ? (
                    filteredSupervisors.map((supervisor) => (
                      <option key={supervisor.id} value={supervisor.id}>
                        {supervisor.name} ({supervisor.department || 'Département non spécifié'})
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      {filters.department || filters.search ? 'Aucun encadreur trouvé avec ces filtres' : 'Aucun encadreur trouvé'}
                    </option>
                  )}
                </select>
                {filteredSupervisors.length === 0 && (
                  <p className="text-sm text-gray-500 mt-1">
                    {filters.department || filters.search ? 
                      `Aucun encadreur trouvé avec les filtres actifs (${supervisors.length} encadreursau total)` : 
                      'Chargement des encadreursacadémiques...'}
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingAssignment(null);
                  setNewAssignment({ student_id: '', academic_supervisor_id: '' });
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Annuler
              </button>
              <button
                onClick={updateAssignment}
                disabled={!newAssignment.academic_supervisor_id}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md disabled:opacity-50"
              >
                Sauvegarder
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== ADMIN DOCUMENTS TAB ====================
const AdminDocumentsTab = () => {
  const [documents, setDocuments] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    fileType: '',
    student: ''
  });
  const [downloadingDoc, setDownloadingDoc] = useState(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        alert('Erreur d\'authentification. Veuillez vous reconnecter.');
        return;
      }

      const response = await axios.get(`${API}/admin/documents`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      console.log('Documents fetched:', response.data);
      
      // Handle the new response structure {documents: [...]}
      const documentsArray = response.data.documents || response.data || [];
      setDocuments(documentsArray);
      
      // Extract unique students from documents
      const uniqueStudents = [];
      const studentMap = new Map();
      
      documentsArray.forEach(doc => {
        if (doc.student_email && doc.user_id && !studentMap.has(doc.user_id)) {
          studentMap.set(doc.user_id, {
            id: doc.user_id,
            name: doc.student_name || 'Nom inconnu',
            email: doc.student_email || 'Email inconnu',
            department: doc.student_department || 'Département inconnu',
            cursus: doc.student_cursus || 'Cursus inconnu',
            parcours: doc.student_parcours || 'Parcours inconnu'
          });
          uniqueStudents.push(studentMap.get(doc.user_id));
        }
      });
      
      setStudents(uniqueStudents);
      
    } catch (error) {
      console.error('Error fetching documents:', error);
      if (error.response?.status === 403) {
        alert('Accès refusé. Vous devez être connecté en tant qu\'administrateur ou encadreur.');
      } else {
        alert('Erreur lors du chargement des documents');
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadDocument = async (documentId, filename) => {
    try {
      setDownloadingDoc(documentId);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API}/admin/documents/${documentId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Document download response:', response.data);
      alert(`Téléchargement simulé pour: ${filename}`);
      
    } catch (error) {
      console.error('Error downloading document:', error);
      if (error.response?.status === 403) {
        alert('Accès refusé. Vous n\'avez pas les permissions nécessaires.');
      } else {
        alert('Erreur lors du téléchargement');
      }
    } finally {
      setDownloadingDoc(null);
    }
  };

  const fetchStudentDocuments = async (studentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/students/${studentId}/documents`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Handle the new response structure {documents: [...]}
      const documentsArray = response.data.documents || response.data || [];
      setDocuments(documentsArray);
      
    } catch (error) {
      console.error('Error fetching student documents:', error);
      alert('Erreur lors du chargement des documents de l\'étudiant');
    }
  };

  const clearFilters = () => {
    setFilters({ search: '', fileType: '', student: '' });
    setSelectedStudent('');
    fetchDocuments();
  };

  // Filter documents
  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = !filters.search || 
      (doc.student_name && doc.student_name.toLowerCase().includes(filters.search.toLowerCase())) ||
      (doc.student_email && doc.student_email.toLowerCase().includes(filters.search.toLowerCase())) ||
      (doc.original_filename && doc.original_filename.toLowerCase().includes(filters.search.toLowerCase()));
    
    const matchesFileType = !filters.fileType || doc.file_type === filters.fileType;
    const matchesStudent = !filters.student || doc.user_id === filters.student;
    
    return matchesSearch && matchesFileType && matchesStudent;
  });

  // Get unique file types (filter out undefined/null values)
  const fileTypes = [...new Set(documents.map(doc => doc.file_type).filter(type => type))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Documents des Étudiants</h2>
          <p className="text-gray-600">Consultez et téléchargez les documents soumis par les étudiants</p>
        </div>
        <button
          onClick={fetchDocuments}
          className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-lg flex items-center space-x-2"
        >
          <span>🔄</span>
          <span>Actualiser</span>
        </button>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">🔍 Filtres</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Recherche</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Nom étudiant, email, fichier..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type de fichier</label>
            <select
              value={filters.fileType}
              onChange={(e) => setFilters({ ...filters, fileType: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tous les types</option>
              {fileTypes.map((type) => (
                <option key={type} value={type}>{type.toUpperCase()}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Étudiant</label>
            <select
              value={filters.student}
              onChange={(e) => {
                setFilters({ ...filters, student: e.target.value });
                if (e.target.value) {
                  fetchStudentDocuments(e.target.value);
                }
              }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tous les étudiants</option>
              {students.map((student) => (
                <option key={student.id} value={student.id}>
                  {student.name} ({student.department})
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-md"
            >
              Effacer les filtres
            </button>
          </div>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-3 gap-2 sm:gap-6">
        <div className="bg-white rounded-lg shadow p-3 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-500">Total documents</p>
              <p className="text-lg sm:text-2xl font-bold text-blue-600">{documents.length}</p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 text-sm sm:text-xl">📁</span>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-3 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-500">Documents filtrés</p>
              <p className="text-lg sm:text-2xl font-bold text-green-600">{filteredDocuments.length}</p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600 text-sm sm:text-xl">🔍</span>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-3 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-500">Étudiants</p>
              <p className="text-lg sm:text-2xl font-bold text-purple-600">{students.length}</p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <span className="text-purple-600 text-sm sm:text-xl">👥</span>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des documents */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Documents ({filteredDocuments.length})
          </h3>
        </div>

        {filteredDocuments.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-gray-400 text-2xl">📁</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900">
              {filters.search || filters.fileType || filters.student 
                ? 'Aucun document trouvé' 
                : 'Aucun document'}
            </h3>
            <p className="text-gray-500 mt-2">
              {filters.search || filters.fileType || filters.student 
                ? 'Essayez d\'ajuster vos filtres' 
                : 'Les étudiants n\'ont pas encore soumis de documents'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fichier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Étudiant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date d'ajout
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDocuments.map((doc) => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <span className="text-blue-600 font-semibold text-sm">
                            {doc.file_type?.toUpperCase() || 'DOC'}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {doc.original_filename || 'Nom de fichier inconnu'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {doc.description || 'Aucune description'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{doc.student_name || 'Nom inconnu'}</div>
                      <div className="text-sm text-gray-500">{doc.student_email || 'Email inconnu'}</div>
                      <div className="text-xs text-gray-400">
                        {doc.student_department || 'Département inconnu'} • {doc.student_cursus || 'Cursus inconnu'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                        {doc.file_type?.toUpperCase() || 'UNKNOWN'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : 'Date inconnue'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => downloadDocument(doc.id, doc.original_filename)}
                        disabled={downloadingDoc === doc.id}
                        className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                      >
                        {downloadingDoc === doc.id ? (
                          <span className="flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                            Téléchargement...
                          </span>
                        ) : (
                          '📥 Télécharger'
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
// ========================================================================
// AJOUTEZ CES DEUX NOUVEAUX COMPOSANTS À LA FIN DE VOTRE FICHIER App.js
// ========================================================================

// ========================================================================
// AJOUTEZ CES DEUX NOUVEAUX COMPOSANTS À LA FIN DE VOTRE FICHIER App.js
// ========================================================================

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      setMessage(response.data.message);
    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Mot de passe oublié</h1>
          <p className="text-gray-600">Entrez votre e-mail pour recevoir un lien de réinitialisation.</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            />
          </div>
          {message && <div className="text-green-600 text-sm">{message}</div>}
          {error && <div className="text-red-600 text-sm">{error}</div>}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center py-2 px-4 border rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Envoi en cours...' : 'Envoyer le lien de réinitialisation'}
          </button>
        </form>
        <div className="text-center mt-4">
          <a href="/login" className="text-sm text-blue-600 hover:underline">Retour à la connexion</a>
        </div>
      </div>
    </div>
  );
};

const ResetPasswordPage = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Récupérer le token depuis l'URL
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    if (!token) {
      setError('Token de réinitialisation manquant ou invalide.');
      return;
    }
    setLoading(true);
    setMessage('');
    setError('');
    try {
      const response = await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setMessage(response.data.message + " Vous pouvez maintenant retourner à la page de connexion.");
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Réinitialiser le mot de passe</h1>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Nouveau mot de passe</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Confirmer le nouveau mot de passe</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            />
          </div>
          {message && <div className="text-green-600 text-sm">{message}</div>}
          {error && <div className="text-red-600 text-sm">{error}</div>}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center py-2 px-4 border rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Mise à jour...' : 'Mettre à jour le mot de passe'}
          </button>
        </form>
         {message && (
          <div className="text-center mt-4">
            <a href="/login" className="text-sm text-blue-600 hover:underline">Retour à la connexion</a>
          </div>
        )}
      </div>
    </div>
  );
};
// Collez ces deux composants à la fin de votre fichier App.js



const ManageTeachersModal = ({ onClose, onSuccess }) => {
    // États pour le formulaire
    const [formData, setFormData] = useState({ full_name: '', grade: '', status: '', department: '' });
    const [options, setOptions] = useState({ grades: [], statuses: [], departments: [] });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // États pour l'import
    const [file, setFile] = useState(null);
    const [isImporting, setIsImporting] = useState(false);

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const response = await axios.get(`${API}/teachers/options`);
                setOptions(response.data);
            } catch (error) {
                console.error("Impossible de charger les options pour le formulaire.", error);
            }
        };
        fetchOptions();
    }, []);

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleIndividualSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await axios.post(`${API}/teachers`, formData);
            alert("Enseignant créé avec succès !");
            onSuccess();
        } catch (error) {
            alert("Erreur lors de la création : " + (error.response?.data?.detail || "Erreur inconnue"));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API}/teachers/template`, {
                headers: { 'Authorization': `Bearer ${token}` },
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'modele_import_enseignants.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            alert("Erreur lors du téléchargement du modèle.");
        }
    };

    const handleBulkImport = async () => {
    if (!file) {
        alert("Veuillez choisir un fichier.");
        return;
    }
    setIsImporting(true);
    
    // La création de l'objet FormData est correcte
    const uploadFormData = new FormData();
     uploadFormData.append('file', file);

     try {
        // --- DÉBUT DE LA CORRECTION ---
        const token = localStorage.getItem('token');
        const response = await axios.post(
            `${API}/teachers/bulk-import`, 
            uploadFormData, 
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}` // N'oubliez pas l'authentification
                },
                timeout: 120000
            }
        );
        // --- FIN DE LA CORRECTION ---

        alert(response.data.message);
        setFile(null);
        onSuccess();
      } catch (error) {
        if (error.code === 'ECONNABORTED') {
            alert("Erreur lors de l'importation : Le serveur a mis trop de temps à répondre (Timeout).");
        } else if (error.response && error.response.status === 422) {
            // C'est une erreur de validation, on affiche le détail !
            alert("Erreur de validation dans le fichier : \n\n" + error.response.data.detail);
        } else {
            // Autre type d'erreur
            alert("Erreur lors de l'importation : " + (error.response?.data?.detail || error.message));
        }
      } finally {
        setIsImporting(false);
      }
   };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-bold text-gray-800">Gérer les Enseignants</h2>
                    <button onClick={onClose} className="text-3xl text-gray-500 hover:text-gray-800">&times;</button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6">
                    {/* Colonne de Gauche : Création Individuelle */}
                    <div className="pr-8 md:border-r md:border-gray-200">
                        <h3 className="text-xl font-semibold mb-4 text-gray-700">Création Individuelle</h3>
                        <form onSubmit={handleIndividualSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Noms et Prénoms *</label>
                                <input type="text" name="full_name" value={formData.full_name} onChange={handleInputChange} className="mt-1 w-full p-2 border border-gray-300 rounded-md" required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Grade *</label>
                                <select name="grade" value={formData.grade} onChange={handleInputChange} className="mt-1 w-full p-2 border border-gray-300 rounded-md" required>
                                    <option value="">Sélectionner un grade...</option>
                                    {options.grades.map(g => <option key={g} value={g}>{g}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Statut *</label>
                                <select name="status" value={formData.status} onChange={handleInputChange} className="mt-1 w-full p-2 border border-gray-300 rounded-md" required>
                                    <option value="">Sélectionner un statut...</option>
                                    {options.statuses.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Département *</label>
                                <select name="department" value={formData.department} onChange={handleInputChange} className="mt-1 w-full p-2 border border-gray-300 rounded-md" required>
                                    <option value="">Sélectionner un département...</option>
                                    {options.departments.map(d => <option key={d} value={d}>{d}</option>)}
                                </select>
                            </div>
                            <div className="pt-4">
                                <button type="submit" disabled={isSubmitting} className="w-full px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">
                                    {isSubmitting ? "Sauvegarde..." : "Sauvegarder l'Enseignant"}
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* Colonne de Droite : Création en Masse */}
                    <div className="space-y-6">
                        <h3 className="text-xl font-semibold mb-4 text-gray-700">Importer en Masse</h3>
                        <div className="bg-yellow-50 p-4 rounded-md border border-yellow-200">
                            <h4 className="font-medium text-yellow-900 mb-2">Instructions</h4>
                            <ol className="text-sm text-yellow-800 list-decimal list-inside space-y-1">
                                <li>Téléchargez le modèle Excel pour garantir le bon format.</li>
                                <li>Remplissez le fichier avec les informations des enseignants.</li>
                                <li>Choisissez le fichier rempli et cliquez sur "Lancer l'Importation".</li>
                            </ol>
                        </div>
                        <div className="flex flex-col gap-4">
                            <button onClick={handleDownloadTemplate} className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md">
                                📥 Télécharger le Modèle
                            </button>
                            <div className="flex items-center gap-2">
                                <input type="file" accept=".xlsx,.xls" onChange={(e) => setFile(e.target.files[0])} className="hidden" id="teacher-upload" />
                                <label htmlFor="teacher-upload" className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-md cursor-pointer text-center">
                                    📤 Choisir un Fichier
                                </label>
                            </div>
                            {file && <span className="text-sm text-center text-gray-600">Fichier : {file.name}</span>}
                            <button onClick={handleBulkImport} disabled={!file || isImporting} className="bg-purple-800 hover:bg-purple-900 text-white font-medium py-2 px-4 rounded-md disabled:opacity-50">
                                {isImporting ? "Importation..." : "Lancer l'Importation"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const TeacherListTab = () => {
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingTeacher, setEditingTeacher] = useState(null);

    const [filters, setFilters] = useState({
        search: '',
        grade: '',
        status: '',
        department: ''
    });
    const [filterOptions, setFilterOptions] = useState({ grades: [], statuses: [], departments: [] });
    const [selectedTeachers, setSelectedTeachers] = useState([]);

    useEffect(() => {
        fetchTeachers();
        // Charger les options pour les filtres
        const fetchOptions = async () => {
            try {
                const response = await axios.get(`${API}/teachers/options`);
                setFilterOptions(response.data);
            } catch (error) {
                console.error("Impossible de charger les options de filtre.", error);
            }
        };
        fetchOptions();
    }, []);

    useEffect(() => {
        fetchTeachers();
    }, []);

    const fetchTeachers = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API}/teachers`);
            setTeachers(response.data);
        } catch (error) {
            alert("Erreur lors du chargement des enseignants.");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (teacher) => {
        setEditingTeacher(teacher);
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (teacher) => {
        setEditingTeacher(teacher);
        setIsEditModalOpen(true);
    };
    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setEditingTeacher(null);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingTeacher(null);
    };

    const handleSaveTeacher = async (formData) => {
        try {
            await axios.put(`${API}/teachers/${editingTeacher.id}`, formData);
            fetchTeachers();
            handleCloseEditModal();
        } catch (error) {
            alert("Erreur lors de la sauvegarde.");
        }
    };
    
    const handleDeleteTeacher = async (teacherId) => {
        if(window.confirm("Êtes-vous sûr de vouloir supprimer cet enseignant ?")) {
            try {
                await axios.delete(`${API}/teachers/${teacherId}`);
                fetchTeachers();
            } catch (error) {
                alert("Erreur lors de la suppression.");
            }
        }
    };
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const resetFilters = () => {
        setFilters({ search: '', grade: '', status: '', department: '' });
    };

    // Logique de filtrage des enseignants
    const filteredTeachers = teachers.filter(teacher => {
        const searchLower = filters.search.toLowerCase();

        const matchesSearch = !filters.search || teacher.full_name.toLowerCase().includes(searchLower);
        const matchesGrade = !filters.grade || teacher.grade === filters.grade;
        const matchesStatus = !filters.status || teacher.status === filters.status;
        const matchesDepartment = !filters.department || teacher.department === filters.department;
        
        return matchesSearch && matchesGrade && matchesStatus && matchesDepartment;
    });

    const handleSelectTeacher = (teacherId) => {
        setSelectedTeachers(prev => 
            prev.includes(teacherId)
                ? prev.filter(id => id !== teacherId)
                : [...prev, teacherId]
        );
    };

    const handleSelectAll = () => {
        if (selectedTeachers.length === filteredTeachers.length) {
            setSelectedTeachers([]);
        } else {
            setSelectedTeachers(filteredTeachers.map(t => t.id));
        }
    };

    const handleDeleteSelected = async () => {
        if (selectedTeachers.length === 0) return;
        if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedTeachers.length} enseignant(s) ?`)) return;

        let deletedCount = 0;
        for (const teacherId of selectedTeachers) {
            try {
                await axios.delete(`${API}/teachers/${teacherId}`);
                deletedCount++;
            } catch (error) {
                console.error(`Erreur lors de la suppression de l'enseignant ${teacherId}:`, error);
                alert(`Une erreur est survenue lors de la suppression. ${deletedCount} sur ${selectedTeachers.length} ont été supprimés.`);
                break;
            }
        }
        alert(`${deletedCount} enseignant(s) supprimé(s) avec succès.`);
        setSelectedTeachers([]);
        fetchTeachers();
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Gestion des Enseignants</h2>
                <div className="flex space-x-3">
                    <button onClick={fetchTeachers} className="bg-gray-200 ...">Actualiser</button>
                    <button 
                        onClick={() => setIsCreateModalOpen(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md"
                    >
                        + Créer / Importer
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">🔍 Filtres</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Recherche (par nom)</label>
                        <input type="text" name="search" value={filters.search} onChange={handleFilterChange} placeholder="Nom de l'enseignant..." className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
                        <select name="grade" value={filters.grade} onChange={handleFilterChange} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                            <option value="">Tous les grades</option>
                            {filterOptions.grades.map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                        <select name="status" value={filters.status} onChange={handleFilterChange} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                            <option value="">Tous les statuts</option>
                            {filterOptions.statuses.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Département</label>
                        <select name="department" value={filters.department} onChange={handleFilterChange} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                            <option value="">Tous les départements</option>
                            {filterOptions.departments.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                    </div>
                </div>
                <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                        {filteredTeachers.length} enseignant(s) affiché(s) sur {teachers.length} total
                    </div>
                    <button onClick={resetFilters} className="text-sm text-blue-600 hover:underline">
                        Réinitialiser les filtres
                    </button>
                </div>
            </div>

            {loading ? (
    <p>Chargement de la liste...</p>
) : (
    <>
        {/* --- NOUVEAU BLOC POUR LES ACTIONS EN MASSE --- */}
        <div className="bg-white rounded-t-lg shadow px-6 py-3 border-b flex items-center justify-between">
            <span className="text-sm text-gray-600">
                {selectedTeachers.length} enseignant(s) sélectionné(s)
            </span>
            {selectedTeachers.length > 0 && (
                <button
                    onClick={handleDeleteSelected}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                >
                    Supprimer la sélection
                </button>
            )}
        </div>

        {/* --- LE TABLEAU EST MAINTENANT DANS UN NOUVEAU CONTENEUR --- */}
        <div className="bg-white rounded-b-lg shadow overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        {/* 1. NOUVELLE COLONNE POUR LA CHECKBOX "TOUT SÉLECTIONNER" */}
                        <th className="px-6 py-3">
                            <input
                                type="checkbox"
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                onChange={handleSelectAll}
                                checked={filteredTeachers.length > 0 && selectedTeachers.length === filteredTeachers.length}
                            />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Noms et Prénoms</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Grade</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Département</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {filteredTeachers.map(teacher => (
                        // 2. AJOUT D'UNE CLASSE POUR LE SURVOL
                        <tr key={teacher.id} className={selectedTeachers.includes(teacher.id) ? 'bg-blue-50' : ''}>
                            {/* 3. NOUVELLE CELLULE POUR LA CHECKBOX DE LIGNE */}
                            <td className="px-6 py-4 whitespace-nowrap">
                                <input
                                    type="checkbox"
                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                    checked={selectedTeachers.includes(teacher.id)}
                                    onChange={() => handleSelectTeacher(teacher.id)}
                                />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">{teacher.full_name}</td>
                            <td className="px-6 py-4 whitespace-nowrap">{teacher.grade}</td>
                            <td className="px-6 py-4 whitespace-nowrap">{teacher.status}</td>
                            <td className="px-6 py-4 whitespace-nowrap">{teacher.department}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button onClick={() => handleOpenModal(teacher)} className="text-blue-600 hover:text-blue-900 mr-3">Modifier</button>
                                <button onClick={() => handleDeleteTeacher(teacher.id)} className="text-red-600 hover:text-red-900">Supprimer</button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </>
)}

            {isModalOpen && editingTeacher && (
                <TeacherFormModal
                    teacher={editingTeacher}
                    onClose={handleCloseModal}
                    onSave={handleSaveTeacher}
                />
            )}

            {/* Appel de la modale de MODIFICATION */}
            {isEditModalOpen && editingTeacher && (
                <TeacherFormModal
                    teacher={editingTeacher}
                    onClose={handleCloseEditModal}
                    onSave={handleSaveTeacher}
                />
            )}

            {/* Appel de la modale de CRÉATION / IMPORT */}
            {isCreateModalOpen && (
                <ManageTeachersModal
                    onClose={() => setIsCreateModalOpen(false)}
                    onSuccess={() => {
                        setIsCreateModalOpen(false);
                        fetchTeachers(); // On rafraîchit la liste après un import réussi !
                    }}
                />
            )}

        </div>
    );
};

// Dans App.js, ajoutez ce nouveau composant complet

const TeacherFormModal = ({ teacher, onClose, onSave }) => {
    // État pour les options des listes déroulantes
    const [options, setOptions] = useState({ grades: [], statuses: [], departments: [] });
    // État pour le formulaire, initialisé avec les données de l'enseignant à modifier
    const [formData, setFormData] = useState({
        full_name: teacher?.full_name || '',
        grade: teacher?.grade || '',
        status: teacher?.status || '',
        department: teacher?.department || '',
    });

    // Charger les options pour les listes déroulantes au montage
    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const response = await axios.get(`${API}/teachers/options`);
                setOptions(response.data);
            } catch (error) {
                console.error("Impossible de charger les options pour le formulaire.", error);
            }
        };
        fetchOptions();
    }, []);

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData); // Appelle la fonction de sauvegarde passée en props
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-lg">
                <h3 className="text-xl font-semibold mb-4">Modifier l'Enseignant</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Noms et Prénoms *</label>
                        <input type="text" name="full_name" value={formData.full_name} onChange={handleInputChange} className="mt-1 w-full p-2 border border-gray-300 rounded-md" required />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Grade *</label>
                        <select name="grade" value={formData.grade} onChange={handleInputChange} className="mt-1 w-full p-2 border border-gray-300 rounded-md" required>
                            <option value="">Sélectionner un grade...</option>
                            {options.grades.map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Statut *</label>
                        <select name="status" value={formData.status} onChange={handleInputChange} className="mt-1 w-full p-2 border border-gray-300 rounded-md" required>
                            <option value="">Sélectionner un statut...</option>
                            {options.statuses.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Département *</label>
                        <select name="department" value={formData.department} onChange={handleInputChange} className="mt-1 w-full p-2 border border-gray-300 rounded-md" required>
                            <option value="">Sélectionner un département...</option>
                            {options.departments.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                    </div>
                    <div className="flex justify-end gap-4 pt-4">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded-md">Annuler</button>
                        <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md">Sauvegarder</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Composant de placeholder pour l'onglet "Liste des Jurys"
const JuryListTab = () => {
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingAssignment, setEditingAssignment] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const [selectedAssignments, setSelectedAssignments] = useState([]);
    const [isMassEditModalOpen, setIsMassEditModalOpen] = useState(false);

    // Les URLs sont maintenant fixes car ce composant n'est que pour l'admin
    const BASE_URL = `${API}/jury-assignments`;
    const EXPORT_URL = `${API}/jury-assignments/export`;

    // États pour les filtres généraux
    const [filters, setFilters] = useState({
        search_name: '', student_department: '', student_cursus: '', student_parcours: '',
        teacher_grade: '', teacher_department: '',jury_number: ''
    });
    const [filterOptions, setFilterOptions] = useState({ 
        teacher: { grades: [], statuses: [], departments: [] },
        student: { departments: [], cursus: [], parcours: [] }
    });


    useEffect(() => {
        const fetchAllOptions = async () => {
            try {
                const [teacherOptsRes, studentDeptsRes] = await Promise.all([
                    axios.get(`${API}/teachers/options`),
                    axios.get(`${API}/structure/departments`)
                ]);
                setFilterOptions(prev => ({
                    ...prev,
                    teacher: teacherOptsRes.data,
                    student: { ...prev.student, departments: studentDeptsRes.data.departments }
                }));
            } catch (error) { console.error("Erreur chargement options:", error); }
        };
        fetchAllOptions();
    }, []); // Tableau vide : ne s'exécute qu'une fois.

    // 2. Un seul useEffect pour le chargement des données, qui réagit aux filtres.
    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            // On met setLoading(true) ici pour montrer un indicateur de chargement pendant le filtrage
            setLoading(true); 
            fetchAssignments();
        }, 500);

        return () => clearTimeout(debounceTimer);
    }, [filters]);

    const fetchAssignments = async () => {
        setLoading(true);
        try {
            const activeFilters = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
            const params = new URLSearchParams(activeFilters);
            const response = await axios.get(BASE_URL, { params });
            setAssignments(response.data);
        } catch (error) { console.error(error); } 
        finally { setLoading(false); }
    };


    const handleFilterChange = async (name, value) => {
     const newFilters = { ...filters, [name]: value };

    // Logique de cascade pour les filtres étudiants
    if (name === 'student_department') {
        newFilters.student_cursus = '';
        newFilters.student_parcours = '';
        setFilterOptions(prev => ({ ...prev, student: { ...prev.student, cursus: [], parcours: [] } }));
        if (value) {
            try {
                const response = await axios.get(`${API}/structure/cursus?department=${value}`);
                setFilterOptions(prev => ({ ...prev, student: { ...prev.student, cursus: response.data.cursus } }));
            } catch (error) { console.error("Erreur chargement cursus:", error); }
        }
    } else if (name === 'student_cursus') {
        newFilters.student_parcours = '';
        setFilterOptions(prev => ({ ...prev, student: { ...prev.student, parcours: [] } }));
        if (value && newFilters.student_department) {
            try {
                const response = await axios.get(`${API}/structure/parcours?department=${newFilters.student_department}&cursus=${value}`);
                setFilterOptions(prev => ({ ...prev, student: { ...prev.student, parcours: response.data.parcours } }));
            } catch (error) { console.error("Erreur chargement parcours:", error); }
        }
    }
    
    setFilters(newFilters);
};

    const resetFilters = () => {
        setFilters({
            search_name: '', student_department: '', student_cursus: '', student_parcours: '',
            teacher_grade: '',  teacher_department: '', jury_number: ''
        });
        
    };

    const handleDeleteAssignment = async (assignmentId) => {
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette attribution de jury ?")) return;
        try {
            await axios.delete(`${API}/jury-assignments/${assignmentId}`);
            alert("Attribution supprimée avec succès.");
            fetchAssignments();
        } catch (error) {
            alert("Erreur lors de la suppression.");
        }
      setFilters(prev => ({...prev})); 
    };
    const handleSelectAssignment = (assignmentId) => {
    setSelectedAssignments(prev => 
        prev.includes(assignmentId) 
            ? prev.filter(id => id !== assignmentId) 
            : [...prev, assignmentId]
    );
};

const handleSelectAll = () => {
    if (selectedAssignments.length === assignments.length) {
        setSelectedAssignments([]);
    } else {
        setSelectedAssignments(assignments.map(a => a.id));
    }
};

const handleDeleteSelected = async () => {
    if (selectedAssignments.length === 0) return;
    if (!window.confirm(`Supprimer les ${selectedAssignments.length} attributions sélectionnées ?`)) return;
    
    try {
        // Suppression en boucle
        for (const id of selectedAssignments) {
            await axios.delete(`${BASE_URL}/${id}`);
        }
        alert("Attributions supprimées avec succès.");
        setSelectedAssignments([]);
        fetchAssignments();
    } catch (error) {
        alert("Erreur lors de la suppression en masse.");
    }
};
const handleMassUpdate = async (data) => {
    try {
        for (const id of selectedAssignments) {
            // data contient { teacher_id: '...', project_title: '...' }
            // On récupère l'objet complet de l'attribution actuelle pour vérifier
            const currentAssignment = assignments.find(a => a.id === id);
            
            // Si le prof est le même, on saute cette ligne
            if (data.teacher_id && currentAssignment.teacher.id === data.teacher_id) {
                console.log("Encadreur identique, on saute la mise à jour pour cet étudiant.");
                continue; 
            }

            await axios.put(`${BASE_URL}/${id}`, data);
        }
        alert("Modifications effectuées avec succès !");
        setIsMassEditModalOpen(false);
        setSelectedAssignments([]);
        fetchAssignments();
    } catch (error) {
        alert("Erreur : " + (error.response?.data?.detail || "Inconnue"));
    }
};
    
     const handleExport = async () => {
        setIsExporting(true);
        try {
            // On utilise les mêmes filtres que pour l'affichage
            const activeFilters = {};
            for (const key in filters) {
                if (filters[key]) {
                    activeFilters[key] = filters[key];
                }
            }
            const params = new URLSearchParams(activeFilters);
            const url = `${API}/jury-assignments/export?${params.toString()}`;

            const token = localStorage.getItem('token');
            const response = await axios.get(url, {
                headers: { 'Authorization': `Bearer ${token}` },
                responseType: 'blob',
            });

            // Logique de téléchargement
            const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', 'export_jurys.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();

        } catch (error) {
            alert("Erreur lors de l'exportation.");
            console.error("Export error:", error);
        } finally {
            setIsExporting(false);
        }
    };


    return (
        <div>
            {/* --- EN-TÊTE DE LA PAGE --- */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Attribution des Jurys</h2>
                    <p className="text-sm text-gray-500">Gérez les jurys de soutenance des étudiants.</p>
                </div>
                <div className="flex space-x-3">
                  {selectedAssignments.length > 0 && (
        <button onClick={() => setIsMassEditModalOpen(true)} className="bg-yellow-500 hover:bg-yellow-600 text-white font-medium py-2 px-4 rounded-md">
            Modifier Sélection
        </button>
    )}
                    <button 
                        onClick={handleExport}
                        disabled={isExporting}
                        className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md disabled:opacity-50 flex items-center"
                    >
                        {isExporting ? '...' : '📥'}
                        <span className="ml-2">{isExporting ? 'Exportation...' : 'Exporter (Excel)'}</span>
                    </button>
                    <button 
                        onClick={() => { setEditingAssignment(null); setIsModalOpen(true); }}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md"
                    >
                        + Nouvelle Attribution
                    </button>
                </div>
            </div>

            {/* --- SECTION DES FILTRES --- */}
           {/* --- NOUVELLE SECTION DE FILTRES FINALE (Compacte sur 3 lignes) --- */}
<div className="bg-white rounded-lg shadow p-6 mb-6 space-y-4">
    <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">🔍 Filtres</h3>
        <button onClick={resetFilters} className="text-sm text-blue-600 hover:underline">Réinitialiser les filtres</button>
    </div>

    {/* Ligne 1: Recherche Générale */}
    <div className="flex flex-col">
        <label className="text-sm font-medium text-gray-700 mb-1">Recherche par Nom</label>
        <input 
            type="text" 
            name="search_name" 
            placeholder="Nom de l'étudiant ou d'un membre du jury..." 
            value={filters.search_name} 
            onChange={(e) => handleFilterChange(e.target.name, e.target.value)} 
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
        />
    </div>

    {/* Ligne 2: Filtres Étudiant */}
    <div className="flex flex-col md:flex-row md:space-x-4 space-y-3 md:space-y-0">
        <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Filtres Étudiant</label>
            <div className="flex space-x-3">
                <select name="student_department" value={filters.student_department} onChange={(e) => handleFilterChange(e.target.name, e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                    <option value="">Par Département</option>
                    {filterOptions.student.departments.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <select name="student_cursus" value={filters.student_cursus} onChange={(e) => handleFilterChange(e.target.name, e.target.value)} disabled={!filters.student_department} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm disabled:bg-gray-100">
                    <option value="">Par Cursus</option>
                    {filterOptions.student.cursus.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select name="student_parcours" value={filters.student_parcours} onChange={(e) => handleFilterChange(e.target.name, e.target.value)} disabled={!filters.student_cursus} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm disabled:bg-gray-100">
                    <option value="">Par Parcours</option>
                    {filterOptions.student.parcours.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
            </div>
        </div>
    </div>
    
    {/* Ligne 3: Filtres Membres du Jury */}
    <div className="flex flex-col md:flex-row md:space-x-4 space-y-3 md:space-y-0">
         <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Filtres Membres du Jury</label>
            <div className="flex space-x-3">
                <select name="teacher_grade" value={filters.teacher_grade} onChange={(e) => handleFilterChange(e.target.name, e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                    <option value="">Par Grade</option>
                    {filterOptions.teacher.grades.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
                <select name="teacher_department" value={filters.teacher_department} onChange={(e) => handleFilterChange(e.target.name, e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                    <option value="">Par Département</option>
                    {filterOptions.teacher.departments.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <input 
                    type="number"
                    name="jury_number"
                    placeholder="Par # Jury"
                    value={filters.jury_number}
                    onChange={(e) => handleFilterChange(e.target.name, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
            </div>
        </div>
    </div>
</div>
            
            {/* --- TABLEAU PRINCIPAL --- */}
{loading ? (
    <p>Chargement des attributions...</p>
) : (
    <>
        {/* --- BARRE D'ACTION EN MASSE --- */}
        <div className="bg-white rounded-t-lg shadow px-6 py-3 border-b flex items-center justify-between">
            <span className="text-sm text-gray-600">
                {selectedAssignments.length} attribution(s) sélectionnée(s)
            </span>
            {selectedAssignments.length > 0 && (
                <button
                    onClick={handleDeleteSelected}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                >
                    Supprimer la sélection
                </button>
            )}
        </div>

        {/* --- TABLEAU --- */}
        <div className="bg-white rounded-b-lg shadow overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-4 py-3">
                            <input
                                type="checkbox"
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                onChange={handleSelectAll}
                                checked={assignments.length > 0 && selectedAssignments.length === assignments.length}
                            />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"># Jury</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Étudiant</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Président</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rapporteur</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Examinateur</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Soutenance</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {assignments.length === 0 ? (
                        <tr><td colSpan="8" className="text-center py-12 text-gray-500">
                            Aucune attribution ne correspond à vos filtres.
                        </td></tr>
                    ) : (
                        assignments.map((assignment) => {
                            const { id, student, president, reporter, examiner, jury_number, soutenance_date } = assignment;
                            return (
                                <tr key={id} className={selectedAssignments.includes(id) ? 'bg-blue-50' : ''}>
                                    <td className="px-4 py-4 whitespace-nowrap">
                                        <input
                                            type="checkbox"
                                            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                            checked={selectedAssignments.includes(id)}
                                            onChange={() => handleSelectAssignment(id)}
                                        />
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap font-bold text-lg text-gray-700">{jury_number}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="font-medium text-gray-900">{student.name}</div>
                                        <div className="text-xs text-gray-500 mt-1">{student.student_department}</div>
                                        <div className="text-xs text-gray-400">{student.student_cursus} - {student.student_parcours}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">{president.full_name} ({president.count})</div>
                                        <div className="text-xs text-gray-500">{president.grade} - {president.department}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">{reporter.full_name} ({reporter.count})</div>
                                        <div className="text-xs text-gray-500">{reporter.grade} - {reporter.department}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">{examiner.full_name} ({examiner.count})</div>
                                        <div className="text-xs text-gray-500">{examiner.grade} - {examiner.department}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">
                                            {new Date(soutenance_date).toLocaleDateString('fr-FR')}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            {new Date(soutenance_date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button 
                                            onClick={() => { setEditingAssignment(assignment); setIsModalOpen(true); }} 
                                            className="text-blue-600 hover:text-blue-900 mr-3">
                                            Modifier
                                        </button>
                                        <button 
                                            onClick={() => handleDeleteAssignment(id, student.name)}
                                            className="text-red-600 hover:text-red-900">
                                            Supprimer
                                        </button>
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
    </>
)}
            
            {/* La modale sera ajoutée ici à la prochaine étape */}
            {isModalOpen && (
                <JuryAssignmentModal
                    // On ne passe plus la prop 'user'
                    assignment={editingAssignment}
                    onClose={() => setIsModalOpen(false)}
                    onSave={async (data) => {
                        try {
                            const url = editingAssignment ? `${BASE_URL}/${editingAssignment.id}` : BASE_URL;
                            const method = editingAssignment ? 'put' : 'post';
                            await axios[method](url, data);
                            alert("Opération réussie !");
                            setIsModalOpen(false);
                            fetchAssignments();
                        } catch (error) {
                            alert("Erreur : " + (error.response?.data?.detail || "Inconnue"));
                        }
        }}
    />
)}
{isMassEditModalOpen && (
    <MassEditModal 
        selectedCount={selectedAssignments.length}
        onClose={() => setIsMassEditModalOpen(false)}
        onSave={handleMassUpdate}
    />
)}
        </div>
    );
};

const MassEditModal = ({ onClose, onSave, selectedCount }) => {
    const [fieldsToEdit, setFieldsToEdit] = useState({ 
        jury_number: false, soutenance_date: false, president_id: false, reporter_id: false, examiner_id: false 
    });
    const [formData, setFormData] = useState({ jury_number: '', soutenance_date: '', president_id: '', reporter_id: '', examiner_id: '' });
    const [step, setStep] = useState(1);
    const [allTeachers, setAllTeachers] = useState([]);
    const [openSelector, setOpenSelector] = useState(null); // Gère quel pop-up est ouvert

    useEffect(() => {
        const fetchTeachers = async () => {
            try {
                const res = await axios.get(`${API}/teachers`);
                setAllTeachers(res.data);
            } catch (err) { console.error(err); }
        };
        fetchTeachers();
    }, []);

    const handleSubmit = () => {
        const data = {};
        if (fieldsToEdit.jury_number) data.jury_number = parseInt(formData.jury_number);
        if (fieldsToEdit.soutenance_date) data.soutenance_date = new Date(formData.soutenance_date).toISOString();
        if (fieldsToEdit.president_id) data.president_id = formData.president_id;
        if (fieldsToEdit.reporter_id) data.reporter_id = formData.reporter_id;
        if (fieldsToEdit.examiner_id) data.examiner_id = formData.examiner_id;
        onSave(data);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            {/* modifiée max-w-lg en max-w-4xl pour une fenêtre bien grande */}
            <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold">{step === 1 ? "Choisir les champs à modifier" : "Nouvelles valeurs"}</h3>
                    <button onClick={onClose} className="text-2xl text-gray-500">&times;</button>
                </div>
                
                {step === 1 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[
                            {id: 'jury_number', label: 'Numéro Jury'}, 
                            {id: 'soutenance_date', label: 'Date Soutenance'}, 
                            {id: 'president_id', label: 'Président'}, 
                            {id: 'reporter_id', label: 'Rapporteur'}, 
                            {id: 'examiner_id', label: 'Examinateur'}
                        ].map(field => (
                            <label key={field.id} className="flex items-center p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                                <input 
                                    type="checkbox" 
                                    checked={fieldsToEdit[field.id]} 
                                    onChange={(e) => setFieldsToEdit({...fieldsToEdit, [field.id]: e.target.checked})} 
                                    className="mr-3 h-5 w-5" 
                                />
                                <span className="font-medium text-gray-700">{field.label}</span>
                            </label>
                        ))}
                        <div className="col-span-full flex justify-end gap-2 mt-6">
                            <button onClick={onClose} className="px-6 py-2 bg-gray-200 rounded-md">Annuler</button>
                            <button onClick={() => setStep(2)} className="px-6 py-2 bg-blue-600 text-white rounded-md">Suivant</button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {fieldsToEdit.jury_number && (
                            <div>
                                <label className="block text-sm font-medium">Nouveau Numéro Jury</label>
                                <input type="number" onChange={e => setFormData({...formData, jury_number: e.target.value})} className="w-full p-3 border rounded-lg" />
                            </div>
                        )}
                        {fieldsToEdit.soutenance_date && (
                            <div>
                                <label className="block text-sm font-medium">Nouvelle Date</label>
                                <input type="datetime-local" onChange={e => setFormData({...formData, soutenance_date: e.target.value})} className="w-full p-3 border rounded-lg" />
                            </div>
                        )}
                        
                        {fieldsToEdit.president_id && (
                            <JuryMemberSelector title="Président" teachers={allTeachers} options={{grades:[], statuses:[], departments:[]}} selectedValue={formData.president_id} onSelect={id => setFormData({...formData, president_id: id})} isOpen={openSelector === 'president'} onToggle={() => setOpenSelector(openSelector === 'president' ? null : 'president')} />
                        )}
                        {fieldsToEdit.reporter_id && (
                            <JuryMemberSelector title="Rapporteur" teachers={allTeachers} options={{grades:[], statuses:[], departments:[]}} selectedValue={formData.reporter_id} onSelect={id => setFormData({...formData, reporter_id: id})} isOpen={openSelector === 'reporter'} onToggle={() => setOpenSelector(openSelector === 'reporter' ? null : 'reporter')} />
                        )}
                        {fieldsToEdit.examiner_id && (
                            <JuryMemberSelector title="Examinateur" teachers={allTeachers} options={{grades:[], statuses:[], departments:[]}} selectedValue={formData.examiner_id} onSelect={id => setFormData({...formData, examiner_id: id})} isOpen={openSelector === 'examiner'} onToggle={() => setOpenSelector(openSelector === 'examiner' ? null : 'examiner')} />
                        )}
                        
                        <div className="flex justify-end gap-2 mt-8 pt-4 border-t">
                            <button onClick={() => setStep(1)} className="px-6 py-2 bg-gray-200 rounded-md">Retour</button>
                            <button onClick={handleSubmit} className="px-6 py-2 bg-blue-600 text-white rounded-md font-bold">Appliquer aux {selectedCount} jurys</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// Remplacez votre composant JuryAssignmentModal par celui-ci

const JuryAssignmentModal = ({ assignment, onClose, onSave }) => {
    const [studentIds, setStudentIds] = useState(
        assignment ? [assignment.student.id] : []
    );
    const [teacherIds, setTeacherIds] = useState(
        assignment ? [assignment.teacher?.id || assignment.president?.id] : []
    );
    const [teacherJuryCounts, setTeacherJuryCounts] = useState({});

    const [allStudents, setAllStudents] = useState([]);
    const [allTeachers, setAllTeachers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openSelector, setOpenSelector] = useState(null);

    const [teacherOptions, setTeacherOptions] = useState({
        grades: [], statuses: [], departments: []
    });
    const [studentStructureOptions, setStudentStructureOptions] = useState({
        departments: [], cursus: [], parcours: []
    });

    // --- NOUVEAU : état pour la recherche étudiants avec debounce ---
    const [studentSearch, setStudentSearch] = useState('');
    const [studentDeptFilter, setStudentDeptFilter] = useState('');
    const [studentCursusFilter, setStudentCursusFilter] = useState('');
    const [studentParcoursFilter, setStudentParcoursFilter] = useState('');
    const [isLoadingStudents, setIsLoadingStudents] = useState(false);

    // Chargement initial (enseignants + options)
    useEffect(() => {
    const fetchData = async () => {
        try {
            const [teachersRes, teacherOptsRes, studentDeptsRes] = await Promise.all([
                axios.get(`${API}/teachers`),
                axios.get(`${API}/teachers/options`),
                axios.get(`${API}/structure/departments`),
            ]);
            setAllTeachers(teachersRes.data);
            setTeacherOptions(teacherOptsRes.data);
            setStudentStructureOptions(prev => ({
                ...prev,
                departments: studentDeptsRes.data.departments
            }));

            // ✅ Séparé pour ne pas bloquer le reste si ça échoue
            try {
                const juryCountsRes = await axios.get(`${API}/teachers/jury-counts`);
                // console.log("=== JURY COUNTS REÇUS ===", juryCountsRes.data);
                // console.log("Nombre d'entrées:", Object.keys(juryCountsRes.data).length);
                setTeacherJuryCounts(juryCountsRes.data);
            } catch (countError) {
                console.error("Erreur jury-counts:", countError.response?.data || countError.message);
            }

            await fetchStudents();
        } catch (error) {
            console.error("Erreur chargement modale:", error);
        } finally {
            setLoading(false);
        }
    };
    fetchData();
}, [assignment]);

    // --- NOUVELLE FONCTION : charger les étudiants avec filtres ---
    const fetchStudents = async (
        search = studentSearch,
        dept = studentDeptFilter,
        curs = studentCursusFilter,
        parc = studentParcoursFilter
    ) => {
        setIsLoadingStudents(true);
        try {
            const params = new URLSearchParams({ limit: 100 });
            if (search) params.append('search', search);
            if (dept) params.append('department', dept);
            if (curs) params.append('cursus', curs);
            if (parc) params.append('parcours', parc);

            const res = await axios.get(
                `${API}/admin/unassigned-students?${params.toString()}`
            );

            let studentList = res.data;
            // En mode édition, s'assurer que l'étudiant actuel est dans la liste
            if (assignment &&
                !studentList.find(s => s.id === assignment.student.id)) {
                studentList = [assignment.student, ...studentList];
            }
            setAllStudents(studentList);
        } catch (error) {
            console.error("Erreur chargement étudiants:", error);
        } finally {
            setIsLoadingStudents(false);
        }
    };

    // Debounce sur la recherche texte
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchStudents(
                studentSearch,
                studentDeptFilter,
                studentCursusFilter,
                studentParcoursFilter
            );
        }, 400);
        return () => clearTimeout(timer);
    }, [studentSearch, studentDeptFilter, studentCursusFilter, studentParcoursFilter]);

    // Gestion du filtre département étudiant (cascade)
    const handleStudentFilterChange = async (field, value) => {
        if (field === 'department') {
            setStudentDeptFilter(value);
            setStudentCursusFilter('');
            setStudentParcoursFilter('');
            setStudentStructureOptions(prev => ({
                ...prev, cursus: [], parcours: []
            }));
            if (value) {
                try {
                    const res = await axios.get(
                        `${API}/structure/cursus?department=${value}`
                    );
                    setStudentStructureOptions(prev => ({
                        ...prev, cursus: res.data.cursus
                    }));
                } catch (e) { console.error(e); }
            }
        } else if (field === 'cursus') {
            setStudentCursusFilter(value);
            setStudentParcoursFilter('');
            setStudentStructureOptions(prev => ({ ...prev, parcours: [] }));
            if (value && studentDeptFilter) {
                try {
                    const res = await axios.get(
                        `${API}/structure/parcours?department=${studentDeptFilter}&cursus=${value}`
                    );
                    setStudentStructureOptions(prev => ({
                        ...prev, parcours: res.data.parcours
                    }));
                } catch (e) { console.error(e); }
            }
        } else if (field === 'parcours') {
            setStudentParcoursFilter(value);
        }
    };

    const handleSubmit = (e) => {
    e.preventDefault();
    
    if (studentIds.length === 0) {
        alert("Veuillez sélectionner au moins un étudiant.");
        return;
    }
    if (!presidentId || !reporterId || !examinerId) {
        alert("Veuillez sélectionner le Président, le Rapporteur et l'Examinateur.");
        return;
    }
    if (!soutenanceDate) {
        alert("Veuillez sélectionner une date de soutenance.");
        return;
    }

    const dataToSave = {
        student_ids: studentIds,
        president_id: presidentId,
        reporter_id: reporterId,
        examiner_id: examinerId,
        soutenance_date: new Date(soutenanceDate).toISOString()
    };

    if (assignment) {
        dataToSave.jury_number = parseInt(juryNumberState);
    }

    onSave(dataToSave);
};

    // États pour les membres du jury
    const [presidentId, setPresidentId] = useState(assignment?.president?.id || '');
    const [reporterId, setReporterId] = useState(assignment?.reporter?.id || '');
    const [examinerId, setExaminerId] = useState(assignment?.examiner?.id || '');
    const [soutenanceDate, setSoutenanceDate] = useState(
        assignment
            ? new Date(assignment.soutenance_date).toISOString().slice(0, 16)
            : ''
    );
    const [juryNumberState, setJuryNumberState] = useState(
        assignment?.jury_number || ''
    );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-semibold text-gray-800">
                        {assignment
                            ? "Modifier l'attribution du Jury"
                            : "Nouvelle Attribution de Jury"}
                    </h3>
                    <button onClick={onClose} className="text-3xl text-gray-500">&times;</button>
                </div>

                {loading ? <p className="text-center py-8">Chargement...</p> : (
                    <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto pr-2 space-y-6">

                        {/* Numéro jury (mode édition) */}
                        {assignment && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Numéro du Jury *
                                </label>
                                <input
                                    type="number"
                                    value={juryNumberState}
                                    onChange={e => setJuryNumberState(e.target.value)}
                                    className="mt-1 w-full max-w-xs p-2 border border-gray-300 rounded-md"
                                    required
                                />
                            </div>
                        )}

                        {/* Date soutenance */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                Date et Heure de la Soutenance *
                            </label>
                            <input
                                type="datetime-local"
                                value={soutenanceDate}
                                onChange={e => setSoutenanceDate(e.target.value)}
                                className="mt-1 w-full max-w-xs p-2 border border-gray-300 rounded-md"
                                required
                            />
                        </div>

                        {/* Sélection étudiants */}
                        <div className="p-4 border rounded-lg bg-gray-50">
                            <label className="block text-lg font-semibold text-gray-800 mb-3">
                                1. Sélectionner le(s) Étudiant(s)
                                <span className="ml-2 text-sm font-normal text-gray-500">
                                    ({allStudents.length} affiché(s))
                                </span>
                            </label>

                            {/* Filtres étudiants */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 mb-3 p-3 bg-white border rounded">
                                <input
                                    type="text"
                                    placeholder="🔍 Rechercher par nom..."
                                    value={studentSearch}
                                    onChange={e => setStudentSearch(e.target.value)}
                                    className="p-2 border rounded-md text-sm col-span-full md:col-span-1"
                                />
                                <select
                                    value={studentDeptFilter}
                                    onChange={e => handleStudentFilterChange('department', e.target.value)}
                                    className="p-2 border rounded-md text-sm"
                                >
                                    <option value="">Département</option>
                                    {studentStructureOptions.departments.map(d => (
                                        <option key={d} value={d}>{d}</option>
                                    ))}
                                </select>
                                <select
                                    value={studentCursusFilter}
                                    onChange={e => handleStudentFilterChange('cursus', e.target.value)}
                                    disabled={!studentDeptFilter}
                                    className="p-2 border rounded-md text-sm disabled:bg-gray-100"
                                >
                                    <option value="">Cursus</option>
                                    {studentStructureOptions.cursus.map(c => (
                                        <option key={c} value={c}>{c}</option>
                                    ))}
                                </select>
                                <select
                                    value={studentParcoursFilter}
                                    onChange={e => handleStudentFilterChange('parcours', e.target.value)}
                                    disabled={!studentCursusFilter}
                                    className="p-2 border rounded-md text-sm disabled:bg-gray-100"
                                >
                                    <option value="">Parcours</option>
                                    {studentStructureOptions.parcours.map(p => (
                                        <option key={p} value={p}>{p}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Liste étudiants */}
                            {isLoadingStudents ? (
                                <div className="text-center py-4 text-gray-500">
                                    Chargement des étudiants...
                                </div>
                            ) : (
                                <div className="max-h-52 overflow-y-auto border rounded-md p-2 bg-white">
                                    {allStudents.length > 0 ? allStudents.map(student => (
                                        <div
                                            key={student.id}
                                            className="flex items-center p-2 hover:bg-gray-100 rounded cursor-pointer"
                                            onClick={() => {
                                                if (assignment) {
                                                    setStudentIds([student.id]);
                                                } else {
                                                    setStudentIds(prev =>
                                                        prev.includes(student.id)
                                                            ? prev.filter(id => id !== student.id)
                                                            : [...prev, student.id]
                                                    );
                                                }
                                            }}
                                        >
                                            <input
                                                type={assignment ? "radio" : "checkbox"}
                                                checked={studentIds.includes(student.id)}
                                                readOnly
                                                className="h-4 w-4 rounded"
                                            />
                                            <span className="ml-3 text-sm">
                                                {student.name}
                                                <span className="text-xs text-gray-500 ml-2">
                                                    — {student.student_parcours || student.student_cursus || student.student_department || ''}
                                                </span>
                                            </span>
                                        </div>
                                    )) : (
                                        <p className="text-center text-sm text-gray-500 p-4">
                                            Aucun étudiant ne correspond aux filtres.
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Membres du jury */}
                        <div className="p-4 border rounded-lg bg-gray-50">
                            <label className="block text-lg font-semibold text-gray-800 mb-4">
                                2. Sélectionner les Membres du Jury
                            </label>
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                <JuryMemberSelector
                                    title="Président du Jury *"
                                    teachers={allTeachers}
                                    options={teacherOptions}
                                    juryCounts={teacherJuryCounts}
                                    selectedValue={presidentId}
                                    onSelect={setPresidentId}
                                    isOpen={openSelector === 'president'}
                                    onToggle={() => setOpenSelector(
                                        openSelector === 'president' ? null : 'president'
                                    )}
                                />
                                <JuryMemberSelector
                                    title="Rapporteur *"
                                    teachers={allTeachers}
                                    options={teacherOptions}
                                    juryCounts={teacherJuryCounts}
                                    selectedValue={reporterId}
                                    onSelect={setReporterId}
                                    isOpen={openSelector === 'reporter'}
                                    onToggle={() => setOpenSelector(
                                        openSelector === 'reporter' ? null : 'reporter'
                                    )}
                                />
                                <JuryMemberSelector
                                    title="Examinateur *"
                                    teachers={allTeachers}
                                    options={teacherOptions}
                                    juryCounts={teacherJuryCounts}
                                    selectedValue={examinerId}
                                    onSelect={setExaminerId}
                                    isOpen={openSelector === 'examiner'}
                                    onToggle={() => setOpenSelector(
                                        openSelector === 'examiner' ? null : 'examiner'
                                    )}
                                />
                            </div>
                        </div>

                        <div className="flex justify-end space-x-4 pt-4">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-6 py-2 bg-gray-200 text-gray-800 rounded-md"
                            >
                                Annuler
                            </button>
                            <button
                                type="submit"
                                disabled={
    studentIds.length === 0 ||
    !presidentId ||
    !reporterId ||
    !examinerId ||
    !soutenanceDate
}
                                className="px-6 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50"
                            >
                                {assignment ? "Sauvegarder" : "Attribuer"}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};
// Remplacez l'ancien JuryMemberSelector par celui-ci

const JuryMemberSelector = ({ 
    title, teachers, options, selectedValue, onSelect, 
    isOpen, onToggle, juryCounts = {}  // ← NOUVEAU paramètre
}) => {
    const [internalFilters, setInternalFilters] = useState({ 
        name: '', grade: '', status: '', department: '' 
    });

    const handleFilterChange = (field, value) => {
        setInternalFilters(prev => ({ ...prev, [field]: value }));
    };
    
    const filteredTeachers = teachers
        .filter(t => {
            if (!t) return false;
            return (
                (!internalFilters.name || t.full_name.toLowerCase().includes(
                    internalFilters.name.toLowerCase()
                )) &&
                (!internalFilters.grade || t.grade === internalFilters.grade) &&
                (!internalFilters.status || t.status === internalFilters.status) &&
                (!internalFilters.department || t.department === internalFilters.department)
            );
        })
        // ✅ Trier par nombre d'attributions croissant (moins utilisé en premier)
        .sort((a, b) => (juryCounts[a.id] || 0) - (juryCounts[b.id] || 0));
    
    const selectedTeacher = teachers.find(t => t.id === selectedValue);
    // ✅ TOUJOURS utiliser juryCounts (global), jamais teacher.count (role-spécifique)
    const selectedCount = selectedTeacher ? (juryCounts[selectedTeacher.id] || 0) : null;
    const displayText = selectedTeacher
        ? `${selectedTeacher.full_name} (${selectedCount})`
        : 'Sélectionner...';

    return (
        <div className="relative">
            <label className="block text-sm font-medium text-gray-700">{title}</label>
            
            <button
                type="button"
                onClick={onToggle}
                className="mt-1 w-full p-2 border border-gray-300 rounded-md bg-white text-left flex justify-between items-center"
            >
                <span className={selectedValue ? 'text-black' : 'text-gray-400'}>
                    {displayText}
                </span>
                <span className="text-gray-400">▼</span>
            </button>

            {isOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg p-2 space-y-2">
                    {/* Filtres */}
                    <div className="space-y-1 p-2 border-b bg-gray-50 rounded">
                        <input
                            type="text"
                            placeholder="🔍 Filtrer par nom..."
                            value={internalFilters.name}
                            onChange={e => handleFilterChange('name', e.target.value)}
                            className="w-full p-2 border rounded"
                        />
                        <div className="grid grid-cols-3 gap-1 text-xs">
                            <select
                                value={internalFilters.grade}
                                onChange={e => handleFilterChange('grade', e.target.value)}
                                className="p-1 border rounded"
                            >
                                <option value="">Grade</option>
                                {options.grades.map(o => (
                                    <option key={o} value={o}>{o}</option>
                                ))}
                            </select>
                            <select
                                value={internalFilters.status}
                                onChange={e => handleFilterChange('status', e.target.value)}
                                className="p-1 border rounded"
                            >
                                <option value="">Statut</option>
                                {options.statuses.map(o => (
                                    <option key={o} value={o}>{o}</option>
                                ))}
                            </select>
                            <select
                                value={internalFilters.department}
                                onChange={e => handleFilterChange('department', e.target.value)}
                                className="p-1 border rounded"
                            >
                                <option value="">Dpt</option>
                                {options.departments.map(o => (
                                    <option key={o} value={o}>{o}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    
                    {/* Liste des enseignants */}
                    <div className="max-h-48 overflow-y-auto">
                        {filteredTeachers.length > 0 ? filteredTeachers.map(teacher => {
                            const count = juryCounts[teacher.id] || 0;
                            return (
                                <div
                                    key={teacher.id}
                                    onClick={() => { onSelect(teacher.id); onToggle(); }}
                                    className={`p-2 rounded cursor-pointer hover:bg-blue-50 flex items-center justify-between ${
                                        selectedValue === teacher.id 
                                            ? 'bg-blue-100 font-semibold' 
                                            : ''
                                    }`}
                                >
                                    <div className="flex items-center">
                                        <input
                                            type="radio"
                                            checked={selectedValue === teacher.id}
                                            readOnly
                                            className="h-4 w-4 text-blue-600"
                                        />
                                        <span className="ml-2 text-sm">
                                            {teacher.full_name}
                                        </span>
                                    </div>
                                    {/* ✅ Badge du compteur */}
                                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                        count === 0
                                            ? 'bg-green-100 text-green-700'
                                            : count <= 5
                                            ? 'bg-blue-100 text-blue-700'
                                            : count <= 10
                                            ? 'bg-yellow-100 text-yellow-700'
                                            : 'bg-red-100 text-red-700'
                                    }`}>
                                        {count}
                                    </span>
                                </div>
                            );
                        }) : (
                            <p className="text-center text-sm text-gray-500 p-2">
                                Aucun enseignant ne correspond.
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
// Collez ces deux composants complets à la fin de votre fichier App.js

const SupervisionTab = () => {
    const [supervisions, setSupervisions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSupervision, setEditingSupervision] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const [selectedSupervisions, setSelectedSupervisions] = useState([]);
    const [isMassEditModalOpen, setIsMassEditModalOpen] = useState(false);
    const [allTeachers, setAllTeachers] = useState([]);

    const [filters, setFilters] = useState({
        search_name: '', student_department: '', student_cursus: '', student_parcours: '',
        teacher_grade: '', teacher_department: ''
    });
    const [filterOptions, setFilterOptions] = useState({ 
        teacher: { grades: [], statuses: [], departments: [] },
        student: { departments: [], cursus: [], parcours: [] }
    });


    useEffect(() => {
        const fetchAllOptions = async () => {
            try {
                const [teacherOptsRes, studentDeptsRes] = await Promise.all([
                    axios.get(`${API}/teachers/options`),
                    axios.get(`${API}/structure/departments`)
                ]);
                setFilterOptions(prev => ({
                    teacher: teacherOptsRes.data,
                    student: { ...prev.student, departments: studentDeptsRes.data.departments }
                }));
            } catch (error) { console.error("Erreur chargement options:", error); }
        };
        fetchAllOptions();
    }, []);
    useEffect(() => {
    fetchSupervisions();
    // Ajoutez le chargement des enseignants ici aussi
    const fetchTeachers = async () => {
        const res = await axios.get(`${API}/teachers`);
        setAllTeachers(res.data);
    };
    fetchTeachers();
}, []);

    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            fetchSupervisions();
        }, 500);
        return () => clearTimeout(debounceTimer);
    }, [filters]);

    const fetchSupervisions = async () => {
        setLoading(true);
        try {
            const activeFilters = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
            const params = new URLSearchParams(activeFilters);
            const response = await axios.get(`${API}/supervisions`, { params });
            setSupervisions(response.data);
        } catch (error) {
            alert("Erreur lors du chargement des encadrements.");
        } finally {
            setLoading(false);
        }
    };
    
    const handleFilterChange = async (name, value) => {
        const newFilters = { ...filters, [name]: value };

        if (name === 'student_department') {
            newFilters.student_cursus = '';
            newFilters.student_parcours = '';
            setFilterOptions(prev => ({ ...prev, student: { ...prev.student, cursus: [], parcours: [] } }));
            if (value) {
                try {
                    const response = await axios.get(`${API}/structure/cursus?department=${value}`);
                    setFilterOptions(prev => ({ ...prev, student: { ...prev.student, cursus: response.data.cursus } }));
                } catch (error) { console.error("Erreur chargement cursus:", error); }
            }
        } else if (name === 'student_cursus') {
            newFilters.student_parcours = '';
            setFilterOptions(prev => ({ ...prev, student: { ...prev.student, parcours: [] } }));
            if (value && newFilters.student_department) {
                try {
                    const response = await axios.get(`${API}/structure/parcours?department=${newFilters.student_department}&cursus=${value}`);
                    setFilterOptions(prev => ({ ...prev, student: { ...prev.student, parcours: response.data.parcours } }));
                } catch (error) { console.error("Erreur chargement parcours:", error); }
            }
        }
        
        setFilters(newFilters);
    };

    const resetFilters = () => {
        setFilters({
            search_name: '', student_department: '', student_cursus: '', student_parcours: '',
            teacher_grade: '', teacher_department: ''
        });
    };

    const handleDeleteSupervision = async (supervisionId) => {
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet encadrement ?")) return;
        try {
            await axios.delete(`${API}/supervisions/${supervisionId}`);
            alert("Encadrement supprimé.");
            fetchSupervisions();
        } catch (error) { alert("Erreur lors de la suppression."); }
    };
    const handleSelectAll = () => {
    if (selectedSupervisions.length === supervisions.length) setSelectedSupervisions([]);
    else setSelectedSupervisions(supervisions.map(s => s.id));
};

const handleSelectSupervision = (id) => {
    setSelectedSupervisions(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
};

const handleDeleteSelected = async () => {
    if (!window.confirm("Supprimer les encadrements sélectionnés ?")) return;
    for (const id of selectedSupervisions) await axios.delete(`${API}/supervisions/${id}`);
    setSelectedSupervisions([]);
    fetchSupervisions();
};
    const handleExport = async () => {
        setIsExporting(true);
        try {
            const activeFilters = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
            const params = new URLSearchParams(activeFilters);
            const url = `${API}/supervisions/export?${params.toString()}`;

            const token = localStorage.getItem('token');
            const response = await axios.get(url, {
                headers: { 'Authorization': `Bearer ${token}` },
                responseType: 'blob',
            });

            // Logique de téléchargement
            const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', 'export_encadrements.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();

        } catch (error) {
            alert("Erreur lors de l'exportation.");
            console.error("Export error:", error);
        } finally {
            setIsExporting(false);
        }
    };
    const handleMassUpdate = async (data) => {
    try {
        for (const id of selectedSupervisions) {
            // Trouver l'attribution actuelle pour récupérer son student_id (requis par le backend)
            const currentAssignment = supervisions.find(s => s.id === id);
            
            // Préparer le payload avec le student_id obligatoire
            const payload = {
                ...data, // Contient project_title, teacher_id, etc.
                student_id: currentAssignment.student.id // INDISPENSABLE pour le backend
            };

            await axios.put(`${API}/supervisions/${id}`, payload);
        }
        alert("Modifications en masse effectuées !");
        setIsMassEditModalOpen(false);
        setSelectedSupervisions([]);
        fetchSupervisions();
    } catch (error) {
        console.error("Erreur détaillée:", error.response?.data);
        alert("Erreur lors de la modification en masse : " + (error.response?.data?.detail || "Inconnue"));
    }
};


    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Gestion des Encadrements</h2>
                <div className="flex space-x-3">
                    <button 
                        onClick={handleExport}
                        disabled={isExporting}
                        className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md disabled:opacity-50 flex items-center"
                    >
                        {isExporting ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        ) : (
                            <span className="mr-2">📥</span>
                        )}
                        {isExporting ? 'Exportation...' : 'Exporter (Excel)'}
                    </button>
                    <button onClick={() => { setEditingSupervision(null); setIsModalOpen(true); }} className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md">
                        + Nouvel Encadrement
                    </button>
                </div>
            
            </div>

            <div className="bg-white rounded-lg shadow p-6 mb-6 space-y-4">
                <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">🔍 Filtres</h3>
                    <button onClick={resetFilters} className="text-sm text-blue-600 hover:underline">Réinitialiser</button>
                </div>
                <div>
                    <input type="text" name="search_name" placeholder="Rechercher par nom (étudiant ou encadreur)..." value={filters.search_name} onChange={(e) => handleFilterChange(e.target.name, e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md"/>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <select name="student_department" value={filters.student_department} onChange={(e) => handleFilterChange(e.target.name, e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                        <option value="">Dpt. Étudiant</option>
                        {filterOptions.student.departments.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <select name="student_cursus" value={filters.student_cursus} onChange={(e) => handleFilterChange(e.target.name, e.target.value)} disabled={!filters.student_department} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm disabled:bg-gray-100">
                        <option value="">Cursus Étudiant</option>
                        {filterOptions.student.cursus.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select name="student_parcours" value={filters.student_parcours} onChange={(e) => handleFilterChange(e.target.name, e.target.value)} disabled={!filters.student_cursus} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm disabled:bg-gray-100">
                        <option value="">Parcours Étudiant</option>
                        {filterOptions.student.parcours.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <select name="teacher_grade" value={filters.teacher_grade} onChange={(e) => handleFilterChange(e.target.name, e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                        <option value="">Grade Encadreur</option>
                        {filterOptions.teacher.grades.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                    <select name="teacher_department" value={filters.teacher_department} onChange={(e) => handleFilterChange(e.target.name, e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                        <option value="">Dpt. Encadreur</option>
                        {filterOptions.teacher.departments.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                </div>
            </div>

             {/* --- BARRE D'ACTION EN MASSE --- */}
            <div className="bg-white rounded-t-lg shadow px-6 py-3 border-b flex items-center justify-between">
                <span className="text-sm text-gray-600">{selectedSupervisions.length} sélectionné(s)</span>
                {selectedSupervisions.length > 0 && (
                    <div className="flex gap-2">
                        {/* NOUVEAU BOUTON MODIFIER */}
                        <button 
                            onClick={() => setIsMassEditModalOpen(true)} 
                            className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-md text-sm font-medium"
                        >
                            Modifier la sélection
                        </button>
                        <button onClick={handleDeleteSelected} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium">
                            Supprimer la sélection
                        </button>
                    </div>
                )}
            </div>

        {/* --- TABLEAU --- */}
        {loading ? <p>Chargement...</p> : (
            <div className="bg-white rounded-b-lg shadow overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3"><input type="checkbox" onChange={handleSelectAll} checked={supervisions.length > 0 && selectedSupervisions.length === supervisions.length} /></th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Étudiant</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Titre du Projet</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Encadreur</th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Taux</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {supervisions.map(s => (
                            <tr key={s.id} className={selectedSupervisions.includes(s.id) ? 'bg-blue-50' : ''}>
                                <td className="px-4 py-4"><input type="checkbox" checked={selectedSupervisions.includes(s.id)} onChange={() => handleSelectSupervision(s.id)}/></td>
                                <td className="px-6 py-4">
                                    <div className="font-medium text-gray-900">{s.student.name}</div>
                                    <div className="text-xs text-gray-500">{s.student.student_department} - {s.student.student_parcours}</div>
                                </td>
                                <td className="px-6 py-4 font-semibold text-gray-800">{s.project_title}</td>
                                <td className="px-6 py-4">
                                    <div className="font-medium">{s.teacher.full_name}</div>
                                    <div className="text-xs text-gray-500">{s.teacher.grade} - {s.teacher.status} - {s.teacher.department}</div>
                                </td>
                                <td className="px-6 py-4 text-center"><span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">{s.supervision_rate}</span></td>
                                <td className="px-6 py-4 text-right">
                                    <button onClick={() => { setEditingSupervision(s); setIsModalOpen(true); }} className="text-blue-600 mr-3">Modifier</button>
                                    <button onClick={() => handleDeleteSupervision(s.id)} className="text-red-600">Supprimer</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
            )}

            

            {isModalOpen && (
    <SupervisionModal 
        supervision={editingSupervision}
        onClose={() => setIsModalOpen(false)}
        // --- DÉBUT DE LA CORRECTION ---
        onSave={async (data) => {
            try {
                let payload = {};
                let url = `${API}/supervisions`;
                let method = 'post';

                if (editingSupervision) {
                    // En mode édition, l'API attend 'student_id' et 'teacher_id' au singulier
                    method = 'put';
                    url = `${API}/supervisions/${editingSupervision.id}`;
                    payload = {
                        student_id: data.student_id, // singulier
                        teacher_id: data.teacher_id, // singulier
                        project_title: data.project_title
                    };
                } else {
                    // En mode création, l'API attend 'student_ids' et 'teacher_ids' au pluriel
                    method = 'post';
                    payload = {
                        student_ids: data.student_ids, // pluriel
                        teacher_ids: data.teacher_ids, // pluriel
                        project_title: data.project_title
                    };
                }

                await axios[method](url, payload);

                alert(editingSupervision ? "Modification réussie !" : "Encadrement(s) ajouté(s) avec succès !");
                setIsModalOpen(false);
                fetchSupervisions();
            } catch (error) { 
                alert("Erreur : " + (error.response?.data?.detail || "Inconnue")); 
            }
        }}
                />
            )}

            {isMassEditModalOpen && (
                <MassEditSupervisionModal
                    selectedCount={selectedSupervisions.length}
                    allTeachers={allTeachers}
                    onClose={() => setIsMassEditModalOpen(false)}
                    onSave={handleMassUpdate}
                />
            )}
        </div>
    );
};


const SupervisionModal = ({ supervision, onClose, onSave }) => {
    // --- ÉTATS CORRIGÉS ---
    const [studentIds, setStudentIds] = useState(supervision ? [supervision.student.id] : []);
    const [teacherIds, setTeacherIds] = useState(supervision ? [supervision.teacher.id] : []);
    const [projectTitle, setProjectTitle] = useState(supervision?.project_title || ''); // On utilise un état dédié
    
    const [allStudents, setAllStudents] = useState([]);
    const [allTeachers, setAllTeachers] = useState([]);
    const [loading, setLoading] = useState(true);

    const [teacherOptions, setTeacherOptions] = useState({ grades: [], statuses: [], departments: [] });
    const [studentStructureOptions, setStudentStructureOptions] = useState({ departments: [], cursus: [], parcours: [] });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [studentsRes, teachersRes, teacherOptsRes, studentDeptsRes] = await Promise.all([
                    axios.get(`${API}/admin/users`),
                    axios.get(`${API}/teachers`),
                    axios.get(`${API}/teachers/options`),
                    axios.get(`${API}/structure/departments`),
                ]);
                setAllStudents(studentsRes.data.filter(u => u.role === 'student'));
                setAllTeachers(teachersRes.data);
                setTeacherOptions(teacherOptsRes.data);
                setStudentStructureOptions(prev => ({...prev, departments: studentDeptsRes.data.departments}));
            } catch (error) { console.error("Erreur chargement:", error); }
            finally { setLoading(false); }
        };
        fetchData();
    }, []);

    // Dans App.js > SupervisionModal > handleSubmit

const handleSubmit = (e) => {
    e.preventDefault();
    
    // On construit l'objet de manière flexible
    const data = {
        project_title: projectTitle
    };

    // Si on est en mode édition, on envoie aussi les ID (requis par le backend)
    if (supervision) {
        data.student_id = studentIds[0];
        data.teacher_id = teacherIds[0];
    } else {
        // En création, on envoie les listes
        data.student_ids = studentIds;
        data.teacher_ids = teacherIds;
    }

    onSave(data);
};
    
    const handleStudentSelection = (studentId) => {
        setStudentIds(prev => prev.includes(studentId) ? prev.filter(id => id !== studentId) : [...prev, studentId]);
    };
    const handleTeacherSelection = (teacherId) => {
        setTeacherIds(prev => prev.includes(teacherId) ? prev.filter(id => id !== teacherId) : [...prev, teacherId]);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-semibold text-gray-800">
                        {supervision ? "Modifier l'Encadrement" : "Ajouter un Encadrement"}
                    </h3>
                    <button onClick={onClose} className="text-3xl text-gray-500 hover:text-gray-800">&times;</button>
                </div>
                
                {loading ? <p>Chargement...</p> : (
                    <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto pr-2 space-y-6">
                        
                        {/* Titre du projet */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Titre du Projet *</label>
                            <input 
                                type="text" 
                                value={projectTitle} 
                                onChange={(e) => setProjectTitle(e.target.value)} 
                                className="mt-1 w-full p-2 border border-gray-300 rounded-md" 
                                required 
                            />
                        </div>

                        {/* Sélection Étudiants */}
                        <div className="p-4 border rounded-lg bg-gray-50">
                            <EntitySelector
                                title={`1. Sélectionner le ou les Étudiant(s) (${studentIds.length})`}
                                items={allStudents}
                                displayKey="name"
                                displaySubKey="student_parcours"
                                isMulti={!supervision}
                                selectedValues={studentIds}
                                onToggleSelect={handleStudentSelection}
                                selectedValue={studentIds[0]}
                                onSelect={(id) => setStudentIds([id])}
                                disabled={!!supervision}
                                filterOptions={{ student: studentStructureOptions }}
                                iutStructure={IUT_STRUCTURE}
                            />
                        </div>
                        
                        {/* Sélection Enseignants */}
                        <div className="p-4 border rounded-lg bg-gray-50">
                            <EntitySelector
                                title={`2. Sélectionner le ou les Encadreur(s) (${teacherIds.length})`}
                                items={allTeachers}
                                displayKey="full_name"
                                displaySubKey="department"
                                isMulti={true}
                                selectedValues={teacherIds}
                                onToggleSelect={handleTeacherSelection}
                                selectedValue={teacherIds[0]}
                                onSelect={(id) => setTeacherIds([id])}
                                filterOptions={{ teacher: teacherOptions }}
                            />
                        </div>

                        <div className="flex justify-end gap-4 pt-4 mt-auto">
                            <button type="button" onClick={onClose} className="px-6 py-2 bg-gray-200 rounded">Annuler</button>
                            <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded">Sauvegarder</button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

const EntitySelector = ({ 
    title, placeholder, items, displayKey = "name", displaySubKey,
    isMulti = false, selectedValue, onSelect, selectedValues, onToggleSelect,
    disabled, filterOptions, iutStructure 
}) => {
    const [showFilters, setShowFilters] = useState(false);
    const [nameFilter, setNameFilter] = useState('');
    const [studentDeptFilter, setStudentDeptFilter] = useState('');
    const [studentCursusFilter, setStudentCursusFilter] = useState('');
    const [studentParcoursFilter, setStudentParcoursFilter] = useState('');
    const [teacherGradeFilter, setTeacherGradeFilter] = useState('');
    const [teacherDeptFilter, setTeacherDeptFilter] = useState('');

    const [cursusOptions, setCursusOptions] = useState([]);
    const [parcoursOptions, setParcoursOptions] = useState([]);

    useEffect(() => {
        if (studentDeptFilter && iutStructure) {
            setCursusOptions(Object.keys(iutStructure[studentDeptFilter] || {}));
        } else { setCursusOptions([]); }
        setStudentCursusFilter('');
    }, [studentDeptFilter, iutStructure]);

    useEffect(() => {
        if (studentDeptFilter && studentCursusFilter && iutStructure) {
            setParcoursOptions(iutStructure[studentDeptFilter]?.[studentCursusFilter] || []);
        } else { setParcoursOptions([]); }
        setStudentParcoursFilter('');
    }, [studentCursusFilter, studentDeptFilter, iutStructure]);
    
    const filteredItems = items.filter(item => {
        if (!item) return false;
        const nameMatch = !nameFilter || (item[displayKey] && item[displayKey].toLowerCase().includes(nameFilter.toLowerCase()));
        
        let specificMatch = true;
        if (filterOptions.student) {
            specificMatch = (!studentDeptFilter || item.student_department === studentDeptFilter) &&
                            (!studentCursusFilter || item.student_cursus === studentCursusFilter) &&
                            (!studentParcoursFilter || item.student_parcours === studentParcoursFilter);
        } else if (filterOptions.teacher) {
            specificMatch = (!teacherGradeFilter || item.grade === teacherGradeFilter) &&
                            (!teacherDeptFilter || item.department === teacherDeptFilter);
        }
        return nameMatch && specificMatch;
    });
    
    const selectedItem = !isMulti ? items.find(item => item.id === selectedValue) : null;
    const displayName = selectedItem ? `${selectedItem[displayKey]} (${selectedItem[displaySubKey] || ''})` : placeholder;

    return (
        <div className="space-y-2">
            <div className="flex justify-between items-center">
                <label className="block text-lg font-semibold text-gray-800">{title}</label>
                {!disabled && (
                    <button type="button" onClick={() => setShowFilters(!showFilters)} className="text-xs text-blue-600 hover:underline">
                        {showFilters ? 'Cacher les filtres' : 'Afficher les filtres'}
                    </button>
                )}
            </div>
            
            {showFilters && !disabled && (
                <div className="space-y-2 p-2 border bg-white rounded-md">
                    <input type="text" placeholder="Filtrer par nom..." value={nameFilter} onChange={e => setNameFilter(e.target.value)} className="w-full p-2 border rounded" />
                    
                    {filterOptions.student && (
                        <div className="grid grid-cols-3 gap-1 text-xs">
                            <select value={studentDeptFilter} onChange={e => setStudentDeptFilter(e.target.value)} className="p-1 border rounded"><option value="">Dpt.</option>{filterOptions.student.departments.map(o => <option key={o} value={o}>{o}</option>)}</select>
                            <select value={studentCursusFilter} onChange={e => setStudentCursusFilter(e.target.value)} disabled={!studentDeptFilter} className="p-1 border rounded"><option value="">Cursus</option>{cursusOptions.map(o => <option key={o} value={o}>{o}</option>)}</select>
                            <select value={studentParcoursFilter} onChange={e => setStudentParcoursFilter(e.target.value)} disabled={!studentCursusFilter} className="p-1 border rounded"><option value="">Parcours</option>{parcoursOptions.map(o => <option key={o} value={o}>{o}</option>)}</select>
                        </div>
                    )}

                    {filterOptions.teacher && (
                        <div className="grid grid-cols-2 gap-1 text-xs">
                            <select value={teacherGradeFilter} onChange={e => setTeacherGradeFilter(e.target.value)} className="p-1 border rounded"><option value="">Grade</option>{filterOptions.teacher.grades.map(o => <option key={o} value={o}>{o}</option>)}</select>
                            <select value={teacherDeptFilter} onChange={e => setTeacherDeptFilter(e.target.value)} className="p-1 border rounded"><option value="">Dpt.</option>{filterOptions.teacher.departments.map(o => <option key={o} value={o}>{o}</option>)}</select>
                        </div>
                    )}
                </div>
            )}
            
            <div className="max-h-48 overflow-y-auto border rounded-md p-2 bg-white">
                {filteredItems.length > 0 ? filteredItems.map(item => (
                    <div 
                        key={item.id}
                        onClick={() => isMulti ? onToggleSelect(item.id) : onSelect(item.id)}
                        className={`p-2 rounded cursor-pointer hover:bg-blue-100 flex items-center ${isMulti ? (selectedValues.includes(item.id) ? 'bg-blue-50' : '') : (selectedValue === item.id ? 'bg-blue-100 font-semibold' : '')}`}
                    >
                        <input
                            type={isMulti ? "checkbox" : "radio"}
                            checked={isMulti ? selectedValues.includes(item.id) : selectedValue === item.id}
                            readOnly
                            className="h-4 w-4"
                        />
                        <span className="ml-2 text-sm">{item[displayKey]} <span className="text-xs text-gray-500">- {item[displaySubKey]}</span></span>
                    </div>
                )) : <p className="text-center text-sm text-gray-500 p-2">Aucun résultat.</p>}
            </div>
        </div>
    );
};
const MassEditSupervisionModal = ({ onClose, onSave, selectedCount, allTeachers }) => {
    const [fieldsToEdit, setFieldsToEdit] = useState({ project_title: false, teacher_id: false });
    const [formData, setFormData] = useState({ project_title: '', teacher_id: '' });
    const [step, setStep] = useState(1);

    const handleSubmit = () => {
        const data = {};
        if (fieldsToEdit.project_title) data.project_title = formData.project_title;
        if (fieldsToEdit.teacher_id) data.teacher_id = formData.teacher_id;
        onSave(data);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-lg">
                <h3 className="text-xl font-bold mb-4">{step === 1 ? "Choisir les champs" : "Nouvelles valeurs"}</h3>
                {step === 1 ? (
                    <div className="space-y-2">
                        <label className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer">
                            <input type="checkbox" onChange={(e) => setFieldsToEdit({...fieldsToEdit, project_title: e.target.checked})} className="mr-3" /> Titre du Projet
                        </label>
                        <label className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer">
                            <input type="checkbox" onChange={(e) => setFieldsToEdit({...fieldsToEdit, teacher_id: e.target.checked})} className="mr-3" /> Encadreur
                        </label>
                        <div className="flex justify-end gap-2 mt-6">
                            <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded">Annuler</button>
                            <button onClick={() => setStep(2)} className="px-4 py-2 bg-blue-600 text-white rounded">Suivant</button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {fieldsToEdit.project_title && (
                            <input type="text" placeholder="Nouveau titre..." onChange={e => setFormData({...formData, project_title: e.target.value})} className="w-full p-2 border rounded" />
                        )}
                        {fieldsToEdit.teacher_id && (
                            <select onChange={e => setFormData({...formData, teacher_id: e.target.value})} className="w-full p-2 border rounded">
                                <option value="">Choisir un nouvel encadreur...</option>
                                {allTeachers.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
                            </select>
                        )}
                        <div className="flex justify-end gap-2 mt-6">
                            <button onClick={() => setStep(1)} className="px-4 py-2 bg-gray-200 rounded">Retour</button>
                            <button onClick={handleSubmit} className="px-4 py-2 bg-blue-600 text-white rounded">Appliquer aux {selectedCount} encadrements</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
// Ajoutez ce nouveau composant à la fin de votre fichier App.js

const MultiTeacherSelector = ({ allTeachers, teacherOptions, selectedTeacherIds, onSelectionChange }) => {
    const [filters, setFilters] = useState({ name: '', grade: '', department: '' });

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const filteredTeachers = allTeachers.filter(t => {
        if (!t) return false;
        return (
            (!filters.name || (t.full_name && t.full_name.toLowerCase().includes(filters.name.toLowerCase()))) &&
            (!filters.grade || t.grade === filters.grade) &&
            (!filters.department || t.department === filters.department)
        );
    });

    return (
        <div className="space-y-2">
            {/* Filtres */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 p-2 border bg-white rounded">
                <input type="text" placeholder="Filtrer par nom..." value={filters.name} onChange={e => handleFilterChange('name', e.target.value)} className="p-2 border rounded" />
                <select value={filters.grade} onChange={e => handleFilterChange('grade', e.target.value)} className="p-2 border rounded text-sm">
                    <option value="">Grade</option>{teacherOptions.grades.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
                <select value={filters.department} onChange={e => handleFilterChange('department', e.target.value)} className="p-2 border rounded text-sm">
                    <option value="">Département</option>{teacherOptions.departments.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
            </div>
            {/* Liste à cocher */}
            <div className="max-h-48 overflow-y-auto border rounded-md p-2 bg-white">
                {filteredTeachers.map(teacher => (
                    <div key={teacher.id} className="flex items-center p-1 hover:bg-gray-100 rounded">
                        <input
                            type="checkbox"
                            id={`teacher-${teacher.id}`}
                            checked={selectedTeacherIds.includes(teacher.id)}
                            onChange={() => onSelectionChange(teacher.id)}
                            className="h-4 w-4 rounded"
                        />
                        <label htmlFor={`teacher-${teacher.id}`} className="ml-3 text-sm cursor-pointer">
                            {teacher.full_name} <span className="text-xs text-gray-500">- {teacher.department}</span>
                        </label>
                    </div>
                ))}
            </div>
        </div>
    );
};
// ========================================================================
// AJOUTEZ CE NOUVEAU COMPOSANT COMPLET À LA FIN DE VOTRE FICHIER App.js
// ========================================================================
const AcademicSupervisorEvaluationTab = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    student_id: '',
    technical_skills_rating: 3,
    communication_skills_rating: 3,
    autonomy_rating: 3,
    overall_rating: 3,
    general_comment: '',
  });
  const [submitStatus, setSubmitStatus] = useState({ loading: false, error: '', success: '' });
  // --- AJOUTEZ CE BLOC ---
const [filters, setFilters] = useState({ search: '', department: '', cursus: '', parcours: '' });
const [departments, setDepartments] = useState([]);
const [cursus, setCursus] = useState([]);
const [parcours, setParcours] = useState([]);
// --- FIN DE L'AJOUT ---

  useEffect(() => {
    fetchAssignedStudents();
    fetchDepartments();
  }, []);

  const fetchAssignedStudents = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/student-assignments`);
      // La réponse contient des 'assignments', nous extrayons les infos des étudiants
      const studentData = response.data.assignments.map(a => ({
        id: a.student_id,
        name: a.student_name,
        email: a.student_email,
        department: a.student_department,
        cursus: a.student_cursus,
        parcours: a.student_parcours
      }));
      setStudents(studentData);
    } catch (err) {
      setError('Erreur lors de la récupération de vos étudiants affectés.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openEvaluationModal = (student) => {
    setSelectedStudent(student);
    setFormData({
      student_id: student.id,
      technical_skills_rating: 3,
      communication_skills_rating: 3,
      autonomy_rating: 3,
      overall_rating: 3,
      general_comment: '',
    });
    setIsModalOpen(true);
    setSubmitStatus({ loading: false, error: '', success: '' });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: name.includes('rating') ? parseInt(value) : value }));
  };

  const handleSubmitEvaluation = async (e) => {
    e.preventDefault();
    setSubmitStatus({ loading: true, error: '', success: '' });
    try {
      await axios.post(`${API}/academic-evaluations`, formData);
      setSubmitStatus({ loading: false, error: '', success: 'Évaluation soumise avec succès !' });
      setTimeout(() => {
        setIsModalOpen(false);
        fetchAssignedStudents(); 
      }, 2000);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Une erreur est survenue.';
      setSubmitStatus({ loading: false, error: errorMessage, success: '' });
    }
  };
  
  const renderRatingInput = (label, name) => (
    <div>
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <select 
        name={name} 
        value={formData[name]} 
        onChange={handleFormChange}
        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
      >
        {[1, 2, 3, 4, 5].map(v => (
          <option key={v} value={v}>
            {v} - {['Très Faible', 'Faible', 'Moyen', 'Bon', 'Excellent'][v - 1]}
          </option>
        ))}
      </select>
    </div>
  );
  // --- AJOUTEZ CE BLOC ---
const fetchDepartments = async () => { try { const res = await axios.get(`${API}/structure/departments`); setDepartments(res.data.departments); } catch (e) { console.error(e); }};
const fetchCursus = async (d) => { try { const res = await axios.get(`${API}/structure/cursus?department=${d}`); setCursus(res.data.cursus); } catch (e) { console.error(e); }};
const fetchParcours = async (d, c) => { try { const res = await axios.get(`${API}/structure/parcours?department=${d}&cursus=${c}`); setParcours(res.data.parcours); } catch (e) { console.error(e); }};

const handleFilterChange = (key, value) => {
  setFilters(prev => {
    const newFilters = { ...prev, [key]: value };
    if (key === 'department') {
      newFilters.cursus = '';
      newFilters.parcours = '';
      setCursus([]);
      setParcours([]);
      if (value) fetchCursus(value);
    } else if (key === 'cursus') {
      newFilters.parcours = '';
      setParcours([]);
      if (value) fetchParcours(newFilters.department, value);
    }
    return newFilters;
  });
};

const clearFilters = () => {
  setFilters({ search: '', department: '', cursus: '', parcours: '' });
};

// Appliquer les filtres avant de rendre la liste
const filteredStudents = students.filter(student => {
  if (!student) return false;
  const searchLower = filters.search.toLowerCase();
  const matchesSearch = !filters.search || student.name.toLowerCase().includes(searchLower) || student.email.toLowerCase().includes(searchLower);
  const matchesDepartment = !filters.department || student.department === filters.department;
  const matchesCursus = !filters.cursus || student.cursus === filters.cursus;
  const matchesParcours = !filters.parcours || student.parcours === filters.parcours;
  return matchesSearch && matchesDepartment && matchesCursus && matchesParcours;
});
// --- FIN DE L'AJOUT ---

  if (loading) return <div className="p-6">Chargement de vos étudiants...</div>;
  if (error) return <div className="p-6 text-red-600">{error}</div>;

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Évaluation des Stagiaires</h2>
     
<div className="bg-white rounded-lg shadow p-6 mb-6">
  <h3 className="text-lg font-semibold text-gray-900 mb-4">🔍 Filtres</h3>
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Recherche</label>
      <input type="text" value={filters.search} onChange={(e) => handleFilterChange('search', e.target.value)} placeholder="Nom ou email..." className="w-full border border-gray-300 rounded-md px-3 py-2"/>
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Département</label>
      <select value={filters.department} onChange={(e) => handleFilterChange('department', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2">
        <option value="">Tous</option>
        {departments.map((d) => <option key={d} value={d}>{d}</option>)}
      </select>
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Cursus</label>
      <select value={filters.cursus} onChange={(e) => handleFilterChange('cursus', e.target.value)} disabled={!filters.department} className="w-full border border-gray-300 rounded-md px-3 py-2 disabled:bg-gray-100">
        <option value="">Tous</option>
        {cursus.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Parcours</label>
      <select value={filters.parcours} onChange={(e) => handleFilterChange('parcours', e.target.value)} disabled={!filters.cursus} className="w-full border border-gray-300 rounded-md px-3 py-2 disabled:bg-gray-100">
        <option value="">Tous</option>
        {parcours.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
    </div>
  </div>
  <div className="mt-4 flex justify-end">
    <button onClick={clearFilters} className="text-gray-600 hover:text-gray-800 text-sm">Effacer les filtres</button>
  </div>
</div>

      
      {filteredStudents.length === 0? (
        <p className="text-gray-600 text-center py-8">
          Aucun étudiant ne vous a encore été affecté.
        </p>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <ul className="divide-y divide-gray-200">
            {filteredStudents.map((student) => (
              <li key={student.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                <div>
                  <p className="text-lg font-semibold text-gray-900">{student.name}</p>
                  <p className="text-sm text-gray-500">{student.email}</p>
                  <p className="text-sm text-blue-600">{student.parcours}</p>
                </div>
                <button 
                  onClick={() => openEvaluationModal(student)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Évaluer
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center">
          <div className="relative mx-auto p-8 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <h3 className="text-xl font-semibold mb-4">Évaluer {selectedStudent?.name}</h3>
            <form onSubmit={handleSubmitEvaluation} className="space-y-4">
              {renderRatingInput("Compétences Techniques", "technical_skills_rating")}
              {renderRatingInput("Compétences en Communication", "communication_skills_rating")}
              {renderRatingInput("Autonomie et Initiative", "autonomy_rating")}
              {renderRatingInput("Appréciation Globale", "overall_rating")}
              <div>
                <label className="block text-sm font-medium text-gray-700">Commentaire Général *</label>
                <textarea 
                  name="general_comment" 
                  value={formData.general_comment}
                  onChange={handleFormChange}
                  rows="4"
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div className="flex justify-end space-x-4 mt-6">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Annuler</button>
                <button type="submit" disabled={submitStatus.loading} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-green-300">
                  {submitStatus.loading ? 'Envoi...' : 'Soumettre l\'évaluation'}
                </button>
              </div>
              {submitStatus.success && <p className="text-green-600 mt-2 text-center">{submitStatus.success}</p>}
              {submitStatus.error && <p className="text-red-600 mt-2 text-center">{submitStatus.error}</p>}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;